var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
import {
  EMPTY_ARRAY,
  atom,
  computed,
  react,
  transact,
  unsafe__withoutCapture
} from "@tldraw/state";
import {
  reverseRecordsDiff
} from "@tldraw/store";
import {
  CameraRecordType,
  InstancePageStateRecordType,
  PageRecordType,
  TLDOCUMENT_ID,
  TLINSTANCE_ID,
  UserRecordType,
  createBindingId,
  createShapeId,
  createUserId,
  getShapePropKeysByStyle,
  isPageId,
  isShapeId
} from "@tldraw/tlschema";
import {
  FileHelpers,
  PerformanceTracker,
  Result,
  ZERO_INDEX_KEY,
  annotateError,
  assert,
  assertExists,
  bind,
  compact,
  debounce,
  dedupe,
  exhaustiveSwitchError,
  fetch,
  getIndexAbove,
  getIndexBetween,
  getIndices,
  getIndicesAbove,
  getIndicesBetween,
  getOwnProperty,
  hasOwnProperty,
  last,
  lerp,
  minBy,
  sortById,
  sortByIndex,
  structuredClone,
  uniqueId
} from "@tldraw/utils";
import EventEmitter from "eventemitter3";
import { createTLCurrentUser } from "../config/createTLCurrentUser.mjs";
import { checkAssets } from "../config/defaultAssets.mjs";
import { checkBindings } from "../config/defaultBindings.mjs";
import { checkShapesAndAddCore } from "../config/defaultShapes.mjs";
import {
  getSnapshot,
  loadSnapshot
} from "../config/TLEditorSnapshot.mjs";
import {
  DEFAULT_ANIMATION_OPTIONS,
  DEFAULT_CAMERA_OPTIONS,
  INTERNAL_POINTER_IDS,
  LEFT_MOUSE_BUTTON,
  MIDDLE_MOUSE_BUTTON,
  RIGHT_MOUSE_BUTTON,
  STYLUS_ERASER_BUTTON
} from "../constants.mjs";
import { getOwnerWindow } from "../exports/domUtils.mjs";
import { exportToSvg } from "../exports/exportToSvg.mjs";
import { getSvgAsImageWithOptions, trimSvgToContent } from "../exports/getSvgAsImage.mjs";
import { tlmenus } from "../globals/menus.mjs";
import { tltime } from "../globals/time.mjs";
import { defaultTldrawOptions } from "../options.mjs";
import { Box } from "../primitives/Box.mjs";
import { EASINGS } from "../primitives/easings.mjs";
import { Group2d } from "../primitives/geometry/Group2d.mjs";
import { intersectPolygonPolygon } from "../primitives/intersect.mjs";
import { Mat } from "../primitives/Mat.mjs";
import { PI, approximately, areAnglesCompatible, clamp, pointInPolygon } from "../primitives/utils.mjs";
import { Vec } from "../primitives/Vec.mjs";
import { areShapesContentEqual } from "../utils/areShapesContentEqual.mjs";
import { dataUrlToFile } from "../utils/assets.mjs";
import { debugFlags } from "../utils/debug-flags.mjs";
import {
  createDeepLinkString,
  parseDeepLinkString
} from "../utils/deepLinks.mjs";
import { getIncrementedName } from "../utils/getIncrementedName.mjs";
import { getReorderingShapesChanges } from "../utils/reorderShapes.mjs";
import { getDroppedShapesToNewParents, kickoutOccludedShapes } from "../utils/reparenting.mjs";
import { applyRotationToSnapshotShapes, getRotationSnapshot } from "../utils/rotation.mjs";
import { SharedStyleMap } from "../utils/SharedStylesMap.mjs";
import { bindingsIndex } from "./derivations/bindingsIndex.mjs";
import { notVisibleShapes } from "./derivations/notVisibleShapes.mjs";
import { parentsToChildren } from "./derivations/parentsToChildren.mjs";
import { deriveShapeIdsInCurrentPage } from "./derivations/shapeIdsInCurrentPage.mjs";
import { ClickManager } from "./managers/ClickManager/ClickManager.mjs";
import { CollaboratorsManager } from "./managers/CollaboratorsManager/CollaboratorsManager.mjs";
import { EdgeScrollManager } from "./managers/EdgeScrollManager/EdgeScrollManager.mjs";
import { FocusManager } from "./managers/FocusManager/FocusManager.mjs";
import { FontManager } from "./managers/FontManager/FontManager.mjs";
import { HistoryManager } from "./managers/HistoryManager/HistoryManager.mjs";
import { InputsManager } from "./managers/InputsManager/InputsManager.mjs";
import { PerformanceManager } from "./managers/PerformanceManager/PerformanceManager.mjs";
import { ScribbleManager } from "./managers/ScribbleManager/ScribbleManager.mjs";
import { SnapManager } from "./managers/SnapManager/SnapManager.mjs";
import { SpatialIndexManager } from "./managers/SpatialIndexManager/SpatialIndexManager.mjs";
import { TextManager } from "./managers/TextManager/TextManager.mjs";
import { ThemeManager, resolveThemes } from "./managers/ThemeManager/ThemeManager.mjs";
import { TickManager } from "./managers/TickManager/TickManager.mjs";
import { UserPreferencesManager } from "./managers/UserPreferencesManager/UserPreferencesManager.mjs";
import { OverlayManager } from "./overlays/OverlayManager.mjs";
import { RootState } from "./tools/RootState.mjs";
const RENDERING_SHAPES_SORT_CACHE_THRESHOLD = 100;
class Editor extends EventEmitter {
  id = uniqueId();
  constructor({
    store,
    user,
    shapeUtils,
    bindingUtils,
    assetUtils: assetUtilConstructors,
    overlayUtils: overlayUtilConstructors,
    tools,
    getContainer,
    // needs to be here for backwards compatibility with TldrawEditor
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    cameraOptions,
    initialState,
    autoFocus,
    options: _options,
    // needs to be here for backwards compatibility with TldrawEditor
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    textOptions: _textOptions,
    getShapeVisibility,
    colorScheme,
    fontAssetUrls,
    themes,
    initialTheme
  }) {
    super();
    this._getShapeVisibility = getShapeVisibility;
    const options = _textOptions ? { ..._options, text: _options?.text ?? _textOptions } : _options;
    this.options = { ...defaultTldrawOptions, ...options };
    this.store = store;
    this.history = new HistoryManager({
      store,
      annotateError: (error) => {
        this.annotateError(error, { origin: "history.batch", willCrashApp: true });
        this.crash(error);
      }
    });
    this.snaps = new SnapManager(this);
    this._spatialIndex = new SpatialIndexManager(this);
    this.disposables.add(() => this._spatialIndex.dispose());
    this.disposables.add(this.timers.dispose);
    this._cameraOptions.set({
      ...DEFAULT_CAMERA_OPTIONS,
      ...cameraOptions,
      ...options?.camera
    });
    this.getContainer = getContainer;
    this._textOptions = atom("text options", options?.text ?? null);
    this.user = new UserPreferencesManager(user ?? createTLCurrentUser(), colorScheme ?? "light");
    this.disposables.add(() => this.user.dispose());
    this.textMeasure = new TextManager(this);
    this.disposables.add(() => this.textMeasure.dispose());
    this._themeManager = new ThemeManager(this, {
      themes: resolveThemes(themes),
      initial: initialTheme ?? "default"
    });
    this.disposables.add(() => this._themeManager.dispose());
    this._tickManager = new TickManager(this);
    this.disposables.add(() => this._tickManager.dispose());
    this.disposables.add(() => {
      this._setCameraState("idle");
    });
    this.fonts = new FontManager(this, fontAssetUrls);
    this.disposables.add(() => this.fonts.dispose());
    this.inputs = new InputsManager(this);
    this.disposables.add(() => this.inputs.dispose());
    this.performance = new PerformanceManager(this);
    this.disposables.add(() => this.performance.dispose());
    this.collaborators = new CollaboratorsManager(this);
    class NewRoot extends RootState {
      static initial = initialState ?? "";
    }
    this.root = new NewRoot(this);
    this.root.children = {};
    this.markEventAsHandled = this.markEventAsHandled.bind(this);
    const allShapeUtils = checkShapesAndAddCore(shapeUtils);
    const _shapeUtils = {};
    const _styleProps = {};
    const allStylesById = /* @__PURE__ */ new Map();
    for (const Util of allShapeUtils) {
      const util = new Util(this);
      _shapeUtils[Util.type] = util;
      const propKeysByStyle = getShapePropKeysByStyle(Util.props ?? {});
      _styleProps[Util.type] = propKeysByStyle;
      for (const style of propKeysByStyle.keys()) {
        if (!allStylesById.has(style.id)) {
          allStylesById.set(style.id, style);
        } else if (allStylesById.get(style.id) !== style) {
          throw Error(
            `Multiple style props with id "${style.id}" in use. Style prop IDs must be unique.`
          );
        }
      }
    }
    this.shapeUtils = _shapeUtils;
    this.styleProps = _styleProps;
    const _shapeUtilsByAssetType = {};
    for (const Util of allShapeUtils) {
      const assetTypes = Util.handledAssetTypes;
      if (assetTypes) {
        for (const assetType of assetTypes) {
          _shapeUtilsByAssetType[assetType] = _shapeUtils[Util.type];
        }
      }
    }
    this._shapeUtilsByAssetType = _shapeUtilsByAssetType;
    const allBindingUtils = checkBindings(bindingUtils);
    const _bindingUtils = {};
    for (const Util of allBindingUtils) {
      const util = new Util(this);
      _bindingUtils[Util.type] = util;
    }
    this.bindingUtils = _bindingUtils;
    if (assetUtilConstructors) {
      const allAssetUtils = checkAssets(assetUtilConstructors);
      const _assetUtils = {};
      for (const Util of allAssetUtils) {
        const util = new Util(this);
        _assetUtils[Util.type] = util;
      }
      this.assetUtils = _assetUtils;
    }
    for (const Tool of [...tools]) {
      if (hasOwnProperty(this.root.children, Tool.id)) {
        throw Error(`Can't override tool with id "${Tool.id}"`);
      }
      this.root.children[Tool.id] = new Tool(this, this.root);
    }
    this.scribbles = new ScribbleManager(this);
    this.overlays = new OverlayManager(this);
    this.disposables.add(() => this.overlays.dispose());
    if (overlayUtilConstructors) {
      for (const Util of overlayUtilConstructors) {
        const util = new Util(this);
        this.overlays.registerUtil(util);
      }
    }
    const cleanupInstancePageState = (prevPageState, shapesNoLongerInPage) => {
      let nextPageState = null;
      const selectedShapeIds = prevPageState.selectedShapeIds.filter(
        (id) => !shapesNoLongerInPage.has(id)
      );
      if (selectedShapeIds.length !== prevPageState.selectedShapeIds.length) {
        if (!nextPageState) nextPageState = { ...prevPageState };
        nextPageState.selectedShapeIds = selectedShapeIds;
      }
      const erasingShapeIds = prevPageState.erasingShapeIds.filter(
        (id) => !shapesNoLongerInPage.has(id)
      );
      if (erasingShapeIds.length !== prevPageState.erasingShapeIds.length) {
        if (!nextPageState) nextPageState = { ...prevPageState };
        nextPageState.erasingShapeIds = erasingShapeIds;
      }
      if (prevPageState.hoveredShapeId && shapesNoLongerInPage.has(prevPageState.hoveredShapeId)) {
        if (!nextPageState) nextPageState = { ...prevPageState };
        nextPageState.hoveredShapeId = null;
      }
      if (prevPageState.editingShapeId && shapesNoLongerInPage.has(prevPageState.editingShapeId)) {
        if (!nextPageState) nextPageState = { ...prevPageState };
        nextPageState.editingShapeId = null;
      }
      const hintingShapeIds = prevPageState.hintingShapeIds.filter(
        (id) => !shapesNoLongerInPage.has(id)
      );
      if (hintingShapeIds.length !== prevPageState.hintingShapeIds.length) {
        if (!nextPageState) nextPageState = { ...prevPageState };
        nextPageState.hintingShapeIds = hintingShapeIds;
      }
      if (prevPageState.focusedGroupId && shapesNoLongerInPage.has(prevPageState.focusedGroupId)) {
        if (!nextPageState) nextPageState = { ...prevPageState };
        nextPageState.focusedGroupId = null;
      }
      return nextPageState;
    };
    this.sideEffects = this.store.sideEffects;
    let deletedBindings = /* @__PURE__ */ new Map();
    const deletedShapeIds = /* @__PURE__ */ new Set();
    const invalidParents = /* @__PURE__ */ new Set();
    const createdShapes = /* @__PURE__ */ new Set();
    let invalidBindingTypes = /* @__PURE__ */ new Set();
    this.disposables.add(
      this.sideEffects.registerOperationCompleteHandler(() => {
        const deletedIds = deletedShapeIds.size ? new Set(deletedShapeIds) : null;
        deletedShapeIds.clear();
        if (deletedIds) {
          const updates = compact(
            this.getPageStates().map((pageState) => {
              return cleanupInstancePageState(pageState, deletedIds);
            })
          );
          if (updates.length) {
            this.store.put(updates);
          }
        }
        const justCreatedShapeIds = new Set(createdShapes);
        createdShapes.clear();
        for (const parentId of invalidParents) {
          invalidParents.delete(parentId);
          if (justCreatedShapeIds.has(parentId)) continue;
          const parent = this.getShape(parentId);
          if (!parent) continue;
          const util = this.getShapeUtil(parent);
          const changes = util.onChildrenChange?.(parent);
          if (changes?.length) {
            this.updateShapes(changes);
          }
        }
        if (invalidBindingTypes.size) {
          const t = invalidBindingTypes;
          invalidBindingTypes = /* @__PURE__ */ new Set();
          for (const type of t) {
            const util = this.getBindingUtil(type);
            util.onOperationComplete?.();
          }
        }
        if (deletedBindings.size) {
          const t = deletedBindings;
          deletedBindings = /* @__PURE__ */ new Map();
          for (const opts of t.values()) {
            this.getBindingUtil(opts.binding).onAfterDelete?.(opts);
          }
        }
        this.emit("update");
      })
    );
    this.disposables.add(
      this.sideEffects.register({
        shape: {
          afterCreate: (shape) => {
            createdShapes.add(shape.id);
            if (shape.parentId && isShapeId(shape.parentId)) {
              invalidParents.add(shape.parentId);
            }
          },
          afterChange: (shapeBefore, shapeAfter) => {
            for (const binding of this.getBindingsInvolvingShape(shapeAfter)) {
              invalidBindingTypes.add(binding.type);
              if (binding.fromId === shapeAfter.id) {
                this.getBindingUtil(binding).onAfterChangeFromShape?.({
                  binding,
                  shapeBefore,
                  shapeAfter,
                  reason: "self"
                });
              }
              if (binding.toId === shapeAfter.id) {
                this.getBindingUtil(binding).onAfterChangeToShape?.({
                  binding,
                  shapeBefore,
                  shapeAfter,
                  reason: "self"
                });
              }
            }
            if (shapeBefore.parentId !== shapeAfter.parentId) {
              const notifyBindingAncestryChange = (id) => {
                const descendantShape = this.getShape(id);
                if (!descendantShape) return;
                for (const binding of this.getBindingsInvolvingShape(descendantShape)) {
                  invalidBindingTypes.add(binding.type);
                  if (binding.fromId === descendantShape.id) {
                    this.getBindingUtil(binding).onAfterChangeFromShape?.({
                      binding,
                      shapeBefore: descendantShape,
                      shapeAfter: descendantShape,
                      reason: "ancestry"
                    });
                  }
                  if (binding.toId === descendantShape.id) {
                    this.getBindingUtil(binding).onAfterChangeToShape?.({
                      binding,
                      shapeBefore: descendantShape,
                      shapeAfter: descendantShape,
                      reason: "ancestry"
                    });
                  }
                }
              };
              notifyBindingAncestryChange(shapeAfter.id);
              this.visitDescendants(shapeAfter.id, notifyBindingAncestryChange);
            }
            if (shapeBefore.parentId !== shapeAfter.parentId && isPageId(shapeAfter.parentId)) {
              const allMovingIds = /* @__PURE__ */ new Set([shapeBefore.id]);
              this.visitDescendants(shapeBefore.id, (id) => {
                allMovingIds.add(id);
              });
              for (const instancePageState of this.getPageStates()) {
                if (instancePageState.pageId === shapeAfter.parentId) continue;
                const nextPageState = cleanupInstancePageState(instancePageState, allMovingIds);
                if (nextPageState) {
                  this.store.put([nextPageState]);
                }
              }
            }
            if (shapeBefore.parentId && isShapeId(shapeBefore.parentId)) {
              invalidParents.add(shapeBefore.parentId);
            }
            if (shapeAfter.parentId !== shapeBefore.parentId && isShapeId(shapeAfter.parentId)) {
              invalidParents.add(shapeAfter.parentId);
            }
          },
          beforeDelete: (shape) => {
            if (deletedShapeIds.has(shape.id)) return;
            if (shape.parentId && isShapeId(shape.parentId)) {
              invalidParents.add(shape.parentId);
            }
            deletedShapeIds.add(shape.id);
            const deleteBindingIds = [];
            for (const binding of this.getBindingsInvolvingShape(shape)) {
              invalidBindingTypes.add(binding.type);
              deleteBindingIds.push(binding.id);
              const util = this.getBindingUtil(binding);
              if (binding.fromId === shape.id) {
                util.onBeforeIsolateToShape?.({ binding, removedShape: shape });
                util.onBeforeDeleteFromShape?.({ binding, shape });
              } else {
                util.onBeforeIsolateFromShape?.({ binding, removedShape: shape });
                util.onBeforeDeleteToShape?.({ binding, shape });
              }
            }
            if (deleteBindingIds.length) {
              this.deleteBindings(deleteBindingIds);
            }
          }
        },
        binding: {
          beforeCreate: (binding) => {
            const next = this.getBindingUtil(binding).onBeforeCreate?.({ binding });
            if (next) return next;
            return binding;
          },
          afterCreate: (binding) => {
            invalidBindingTypes.add(binding.type);
            this.getBindingUtil(binding).onAfterCreate?.({ binding });
          },
          beforeChange: (bindingBefore, bindingAfter) => {
            const updated = this.getBindingUtil(bindingAfter).onBeforeChange?.({
              bindingBefore,
              bindingAfter
            });
            if (updated) return updated;
            return bindingAfter;
          },
          afterChange: (bindingBefore, bindingAfter) => {
            invalidBindingTypes.add(bindingAfter.type);
            this.getBindingUtil(bindingAfter).onAfterChange?.({ bindingBefore, bindingAfter });
          },
          beforeDelete: (binding) => {
            this.getBindingUtil(binding).onBeforeDelete?.({ binding });
          },
          afterDelete: (binding) => {
            this.getBindingUtil(binding).onAfterDelete?.({ binding });
            invalidBindingTypes.add(binding.type);
          }
        },
        page: {
          afterCreate: (record) => {
            const cameraId = CameraRecordType.createId(record.id);
            const _pageStateId = InstancePageStateRecordType.createId(record.id);
            if (!this.store.has(cameraId)) {
              this.store.put([CameraRecordType.create({ id: cameraId })]);
            }
            if (!this.store.has(_pageStateId)) {
              this.store.put([
                InstancePageStateRecordType.create({ id: _pageStateId, pageId: record.id })
              ]);
            }
          },
          afterDelete: (record, source) => {
            if (this.getInstanceState()?.currentPageId === record.id) {
              const backupPageId = this.getPages().find((p) => p.id !== record.id)?.id;
              if (backupPageId) {
                this.store.put([{ ...this.getInstanceState(), currentPageId: backupPageId }]);
              } else if (source === "user") {
                this.store.ensureStoreIsUsable();
              }
            }
            const cameraId = CameraRecordType.createId(record.id);
            const instance_PageStateId = InstancePageStateRecordType.createId(record.id);
            this.store.remove([cameraId, instance_PageStateId]);
          }
        },
        instance: {
          afterChange: (prev, next, source) => {
            if (!this.store.has(next.currentPageId)) {
              const backupPageId = this.store.has(prev.currentPageId) ? prev.currentPageId : this.getPages()[0]?.id;
              if (backupPageId) {
                this.store.update(next.id, (instance) => ({
                  ...instance,
                  currentPageId: backupPageId
                }));
              } else if (source === "user") {
                this.store.ensureStoreIsUsable();
              }
            }
          }
        },
        instance_page_state: {
          afterChange: (prev, next) => {
            if (prev?.focusedGroupId !== next?.focusedGroupId) {
              this.cancelDoubleClick();
            }
            if (prev?.selectedShapeIds !== next?.selectedShapeIds) {
              const filtered = next.selectedShapeIds.filter((id) => {
                let parentId = this.getShape(id)?.parentId;
                while (isShapeId(parentId)) {
                  if (next.selectedShapeIds.includes(parentId)) {
                    return false;
                  }
                  parentId = this.getShape(parentId)?.parentId;
                }
                return true;
              });
              let nextFocusedGroupId = null;
              if (filtered.length > 0) {
                const commonGroupAncestor = this.findCommonAncestor(
                  compact(filtered.map((id) => this.getShape(id))),
                  (shape) => this.isShapeOfType(shape, "group")
                );
                if (commonGroupAncestor) {
                  nextFocusedGroupId = commonGroupAncestor;
                }
              } else {
                if (next?.focusedGroupId) {
                  nextFocusedGroupId = next.focusedGroupId;
                }
              }
              if (filtered.length !== next.selectedShapeIds.length || nextFocusedGroupId !== next.focusedGroupId) {
                this.store.put([
                  {
                    ...next,
                    selectedShapeIds: filtered,
                    focusedGroupId: nextFocusedGroupId ?? null
                  }
                ]);
              }
            }
          }
        }
      })
    );
    this._currentPageShapeIds = deriveShapeIdsInCurrentPage(
      this.store,
      () => this.getCurrentPageId()
    );
    this._parentIdsToChildIds = parentsToChildren(this.store);
    this.disposables.add(
      this.store.listen((changes) => {
        this.emit("change", changes);
      })
    );
    this.disposables.add(this.history.dispose);
    this.run(
      () => {
        this.store.ensureStoreIsUsable();
        this._updateCurrentPageState({
          editingShapeId: null,
          hoveredShapeId: null,
          erasingShapeIds: []
        });
      },
      { history: "ignore" }
    );
    if (initialState && this.root.children[initialState] === void 0) {
      throw Error(`No state found for initialState "${initialState}".`);
    }
    this.root.enter(void 0, "initial");
    this.edgeScrollManager = new EdgeScrollManager(this);
    this.focusManager = new FocusManager(this, autoFocus);
    this.disposables.add(() => this.focusManager.dispose());
    if (this.getInstanceState().followingUserId) {
      this.stopFollowingUser();
    }
    this.on("tick", this._flushEventsForTick);
    this.on("mount", () => {
      this._isMounted.set(true);
    });
    this.on("unmount", () => {
      this._isMounted.set(false);
    });
    this.timers.requestAnimationFrame(() => {
      this._tickManager.start();
    });
    this.performanceTracker = new PerformanceTracker();
    if (this.store.props.collaboration?.mode) {
      const mode = this.store.props.collaboration.mode;
      this.disposables.add(
        react("update collaboration mode", () => {
          this.store.put([{ ...this.getInstanceState(), isReadonly: mode.get() === "readonly" }]);
        })
      );
    }
    this.disposables.add(
      react("sync current user record", () => {
        const user2 = this.store.props.users.currentUser.get();
        if (user2) {
          this._ensureUserRecord(user2);
        }
      })
    );
  }
  _getShapeVisibility;
  getIsShapeHiddenCache() {
    if (!this._getShapeVisibility) return null;
    return this.store.createComputedCache("isShapeHidden", (shape) => {
      const visibility = this._getShapeVisibility(shape, this);
      const isParentHidden = PageRecordType.isId(shape.parentId) ? false : this.isShapeHidden(shape.parentId);
      if (isParentHidden) return visibility !== "visible";
      return visibility === "hidden";
    });
  }
  isShapeHidden(shapeOrId) {
    if (!this._getShapeVisibility) return false;
    return !!this.getIsShapeHiddenCache().get(
      typeof shapeOrId === "string" ? shapeOrId : shapeOrId.id
    );
  }
  options;
  contextId = uniqueId();
  /**
   * The editor's store
   *
   * @public
   */
  store;
  /**
   * The root state of the statechart.
   *
   * @public
   */
  root;
  /**
   * Set a tool. Useful if you need to add a tool to the state chart on demand,
   * after the editor has already been initialized.
   *
   * @param Tool - The tool to set.
   * @param parent - The parent state node to set the tool on.
   *
   * @public
   */
  setTool(Tool, parent) {
    parent ??= this.root;
    if (hasOwnProperty(parent.children, Tool.id)) {
      throw Error(`Can't override tool with id "${Tool.id}"`);
    }
    parent.children[Tool.id] = new Tool(this, parent);
  }
  /**
   * Remove a tool. Useful if you need to remove a tool from the state chart on demand,
   * after the editor has already been initialized.
   *
   * @param Tool - The tool to delete.
   * @param parent - The parent state node to remove the tool from.
   *
   * @public
   */
  removeTool(Tool, parent) {
    parent ??= this.root;
    if (hasOwnProperty(parent.children, Tool.id)) {
      delete parent.children[Tool.id];
    }
  }
  /**
   * A set of functions to call when the editor is disposed.
   *
   * @public
   */
  disposables = /* @__PURE__ */ new Set();
  /**
   * Whether the editor is disposed.
   *
   * @public
   */
  isDisposed = false;
  _isMounted = atom("isMounted", false);
  getIsMounted() {
    return this._isMounted.get();
  }
  /**
   * A manager for the editor's tick events.
   *
   * @internal */
  _tickManager;
  /**
   * A manager for the editor's input state.
   *
   * @public
   */
  inputs;
  /**
   * A manager for the editor's snapping feature.
   *
   * @public
   */
  snaps;
  /**
   * A manager for performance measurement hooks.
   *
   * @public
   */
  performance;
  /**
   * A manager for the spatial index, tracking where shapes exist on the canvas.
   *
   * @internal
   */
  _spatialIndex;
  /**
   * A manager for the any asynchronous events and making sure they're
   * cleaned up upon disposal.
   *
   * @public
   */
  timers = tltime.forContext(this.contextId);
  /**
   * A manager for remote peer collaborators connected to this editor.
   *
   * @public
   */
  collaborators;
  /**
   * A manager for the user and their preferences.
   *
   * @public
   */
  user;
  /**
   * A manager for the editor's themes.
   *
   * @internal
   */
  _themeManager;
  /**
   * A helper for measuring text.
   *
   * @public
   */
  textMeasure;
  /**
   * A utility for managing the set of fonts that should be rendered in the document.
   *
   * @public
   */
  fonts;
  /**
   * A manager for the editor's scribbles.
   *
   * @public
   */
  scribbles;
  /**
   * A manager for canvas overlay UI elements (selection handles, shape handles, etc.).
   *
   * @public
   */
  overlays;
  /**
   * A manager for side effects and correct state enforcement. See {@link @tldraw/store#StoreSideEffects} for details.
   *
   * @public
   */
  sideEffects;
  /**
   * A manager for moving the camera when the mouse is at the edge of the screen.
   *
   * @public
   */
  edgeScrollManager;
  /**
   * A manager for ensuring correct focus. See FocusManager for details.
   *
   * @internal
   */
  focusManager;
  /**
   * The current HTML element containing the editor.
   *
   * @example
   * ```ts
   * const container = editor.getContainer()
   * ```
   *
   * @public
   */
  getContainer;
  /**
   * The document that the editor's container element belongs to.
   * Use this instead of the global `document` to support cross-window embedding.
   *
   * @internal
   */
  getContainerDocument() {
    return this.getContainer().ownerDocument;
  }
  /**
   * The window that the editor's container element belongs to.
   * Use this instead of the global `window` to support cross-window embedding.
   *
   * @internal
   */
  getContainerWindow() {
    return getOwnerWindow(this.getContainer());
  }
  /**
   * Dispose the editor.
   *
   * @public
   */
  dispose() {
    if (this._isMounted.get()) {
      this.emit("unmount");
    }
    this.stopCameraAnimation();
    if (this.getInstanceState().followingUserId) {
      this.stopFollowingUser();
    }
    this.disposables.forEach((dispose) => dispose());
    this.disposables.clear();
    this.menus.clearOpenMenus();
    this.store.dispose();
    this.isDisposed = true;
    this.emit("dispose");
    this.removeAllListeners();
  }
  /* ------------------ Themes (shadowing the theme manager) ------------------ */
  /**
   * Get the current color mode (`'light'` or `'dark'`), based on the user's dark mode preference.
   *
   * @public
   */
  getColorMode() {
    return this._themeManager.getColorMode();
  }
  /**
   * Set the color mode. Note that this is a convenience method that passes the mode to
   * `user.updateUserPreferences`, which is the source of truth for the user's color mode preference.
   *
   * @public
   */
  setColorMode(mode) {
    this.user.updateUserPreferences({ colorScheme: mode });
    return this;
  }
  /**
   * Get the id of the current theme.
   *
   * @public
   */
  getCurrentThemeId() {
    return this._themeManager.getCurrentThemeId();
  }
  /**
   * Get the current theme definition.
   *
   * @public
   */
  getCurrentTheme() {
    return this._themeManager.getCurrentTheme();
  }
  /**
   * Set the current theme by id.
   *
   * @public
   */
  setCurrentTheme(id) {
    this._themeManager.setCurrentTheme(id);
    return this;
  }
  /**
   * Get all registered theme definitions.
   *
   * @public
   */
  getThemes() {
    return this._themeManager.getThemes();
  }
  /**
   * Get a single theme definition by id.
   *
   * @public
   */
  getTheme(id) {
    return this._themeManager.getTheme(id);
  }
  /**
   * Replace all theme definitions, or update them via a callback that receives a deep copy.
   * The `'default'` theme must always be present in the result.
   *
   * @example
   * ```ts
   * // Replace all themes
   * editor.updateThemes({ default: myDefaultTheme, ocean: myOceanTheme })
   *
   * // Update via callback
   * editor.updateThemes((themes) => {
   *   delete themes.ocean
   *   return themes
   * })
   * ```
   *
   * @public
   */
  updateThemes(themes) {
    this._themeManager.updateThemes(themes);
    return this;
  }
  /**
   * Register or update a single theme definition. The theme is keyed by its `id` property.
   *
   * @example
   * ```ts
   * // Override a property on the default theme
   * editor.updateTheme({ ...editor.getTheme('default')!, fontSize: 24 })
   *
   * // Register a new theme
   * editor.updateTheme({ id: 'ocean', ...myOceanTheme })
   * ```
   *
   * @public
   */
  updateTheme(theme) {
    this._themeManager.updateTheme(theme);
    return this;
  }
  /* ------------------- Shape Utils ------------------ */
  /**
   * A map of shape utility classes (TLShapeUtils) by shape type.
   *
   * @public
   */
  shapeUtils;
  /** @internal */
  _shapeUtilsByAssetType = {};
  styleProps;
  getShapeUtil(arg) {
    const type = typeof arg === "string" ? arg : arg.type;
    const shapeUtil = getOwnProperty(this.shapeUtils, type);
    assert(shapeUtil, `No shape util found for type "${type}"`);
    return shapeUtil;
  }
  hasShapeUtil(arg) {
    const type = typeof arg === "string" ? arg : arg.type;
    return hasOwnProperty(this.shapeUtils, type);
  }
  /**
   * Get the shape util that handles the given asset type.
   * Returns the shape util whose {@link ShapeUtil.handledAssetTypes} includes
   * the given asset type, or undefined if none matches.
   *
   * @param assetType - The asset type string.
   * @public
   */
  getShapeUtilForAssetType(assetType) {
    return getOwnProperty(this._shapeUtilsByAssetType, assetType);
  }
  /* ------------------- Binding Utils ------------------ */
  /**
   * A map of shape utility classes (TLShapeUtils) by shape type.
   *
   * @public
   */
  bindingUtils;
  getBindingUtil(arg) {
    const type = typeof arg === "string" ? arg : arg.type;
    const bindingUtil = getOwnProperty(this.bindingUtils, type);
    assert(bindingUtil, `No binding util found for type "${type}"`);
    return bindingUtil;
  }
  /* ------------------- Asset Utils ------------------ */
  /**
   * A map of asset utility classes by asset type.
   *
   * @public
   */
  assetUtils = {};
  getAssetUtil(arg) {
    const type = typeof arg === "string" ? arg : arg.type;
    const assetUtil = getOwnProperty(this.assetUtils, type);
    assert(assetUtil, `No asset util found for type "${type}"`);
    return assetUtil;
  }
  /**
   * Returns true if the editor has an asset util for the given asset type.
   *
   * @public
   */
  hasAssetUtil(arg) {
    const type = typeof arg === "string" ? arg : arg.type;
    return hasOwnProperty(this.assetUtils, type);
  }
  /**
   * Get the asset util that accepts the given MIME type.
   * Returns null if no registered asset util accepts the MIME type.
   *
   * @public
   */
  getAssetUtilForMimeType(mimeType) {
    for (const util of Object.values(this.assetUtils)) {
      if (util && util.acceptsMimeType(mimeType)) {
        return util;
      }
    }
    return null;
  }
  /* --------------------- History -------------------- */
  /**
   * A manager for the editor's history.
   *
   * @readonly
   */
  history;
  /**
   * Undo to the last mark.
   *
   * @example
   * ```ts
   * editor.undo()
   * ```
   *
   * @public
   */
  undo() {
    this._flushEventsForTick(0);
    this.complete();
    this.history.undo();
    this.performance._notifyUndoRedo("undo", this.history.getNumUndos(), this.history.getNumRedos());
    return this;
  }
  canUndo() {
    return this.history.getNumUndos() > 0;
  }
  getCanUndo() {
    return this.canUndo();
  }
  /**
   * Redo to the next mark.
   *
   * @example
   * ```ts
   * editor.redo()
   * ```
   *
   * @public
   */
  redo() {
    this._flushEventsForTick(0);
    this.complete();
    this.history.redo();
    this.performance._notifyUndoRedo("redo", this.history.getNumUndos(), this.history.getNumRedos());
    return this;
  }
  canRedo() {
    return this.history.getNumRedos() > 0;
  }
  getCanRedo() {
    return this.canRedo();
  }
  clearHistory() {
    this.history.clear();
    return this;
  }
  /**
   * Create a new "mark", or stopping point, in the undo redo history. Creating a mark will clear
   * any redos. You typically want to do this just before a user interaction begins or is handled.
   *
   * @example
   * ```ts
   * editor.markHistoryStoppingPoint()
   * editor.flipShapes(editor.getSelectedShapes())
   * ```
   * @example
   * ```ts
   * const beginRotateMark = editor.markHistoryStoppingPoint()
   * // if the use cancels the rotation, you can bail back to this mark
   * editor.bailToMark(beginRotateMark)
   * ```
   *
   * @public
   * @param name - The name of the mark, useful for debugging the undo/redo stacks
   * @returns a unique id for the mark that can be used with `squashToMark` or `bailToMark`.
   */
  markHistoryStoppingPoint(name) {
    const id = `[${name ?? "stop"}]_${uniqueId()}`;
    this.history._mark(id);
    return id;
  }
  /**
   * @internal this is only used to implement some backwards-compatibility logic. Should be fine to delete after 6 months or whatever.
   */
  getMarkIdMatching(idSubstring) {
    return this.history.getMarkIdMatching(idSubstring);
  }
  /**
   * Whether the editor is currently replaying history (i.e. an undo or redo is being applied).
   *
   * @internal
   */
  isReplayingHistory() {
    return this.history.isReplaying();
  }
  /**
   * Coalesces all changes since the given mark into a single change, removing any intermediate marks.
   *
   * This is useful if you need to 'compress' the recent history to simplify the undo/redo experience of a complex interaction.
   *
   * @example
   * ```ts
   * const bumpShapesMark = editor.markHistoryStoppingPoint()
   * // ... some changes
   * editor.squashToMark(bumpShapesMark)
   * ```
   *
   * @param markId - The mark id to squash to.
   */
  squashToMark(markId) {
    this.history.squashToMark(markId);
    return this;
  }
  /**
   * Undo to the closest mark, discarding the changes so they cannot be redone.
   *
   * @example
   * ```ts
   * editor.bail()
   * ```
   *
   * @public
   */
  bail() {
    this.history.bail();
    return this;
  }
  /**
   * Undo to the given mark, discarding the changes so they cannot be redone.
   *
   * @example
   * ```ts
   * const beginDrag = editor.markHistoryStoppingPoint()
   * // ... some changes
   * editor.bailToMark(beginDrag)
   * ```
   *
   * @public
   */
  bailToMark(id) {
    this.history.bailToMark(id);
    return this;
  }
  _shouldIgnoreShapeLock = false;
  /**
   * Run a function in a transaction with optional options for context.
   * You can use the options to change the way that history is treated
   * or allow changes to locked shapes.
   *
   * @example
   * ```ts
   * // updating with
   * editor.run(() => {
   * 	editor.updateShape({ ...myShape, x: 100 })
   * }, { history: "ignore" })
   *
   * // forcing changes / deletions for locked shapes
   * editor.toggleLock([myShape])
   * editor.run(() => {
   * 	editor.updateShape({ ...myShape, x: 100 })
   * 	editor.deleteShape(myShape)
   * }, { ignoreShapeLock: true }, )
   * ```
   *
   * @param fn - The callback function to run.
   * @param opts - The options for the batch.
   *
   *
   * @public
   */
  run(fn, opts) {
    const previousIgnoreShapeLock = this._shouldIgnoreShapeLock;
    this._shouldIgnoreShapeLock = opts?.ignoreShapeLock ?? previousIgnoreShapeLock;
    try {
      this.history.batch(fn, opts);
    } finally {
      this._shouldIgnoreShapeLock = previousIgnoreShapeLock;
    }
    return this;
  }
  /* --------------------- Errors --------------------- */
  /** @internal */
  annotateError(error, {
    origin,
    willCrashApp,
    tags,
    extras
  }) {
    const defaultAnnotations = this.createErrorAnnotations(origin, willCrashApp);
    annotateError(error, {
      tags: { ...defaultAnnotations.tags, ...tags },
      extras: { ...defaultAnnotations.extras, ...extras }
    });
    if (willCrashApp) {
      this.store.markAsPossiblyCorrupted();
    }
    return this;
  }
  /** @internal */
  createErrorAnnotations(origin, willCrashApp) {
    try {
      const editingShapeId = this.getEditingShapeId();
      return {
        tags: {
          origin,
          willCrashApp
        },
        extras: {
          activeStateNode: this.root.getPath(),
          selectedShapes: this.getSelectedShapes().map((s) => {
            const { props, ...rest } = s;
            const { text: _text, richText: _richText, ...restProps } = props;
            return {
              ...rest,
              props: restProps
            };
          }),
          selectionCount: this.getSelectedShapes().length,
          editingShape: editingShapeId ? this.getShape(editingShapeId) : void 0,
          inputs: this.inputs.toJson(),
          pageState: this.getCurrentPageState(),
          instanceState: this.getInstanceState(),
          collaboratorCount: this.getCollaboratorsOnCurrentPage().length
        }
      };
    } catch {
      return {
        tags: {
          origin,
          willCrashApp
        },
        extras: {}
      };
    }
  }
  /** @internal */
  _crashingError = null;
  /**
   * We can't use an `atom` here because there's a chance that when `crashAndReportError` is called,
   * we're in a transaction that's about to be rolled back due to the same error we're currently
   * reporting.
   *
   * Instead, to listen to changes to this value, you need to listen to editor's `crash` event.
   *
   * @internal
   */
  getCrashingError() {
    return this._crashingError;
  }
  /** @internal */
  crash(error) {
    this._crashingError = error;
    this.store.markAsPossiblyCorrupted();
    this.emit("crash", { error });
    return this;
  }
  getPath() {
    return this.root.getPath().split("root.")[1];
  }
  /**
   * Get whether a certain tool (or other state node) is currently active.
   *
   * @example
   * ```ts
   * editor.isIn('select')
   * editor.isIn('select.brushing')
   * ```
   *
   * @param path - The path of active states, separated by periods.
   *
   * @public
   */
  isIn(path) {
    const ids = path.split(".").reverse();
    let state = this.root;
    while (ids.length > 0) {
      const id = ids.pop();
      if (!id) return true;
      const current = state.getCurrent();
      if (current?.id === id) {
        if (ids.length === 0) return true;
        state = current;
        continue;
      } else return false;
    }
    return false;
  }
  /**
   * Get whether the state node is in any of the given active paths.
   *
   * @example
   * ```ts
   * state.isInAny('select', 'erase')
   * state.isInAny('select.brushing', 'erase.idle')
   * ```
   *
   * @public
   */
  isInAny(...paths) {
    return paths.some((path) => this.isIn(path));
  }
  /**
   * Set the selected tool.
   *
   * @example
   * ```ts
   * editor.setCurrentTool('hand')
   * editor.setCurrentTool('hand', { date: Date.now() })
   * ```
   *
   * @param id - The id of the tool to select.
   * @param info - Arbitrary data to pass along into the transition.
   *
   * @public
   */
  setCurrentTool(id, info = {}) {
    this.root.transition(id, info);
    return this;
  }
  getCurrentTool() {
    return this.root.getCurrent();
  }
  getCurrentToolId() {
    const currentTool = this.getCurrentTool();
    if (!currentTool) return "";
    return currentTool.getCurrentToolIdMask() ?? currentTool.id;
  }
  /**
   * Get a descendant by its path.
   *
   * @example
   * ```ts
   * editor.getStateDescendant('select')
   * editor.getStateDescendant('select.brushing')
   * ```
   *
   * @param path - The descendant's path of state ids, separated by periods.
   *
   * @public
   */
  getStateDescendant(path) {
    const ids = path.split(".").reverse();
    let state = this.root;
    while (ids.length > 0) {
      const id = ids.pop();
      if (!id) return state;
      const childState = state.children?.[id];
      if (!childState) return void 0;
      state = childState;
    }
    return state;
  }
  getDocumentSettings() {
    return this.store.get(TLDOCUMENT_ID);
  }
  /**
   * Update the global document settings that apply to all users.
   *
   * @public
   **/
  updateDocumentSettings(settings) {
    this.run(
      () => {
        this.store.put([{ ...this.getDocumentSettings(), ...settings }]);
      },
      { history: "ignore" }
    );
    return this;
  }
  getInstanceState() {
    return this.store.get(TLINSTANCE_ID);
  }
  /**
   * Update the instance's state.
   *
   * @param partial - A partial object to update the instance state with.
   * @param historyOptions - History batch options.
   *
   * @public
   */
  updateInstanceState(partial, historyOptions) {
    this._updateInstanceState(partial, { history: "ignore", ...historyOptions });
    if (partial.isChangingStyle !== void 0) {
      clearTimeout(this._isChangingStyleTimeout);
      if (partial.isChangingStyle === true) {
        this._isChangingStyleTimeout = this.timers.setTimeout(() => {
          this._updateInstanceState({ isChangingStyle: false }, { history: "ignore" });
        }, 1e3);
      }
    }
    return this;
  }
  /** @internal */
  _updateInstanceState(partial, opts) {
    this.run(() => {
      this.store.put([
        {
          ...this.getInstanceState(),
          ...partial
        }
      ]);
    }, opts);
  }
  /** @internal */
  _isChangingStyleTimeout = -1;
  // Menus
  menus = tlmenus.forContext(this.contextId);
  /* --------------------- Cursor --------------------- */
  /**
   * Set the cursor.
   *
   * No-op when the partial wouldn't change the current cursor — `setCursor`
   * is called from pointer-move hot paths (see `updateHoveredOverlayId`,
   * various tool states) and skipping redundant writes avoids needlessly
   * dirtying instance state.
   *
   * @param cursor - The cursor to set.
   * @public
   */
  setCursor(cursor) {
    const current = this.getInstanceState().cursor;
    if ((cursor.type === void 0 || cursor.type === current.type) && (cursor.rotation === void 0 || cursor.rotation === current.rotation)) {
      return this;
    }
    this.updateInstanceState({ cursor: { ...current, ...cursor } });
    return this;
  }
  getPageStates() {
    return this._getPageStatesQuery().get();
  }
  _getPageStatesQuery() {
    return this.store.query.records("instance_page_state");
  }
  getCurrentPageState() {
    return this.store.get(this._getCurrentPageStateId());
  }
  _getCurrentPageStateId() {
    return InstancePageStateRecordType.createId(this.getCurrentPageId());
  }
  /**
   * Update this instance's page state.
   *
   * @example
   * ```ts
   * editor.updateCurrentPageState({ id: 'page1', editingShapeId: 'shape:123' })
   * ```
   *
   * @param partial - The partial of the page state object containing the changes.
   *
   * @public
   */
  updateCurrentPageState(partial) {
    this._updateCurrentPageState(partial);
    return this;
  }
  _updateCurrentPageState(partial) {
    this.store.update(partial.id ?? this.getCurrentPageState().id, (state) => ({
      ...state,
      ...partial
    }));
  }
  getSelectedShapeIds() {
    return this.getCurrentPageState().selectedShapeIds;
  }
  getSelectedShapes() {
    return compact(this.getSelectedShapeIds().map((id) => this.store.get(id)));
  }
  /**
   * Select one or more shapes.
   *
   * @example
   * ```ts
   * editor.setSelectedShapes(['id1'])
   * editor.setSelectedShapes(['id1', 'id2'])
   * ```
   *
   * @param shapes - The shape (or shape ids) to select.
   *
   * @public
   */
  setSelectedShapes(shapes) {
    return this.run(
      () => {
        const ids = shapes.map((shape) => typeof shape === "string" ? shape : shape.id);
        const { selectedShapeIds: prevSelectedShapeIds } = this.getCurrentPageState();
        const prevSet = new Set(prevSelectedShapeIds);
        if (ids.length === prevSet.size && ids.every((id) => prevSet.has(id))) return null;
        this.store.put([{ ...this.getCurrentPageState(), selectedShapeIds: ids }]);
      },
      { history: "record-preserveRedoStack" }
    );
  }
  /**
   * Determine whether or not any of a shape's ancestors are selected.
   *
   * @param shape - The shape (or shape id) of the shape to check.
   *
   * @public
   */
  isAncestorSelected(shape) {
    const id = typeof shape === "string" ? shape : shape?.id ?? null;
    const _shape = this.getShape(id);
    if (!_shape) return false;
    const selectedShapeIds = this.getSelectedShapeIds();
    return !!this.findShapeAncestor(_shape, (parent) => selectedShapeIds.includes(parent.id));
  }
  /**
   * Select one or more shapes.
   *
   * @example
   * ```ts
   * editor.select('id1')
   * editor.select('id1', 'id2')
   * ```
   *
   * @param shapes - The shape (or the shape ids) to select.
   *
   * @public
   */
  select(...shapes) {
    const ids = typeof shapes[0] === "string" ? shapes : shapes.map((shape) => shape.id);
    this.setSelectedShapes(ids);
    return this;
  }
  /**
   * Remove a shape from the existing set of selected shapes.
   *
   * @example
   * ```ts
   * editor.deselect(shape.id)
   * ```
   *
   * @public
   */
  deselect(...shapes) {
    const ids = typeof shapes[0] === "string" ? shapes : shapes.map((shape) => shape.id);
    const selectedShapeIds = this.getSelectedShapeIds();
    if (selectedShapeIds.length > 0 && ids.length > 0) {
      this.setSelectedShapes(selectedShapeIds.filter((id) => !ids.includes(id)));
    }
    return this;
  }
  /**
   * Select all shapes. If the user has selected shapes that share a parent,
   * select all shapes within that parent. If the user has not selected any shapes,
   * or if the shapes shapes are only on select all shapes on the current page.
   *
   * @example
   * ```ts
   * editor.selectAll()
   * ```
   *
   * @public
   */
  selectAll() {
    let parentToSelectWithinId = null;
    const selectedShapeIds = this.getSelectedShapeIds();
    if (selectedShapeIds.length > 0) {
      for (const id of selectedShapeIds) {
        const shape = this.getShape(id);
        if (!shape) continue;
        if (parentToSelectWithinId === null) {
          parentToSelectWithinId = shape.parentId;
        } else if (parentToSelectWithinId !== shape.parentId) {
          return this;
        }
      }
    }
    if (!parentToSelectWithinId) {
      parentToSelectWithinId = this.getCurrentPageId();
    }
    const ids = this.getSortedChildIdsForParent(parentToSelectWithinId);
    if (ids.length <= 0) return this;
    this.setSelectedShapes(this._getUnlockedShapeIds(ids));
    return this;
  }
  /**
   * Select the next shape in the reading order or in cardinal order.
   *
   * @example
   * ```ts
   * editor.selectAdjacentShape('next')
   * ```
   *
   * @public
   */
  selectAdjacentShape(direction) {
    const selectedShapeIds = this.getSelectedShapeIds();
    const firstParentId = selectedShapeIds[0] ? this.getShape(selectedShapeIds[0])?.parentId : null;
    const isSelectedWithinContainer = firstParentId && selectedShapeIds.every((shapeId) => this.getShape(shapeId)?.parentId === firstParentId) && !isPageId(firstParentId);
    const filteredShapes = isSelectedWithinContainer ? this.getCurrentPageShapes().filter((shape2) => shape2.parentId === firstParentId) : this.getCurrentPageShapes().filter((shape2) => isPageId(shape2.parentId));
    const readingOrderShapes = isSelectedWithinContainer ? this._getShapesInReadingOrder(filteredShapes) : this.getCurrentPageShapesInReadingOrder();
    const currentShapeId = selectedShapeIds.length === 1 ? selectedShapeIds[0] : readingOrderShapes.find((shape2) => selectedShapeIds.includes(shape2.id))?.id;
    let adjacentShapeId;
    if (direction === "next" || direction === "prev") {
      const shapeIds = readingOrderShapes.map((shape2) => shape2.id);
      const currentIndex = currentShapeId ? shapeIds.indexOf(currentShapeId) : -1;
      const adjacentIndex = (currentIndex + (direction === "next" ? 1 : -1) + shapeIds.length) % shapeIds.length;
      adjacentShapeId = shapeIds[adjacentIndex];
    } else {
      if (!currentShapeId) return;
      adjacentShapeId = this.getNearestAdjacentShape(filteredShapes, currentShapeId, direction);
    }
    const shape = this.getShape(adjacentShapeId);
    if (!shape) return;
    this._selectShapesAndZoom([shape.id]);
  }
  getCurrentPageShapesInReadingOrder() {
    const shapes = this.getCurrentPageShapes().filter((shape) => isPageId(shape.parentId));
    return this._getShapesInReadingOrder(shapes);
  }
  _getShapesInReadingOrder(shapes) {
    const SHALLOW_ANGLE = 20;
    const ROW_THRESHOLD = 100;
    const tabbableShapes = shapes.filter((shape) => this.getShapeUtil(shape).canTabTo(shape));
    if (tabbableShapes.length <= 1) return tabbableShapes;
    const shapesWithCenters = tabbableShapes.map((shape) => ({
      shape,
      center: this.getShapePageBounds(shape).center
    }));
    shapesWithCenters.sort((a, b) => a.center.y - b.center.y);
    const rows = [];
    for (const shapeWithCenter of shapesWithCenters) {
      let rowIndex = -1;
      for (let i = rows.length - 1; i >= 0; i--) {
        const row = rows[i];
        const lastShapeInRow = row[row.length - 1];
        if (Math.abs(shapeWithCenter.center.y - lastShapeInRow.center.y) < ROW_THRESHOLD) {
          rowIndex = i;
          break;
        }
      }
      if (rowIndex === -1) {
        rows.push([shapeWithCenter]);
      } else {
        rows[rowIndex].push(shapeWithCenter);
      }
    }
    for (const row of rows) {
      row.sort((a, b) => a.center.x - b.center.x);
    }
    for (const row of rows) {
      if (row.length <= 2) continue;
      for (let i = 0; i < row.length - 2; i++) {
        const currentShape = row[i];
        const nextShape = row[i + 1];
        const nextNextShape = row[i + 2];
        const dist1 = Vec.Dist2(currentShape.center, nextShape.center);
        const dist2 = Vec.Dist2(currentShape.center, nextNextShape.center);
        if (dist2 < dist1 * 0.9) {
          const angle = Math.abs(
            Vec.Angle(currentShape.center, nextNextShape.center) * (180 / Math.PI)
          );
          if (angle <= SHALLOW_ANGLE) {
            ;
            [row[i + 1], row[i + 2]] = [row[i + 2], row[i + 1]];
          }
        }
      }
    }
    return rows.flat().map((item) => item.shape);
  }
  /**
   * Find the nearest adjacent shape in a specific direction.
   *
   * @public
   */
  getNearestAdjacentShape(shapes, currentShapeId, direction) {
    const directionToAngle = { right: 0, left: 180, down: 90, up: 270 };
    const currentShape = this.getShape(currentShapeId);
    if (!currentShape) return currentShapeId;
    const tabbableShapes = shapes.filter(
      (shape) => this.getShapeUtil(shape).canTabTo(shape) && shape.id !== currentShapeId
    );
    if (!tabbableShapes.length) return currentShapeId;
    const currentCenter = this.getShapePageBounds(currentShape).center;
    const shapesWithCenters = tabbableShapes.map((shape) => ({
      shape,
      center: this.getShapePageBounds(shape).center
    }));
    const shapesInDirection = shapesWithCenters.filter(({ center }) => {
      const isRight = center.x > currentCenter.x;
      const isDown = center.y > currentCenter.y;
      const xDist = center.x - currentCenter.x;
      const yDist = center.y - currentCenter.y;
      const isInXDirection = Math.abs(yDist) < Math.abs(xDist) * 2;
      const isInYDirection = Math.abs(xDist) < Math.abs(yDist) * 2;
      if (direction === "left" || direction === "right") {
        return isInXDirection && (direction === "right" ? isRight : !isRight);
      }
      if (direction === "up" || direction === "down") {
        return isInYDirection && (direction === "down" ? isDown : !isDown);
      }
    });
    if (shapesInDirection.length === 0) return currentShapeId;
    const lowestScoringShape = minBy(shapesInDirection, ({ center }) => {
      const distance = Vec.Dist2(currentCenter, center);
      const dirProp = ["left", "right"].includes(direction) ? "x" : "y";
      const directionalDistance = Math.abs(center[dirProp] - currentCenter[dirProp]);
      const offProp = ["left", "right"].includes(direction) ? "y" : "x";
      const offAxisDeviation = Math.abs(center[offProp] - currentCenter[offProp]);
      const angle = Math.abs(Vec.Angle(currentCenter, center) * (180 / Math.PI));
      const angleDeviation = Math.abs(angle - directionToAngle[direction]);
      return distance * 1 + // Base distance
      offAxisDeviation * 2 + // Heavy penalty for off-axis deviation
      (distance - directionalDistance) * 1.5 + // Penalty for diagonal distance
      angleDeviation * 0.5;
    });
    return lowestScoringShape.shape.id;
  }
  selectParentShape() {
    const selectedShape = this.getOnlySelectedShape();
    if (!selectedShape) return;
    const parentShape = this.getShape(selectedShape.parentId);
    if (!parentShape) return;
    this._selectShapesAndZoom([parentShape.id]);
  }
  selectFirstChildShape() {
    const selectedShapes = this.getSelectedShapes();
    if (!selectedShapes.length) return;
    const selectedShape = selectedShapes[0];
    const children = this.getSortedChildIdsForParent(selectedShape.id).map((id) => this.getShape(id)).filter((i) => i);
    const sortedChildren = this._getShapesInReadingOrder(children);
    if (sortedChildren.length === 0) return;
    this._selectShapesAndZoom([sortedChildren[0].id]);
  }
  _selectShapesAndZoom(ids) {
    this.setSelectedShapes(ids);
    this.zoomToSelectionIfOffscreen(256, {
      animation: {
        duration: this.options.animationMediumMs
      },
      inset: 0
    });
  }
  /**
   * Clear the selection.
   *
   * @example
   * ```ts
   * editor.selectNone()
   * ```
   *
   * @public
   */
  selectNone() {
    if (this.getSelectedShapeIds().length > 0) {
      this.setSelectedShapes([]);
    }
    return this;
  }
  getOnlySelectedShapeId() {
    return this.getOnlySelectedShape()?.id ?? null;
  }
  getOnlySelectedShape() {
    const selectedShapes = this.getSelectedShapes();
    return selectedShapes.length === 1 ? selectedShapes[0] : null;
  }
  /**
   * Get the page bounds of all the provided shapes.
   *
   * @public
   */
  getShapesPageBounds(shapeIds) {
    const bounds = compact(shapeIds.map((id) => this.getShapePageBounds(id)));
    if (bounds.length === 0) return null;
    return Box.Common(bounds);
  }
  getSelectionPageBounds() {
    return this.getShapesPageBounds(this.getSelectedShapeIds());
  }
  /**
   * The bounds of the selection bounding box in the current page space.
   *
   * @readonly
   * @public
   */
  getSelectionScreenBounds() {
    const bounds = this.getSelectionPageBounds();
    if (!bounds) return void 0;
    const { x, y } = this.pageToScreen(bounds.point);
    const zoom = this.getZoomLevel();
    return new Box(x, y, bounds.width * zoom, bounds.height * zoom);
  }
  /**
   * @internal
   */
  getShapesSharedRotation(shapeIds) {
    let foundFirst = false;
    let rotation = 0;
    for (let i = 0, n = shapeIds.length; i < n; i++) {
      const pageTransform = this.getShapePageTransform(shapeIds[i]);
      if (!pageTransform) continue;
      if (foundFirst) {
        if (pageTransform.rotation() !== rotation) {
          return 0;
        }
      } else {
        foundFirst = true;
        rotation = pageTransform.rotation();
      }
    }
    return rotation;
  }
  getSelectionRotation() {
    return this.getShapesSharedRotation(this.getSelectedShapeIds());
  }
  /**
   * @internal
   */
  getShapesRotatedPageBounds(shapeIds) {
    if (shapeIds.length === 0) {
      return void 0;
    }
    const selectionRotation = this.getShapesSharedRotation(shapeIds);
    if (selectionRotation === 0) {
      return this.getShapesPageBounds(shapeIds) ?? void 0;
    }
    if (shapeIds.length === 1) {
      const bounds = this.getShapeGeometry(shapeIds[0]).bounds.clone();
      const pageTransform = this.getShapePageTransform(shapeIds[0]);
      bounds.point = pageTransform.applyToPoint(bounds.point);
      return bounds;
    }
    const boxFromRotatedVertices = Box.FromPoints(
      shapeIds.flatMap((id) => {
        const pageTransform = this.getShapePageTransform(id);
        if (!pageTransform) return [];
        return pageTransform.applyToPoints(this.getShapeGeometry(id).bounds.corners);
      }).map((p) => p.rot(-selectionRotation))
    );
    boxFromRotatedVertices.point = boxFromRotatedVertices.point.rot(selectionRotation);
    return boxFromRotatedVertices;
  }
  getSelectionRotatedPageBounds() {
    return this.getShapesRotatedPageBounds(this.getSelectedShapeIds());
  }
  getSelectionRotatedScreenBounds() {
    const bounds = this.getSelectionRotatedPageBounds();
    if (!bounds) return void 0;
    const { x, y } = this.pageToScreen(bounds.point);
    const zoom = this.getZoomLevel();
    return new Box(x, y, bounds.width * zoom, bounds.height * zoom);
  }
  getFocusedGroupId() {
    return this.getCurrentPageState().focusedGroupId ?? this.getCurrentPageId();
  }
  getFocusedGroup() {
    const focusedGroupId = this.getFocusedGroupId();
    return focusedGroupId ? this.getShape(focusedGroupId) : void 0;
  }
  /**
   * Set the current focused group shape.
   *
   * @param shape - The group shape id (or group shape's id) to set as the focused group shape.
   *
   * @public
   */
  setFocusedGroup(shape) {
    const id = typeof shape === "string" ? shape : shape?.id ?? null;
    if (id !== null) {
      const shape2 = this.getShape(id);
      if (!shape2) {
        throw Error(`Editor.setFocusedGroup: Shape with id ${id} does not exist`);
      }
      if (!this.isShapeOfType(shape2, "group")) {
        throw Error(
          `Editor.setFocusedGroup: Cannot set focused group to shape of type ${shape2.type}`
        );
      }
    }
    if (id === this.getFocusedGroupId()) return this;
    return this.run(
      () => {
        this.store.update(this.getCurrentPageState().id, (s) => ({ ...s, focusedGroupId: id }));
      },
      { history: "record-preserveRedoStack" }
    );
  }
  /**
   * Exit the current focused group, moving up to the next parent group if there is one.
   *
   * @public
   */
  popFocusedGroupId() {
    const focusedGroup = this.getFocusedGroup();
    if (focusedGroup) {
      const match = this.findShapeAncestor(
        focusedGroup,
        (shape) => this.isShapeOfType(shape, "group")
      );
      this.setFocusedGroup(match?.id ?? null);
      this.select(focusedGroup.id);
    } else {
      this.setFocusedGroup(null);
      this.selectNone();
    }
    return this;
  }
  getEditingShapeId() {
    return this.getCurrentPageState().editingShapeId;
  }
  getEditingShape() {
    const editingShapeId = this.getEditingShapeId();
    return editingShapeId ? this.getShape(editingShapeId) : void 0;
  }
  /**
   * Whether the shape can be edited.
   *
   * @param shape - The shape (or shape id) to check if it can be edited.
   * @param info - The info about the edit start.
   *
   * @public
   * @returns true if the shape can be edited, false otherwise.
   */
  canEditShape(shape, info) {
    const id = typeof shape === "string" ? shape : shape?.id ?? null;
    if (!id) return false;
    if (id === this.getEditingShapeId()) return false;
    const _shape = this.getShape(id);
    if (!_shape) return false;
    const util = this.getShapeUtil(_shape);
    const _info = info ?? { type: "unknown" };
    if (!util.canEdit(_shape, _info)) return false;
    if (this.getIsReadonly() && !util.canEditInReadonly(_shape)) return false;
    if (this.isShapeOrAncestorLocked(_shape) && !util.canEditWhileLocked(_shape)) return false;
    return true;
  }
  /**
   * Set the current editing shape.
   *
   * @example
   * ```ts
   * editor.setEditingShape(myShape)
   * editor.setEditingShape(myShape.id)
   * ```
   *
   * @param shape - The shape (or shape id) to set as editing.
   *
   * @public
   */
  setEditingShape(shape) {
    const id = typeof shape === "string" ? shape : shape?.id ?? null;
    if (!id) {
      this.run(
        () => {
          const prevEditingShapeId = this.getEditingShapeId();
          if (prevEditingShapeId) {
            const prevEditingShape = this.getShape(prevEditingShapeId);
            if (prevEditingShape) {
              this.getShapeUtil(prevEditingShape).onEditEnd?.(prevEditingShape);
            }
          }
          this._updateCurrentPageState({ editingShapeId: null });
          this._currentRichTextEditor.set(null);
        },
        { history: "ignore" }
      );
      return this;
    }
    if (!this.canEditShape(id)) return this;
    this.run(
      () => {
        const prevEditingShapeId = this.getEditingShapeId();
        if (prevEditingShapeId) {
          const prevEditingShape = this.getShape(prevEditingShapeId);
          if (prevEditingShape) {
            this.getShapeUtil(prevEditingShape).onEditEnd?.(prevEditingShape);
          }
        }
        this._updateCurrentPageState({ editingShapeId: null });
        this._currentRichTextEditor.set(null);
        this.select(id);
        this._updateCurrentPageState({ editingShapeId: id });
        const nextEditingShape = this.getShape(id);
        this.getShapeUtil(nextEditingShape).onEditStart?.(nextEditingShape);
      },
      { history: "ignore" }
    );
    return this;
  }
  // Rich text editor
  _currentRichTextEditor = atom("rich text editor", null);
  getRichTextEditor() {
    return this._currentRichTextEditor.get();
  }
  /**
   * Set the current editing shape's rich text editor.
   *
   * @example
   * ```ts
   * editor.setRichTextEditor(richTextEditorView)
   * ```
   *
   * @param textEditor - The text editor to set as the current editing shape's text editor.
   *
   * @public
   */
  setRichTextEditor(textEditor) {
    this._currentRichTextEditor.set(textEditor);
    return this;
  }
  getHoveredShapeId() {
    return this.getCurrentPageState().hoveredShapeId;
  }
  getHoveredShape() {
    const hoveredShapeId = this.getHoveredShapeId();
    return hoveredShapeId ? this.getShape(hoveredShapeId) : void 0;
  }
  /**
   * Set the editor's current hovered shape.
   *
   * @example
   * ```ts
   * editor.setHoveredShape(myShape)
   * editor.setHoveredShape(myShape.id)
   * ```
   *
   * @param shape - The shape (or shape id) to set as hovered.
   *
   * @public
   */
  setHoveredShape(shape) {
    const id = typeof shape === "string" ? shape : shape?.id ?? null;
    if (id === this.getHoveredShapeId()) return this;
    this.run(
      () => {
        this.updateCurrentPageState({ hoveredShapeId: id });
      },
      { history: "ignore" }
    );
    return this;
  }
  getHintingShapeIds() {
    return this.getCurrentPageState().hintingShapeIds;
  }
  getHintingShape() {
    const hintingShapeIds = this.getHintingShapeIds();
    return compact(hintingShapeIds.map((id) => this.getShape(id)));
  }
  /**
   * Set the editor's current hinting shapes.
   *
   * @example
   * ```ts
   * editor.setHintingShapes([myShape])
   * editor.setHintingShapes([myShape.id])
   * ```
   *
   * @param shapes - The shapes (or shape ids) to set as hinting.
   *
   * @public
   */
  setHintingShapes(shapes) {
    const ids = typeof shapes[0] === "string" ? shapes : shapes.map((shape) => shape.id);
    this.run(
      () => {
        this._updateCurrentPageState({ hintingShapeIds: dedupe(ids) });
      },
      { history: "ignore" }
    );
    return this;
  }
  getErasingShapeIds() {
    return this.getCurrentPageState().erasingShapeIds;
  }
  getErasingShapes() {
    const erasingShapeIds = this.getErasingShapeIds();
    return compact(erasingShapeIds.map((id) => this.getShape(id)));
  }
  /**
   * Set the editor's current erasing shapes.
   *
   * @example
   * ```ts
   * editor.setErasingShapes([myShape])
   * editor.setErasingShapes([myShape.id])
   * ```
   *
   * @param shapes - The shapes (or shape ids) to set as hinting.
   *
   * @public
   */
  setErasingShapes(shapes) {
    const ids = typeof shapes[0] === "string" ? shapes : shapes.map((shape) => shape.id);
    ids.sort();
    const erasingShapeIds = this.getErasingShapeIds();
    this.run(
      () => {
        if (ids.length === erasingShapeIds.length) {
          for (let i = 0; i < ids.length; i++) {
            if (ids[i] !== erasingShapeIds[i]) {
              this._updateCurrentPageState({ erasingShapeIds: ids });
              break;
            }
          }
        } else {
          this._updateCurrentPageState({ erasingShapeIds: ids });
        }
      },
      { history: "ignore" }
    );
    return this;
  }
  // Cropping
  /**
   * The current cropping shape's id.
   *
   * @public
   */
  getCroppingShapeId() {
    return this.getCurrentPageState().croppingShapeId;
  }
  /**
   * Whether the shape can be cropped.
   *
   * @param shape - The shape (or shape id) to check if it can be cropped.
   *
   * @public
   * @returns true if the shape can be cropped, false otherwise.
   */
  canCropShape(shape) {
    if (!shape) return false;
    const id = typeof shape === "string" ? shape : shape?.id ?? null;
    if (!id) return false;
    const _shape = this.getShape(id);
    if (!_shape) return false;
    const util = this.getShapeUtil(_shape);
    if (!util.canCrop(_shape)) return false;
    if (this.isShapeOrAncestorLocked(_shape)) return false;
    return true;
  }
  /**
   * Set the current cropping shape.
   *
   * @example
   * ```ts
   * editor.setCroppingShape(myShape)
   * editor.setCroppingShape(myShape.id)
   * ```
   *
   *
   * @param shape - The shape (or shape id) to set as cropping.
   *
   * @public
   */
  setCroppingShape(shape) {
    const id = typeof shape === "string" ? shape : shape?.id ?? null;
    if (id !== this.getCroppingShapeId()) {
      this.run(
        () => {
          if (!id) {
            this.updateCurrentPageState({ croppingShapeId: null });
          } else if (this.canCropShape(id)) {
            this.updateCurrentPageState({ croppingShapeId: id });
          }
        },
        { history: "ignore" }
      );
    }
    return this;
  }
  _textOptions;
  /**
   * Get the current text options.
   *
   * @example
   * ```ts
   * editor.getTextOptions()
   * ```
   *
   *  @public */
  getTextOptions() {
    return assertExists(this._textOptions.get(), "Cannot use text without setting textOptions");
  }
  _unsafe_getCameraId() {
    return CameraRecordType.createId(this.getCurrentPageId());
  }
  getCamera() {
    const baseCamera = this.store.get(this._unsafe_getCameraId());
    if (this._isLockedOnFollowingUser.get()) {
      const followingCamera = this.getCameraForFollowing();
      if (followingCamera) {
        return { ...baseCamera, ...followingCamera };
      }
    }
    return baseCamera;
  }
  _getFollowingPresence(targetUserId) {
    const visited = [this.user.getRecordId()];
    const collaborators = this.getCollaborators();
    let leaderPresence = null;
    while (targetUserId && !visited.includes(targetUserId)) {
      leaderPresence = collaborators.find((c) => c.userId === targetUserId) ?? null;
      targetUserId = leaderPresence?.followingUserId ?? null;
      if (leaderPresence) {
        visited.push(leaderPresence.userId);
      }
    }
    return leaderPresence;
  }
  getViewportPageBoundsForFollowing() {
    const leaderPresence = this._getFollowingPresence(this.getInstanceState().followingUserId);
    if (!leaderPresence?.camera || !leaderPresence?.screenBounds) return null;
    const { w: lw, h: lh } = leaderPresence.screenBounds;
    const { x: lx, y: ly, z: lz } = leaderPresence.camera;
    const theirViewport = new Box(-lx, -ly, lw / lz, lh / lz);
    const ourViewport = this.getViewportScreenBounds().clone();
    const ourAspectRatio = ourViewport.width / ourViewport.height;
    ourViewport.width = theirViewport.width;
    ourViewport.height = ourViewport.width / ourAspectRatio;
    if (ourViewport.height < theirViewport.height) {
      ourViewport.height = theirViewport.height;
      ourViewport.width = ourViewport.height * ourAspectRatio;
    }
    ourViewport.center = theirViewport.center;
    return ourViewport;
  }
  getCameraForFollowing() {
    const viewport = this.getViewportPageBoundsForFollowing();
    if (!viewport) return null;
    return {
      x: -viewport.x,
      y: -viewport.y,
      z: this.getViewportScreenBounds().w / viewport.width
    };
  }
  getZoomLevel() {
    return this.getCamera().z;
  }
  getResizeScaleFactor() {
    return this.user.getIsDynamicResizeMode() ? 1 / this.getZoomLevel() : 1;
  }
  _debouncedZoomLevel = atom("debounced zoom level", 1);
  getDebouncedZoomLevel() {
    if (this.options.debouncedZoom) {
      if (this.getCameraState() === "idle") {
        return this.getZoomLevel();
      } else {
        return this._debouncedZoomLevel.get();
      }
    }
    return this.getZoomLevel();
  }
  _getAboveDebouncedZoomThreshold() {
    return this.getCurrentPageShapeIds().size > this.options.debouncedZoomThreshold;
  }
  getEfficientZoomLevel() {
    return this._getAboveDebouncedZoomThreshold() ? this.getDebouncedZoomLevel() : this.getZoomLevel();
  }
  /**
   * Get the camera's initial or reset zoom level.
   *
   * @example
   * ```ts
   * editor.getInitialZoom()
   * ```
   *
   * @public */
  getInitialZoom() {
    const cameraOptions = this.getCameraOptions();
    if (!cameraOptions.constraints) return 1;
    if (cameraOptions.constraints.initialZoom === "default") return 1;
    const { zx, zy } = getCameraFitXFitY(this, cameraOptions);
    switch (cameraOptions.constraints.initialZoom) {
      case "fit-min": {
        return Math.max(zx, zy);
      }
      case "fit-max": {
        return Math.min(zx, zy);
      }
      case "fit-x": {
        return zx;
      }
      case "fit-y": {
        return zy;
      }
      case "fit-min-100": {
        return Math.min(1, Math.max(zx, zy));
      }
      case "fit-max-100": {
        return Math.min(1, Math.min(zx, zy));
      }
      case "fit-x-100": {
        return Math.min(1, zx);
      }
      case "fit-y-100": {
        return Math.min(1, zy);
      }
      default: {
        throw exhaustiveSwitchError(cameraOptions.constraints.initialZoom);
      }
    }
  }
  /**
   * Get the camera's base level for calculating actual zoom levels based on the zoom steps.
   *
   * @example
   * ```ts
   * editor.getBaseZoom()
   * ```
   *
   * @public */
  getBaseZoom() {
    const cameraOptions = this.getCameraOptions();
    if (!cameraOptions.constraints) return 1;
    if (cameraOptions.constraints.baseZoom === "default") return 1;
    const { zx, zy } = getCameraFitXFitY(this, cameraOptions);
    switch (cameraOptions.constraints.baseZoom) {
      case "fit-min": {
        return Math.max(zx, zy);
      }
      case "fit-max": {
        return Math.min(zx, zy);
      }
      case "fit-x": {
        return zx;
      }
      case "fit-y": {
        return zy;
      }
      case "fit-min-100": {
        return Math.min(1, Math.max(zx, zy));
      }
      case "fit-max-100": {
        return Math.min(1, Math.min(zx, zy));
      }
      case "fit-x-100": {
        return Math.min(1, zx);
      }
      case "fit-y-100": {
        return Math.min(1, zy);
      }
      default: {
        throw exhaustiveSwitchError(cameraOptions.constraints.baseZoom);
      }
    }
  }
  _cameraOptions = atom("camera options", DEFAULT_CAMERA_OPTIONS);
  /**
   * Get the current camera options.
   *
   * @example
   * ```ts
   * editor.getCameraOptions()
   * ```
   *
   *  @public */
  getCameraOptions() {
    return this._cameraOptions.get();
  }
  /**
   * Set the camera options. Changing the options won't immediately change the camera itself, so you may want to call `setCamera` after changing the options.
   *
   * @example
   * ```ts
   * editor.setCameraOptions(myCameraOptions)
   * editor.setCamera(editor.getCamera())
   * ```
   *
   * @param opts - The camera options to set.
   *
   * @public */
  setCameraOptions(opts) {
    const next = structuredClone({
      ...this._cameraOptions.__unsafe__getWithoutCapture(),
      ...opts
    });
    if (next.zoomSteps?.length < 1) next.zoomSteps = [1];
    this._cameraOptions.set(next);
    this.setCamera(this.getCamera());
    return this;
  }
  /** @internal */
  getConstrainedCamera(point, opts) {
    const currentCamera = this.getCamera();
    let { x, y, z = currentCamera.z } = point;
    const preserveFocalPoint = (current, requested, rz, z2) => {
      const cz = currentCamera.z;
      if (rz === cz) return current;
      return current + (requested - current) * (1 / z2 - 1 / cz) / (1 / rz - 1 / cz);
    };
    if (!opts?.force) {
      const cameraOptions = this.getCameraOptions();
      const zoomMin = cameraOptions.zoomSteps[0];
      const zoomMax = last(cameraOptions.zoomSteps);
      const vsb = this.getViewportScreenBounds();
      if (cameraOptions.constraints) {
        const { constraints } = cameraOptions;
        const py = Math.min(constraints.padding.y, vsb.w / 2);
        const px = Math.min(constraints.padding.x, vsb.h / 2);
        const bounds = Box.From(cameraOptions.constraints.bounds);
        const zx = (vsb.w - px * 2) / bounds.w;
        const zy = (vsb.h - py * 2) / bounds.h;
        const baseZoom = this.getBaseZoom();
        const maxZ = zoomMax * baseZoom;
        const minZ = zoomMin * baseZoom;
        if (opts?.reset) {
          z = this.getInitialZoom();
        }
        if (z < minZ || z > maxZ) {
          const rz = z;
          z = clamp(z, minZ, maxZ);
          x = preserveFocalPoint(currentCamera.x, x, rz, z);
          y = preserveFocalPoint(currentCamera.y, y, rz, z);
        }
        const minX = px / z - bounds.x;
        const minY = py / z - bounds.y;
        const freeW = (vsb.w - px * 2) / z - bounds.w;
        const freeH = (vsb.h - py * 2) / z - bounds.h;
        const originX = minX + freeW * constraints.origin.x;
        const originY = minY + freeH * constraints.origin.y;
        const behaviorX = typeof constraints.behavior === "string" ? constraints.behavior : constraints.behavior.x;
        const behaviorY = typeof constraints.behavior === "string" ? constraints.behavior : constraints.behavior.y;
        if (opts?.reset) {
          x = originX;
          y = originY;
        } else {
          switch (behaviorX) {
            case "fixed": {
              x = originX;
              break;
            }
            case "contain": {
              if (z < zx) x = originX;
              else x = clamp(x, minX + freeW, minX);
              break;
            }
            case "inside": {
              if (z < zx) x = clamp(x, minX, (vsb.w - px) / z - bounds.w);
              else x = clamp(x, minX + freeW, minX);
              break;
            }
            case "outside": {
              x = clamp(x, px / z - bounds.w, (vsb.w - px) / z);
              break;
            }
            case "free": {
              break;
            }
            default: {
              throw exhaustiveSwitchError(behaviorX);
            }
          }
          switch (behaviorY) {
            case "fixed": {
              y = originY;
              break;
            }
            case "contain": {
              if (z < zy) y = originY;
              else y = clamp(y, minY + freeH, minY);
              break;
            }
            case "inside": {
              if (z < zy) y = clamp(y, minY, (vsb.h - py) / z - bounds.h);
              else y = clamp(y, minY + freeH, minY);
              break;
            }
            case "outside": {
              y = clamp(y, py / z - bounds.h, (vsb.h - py) / z);
              break;
            }
            case "free": {
              break;
            }
            default: {
              throw exhaustiveSwitchError(behaviorY);
            }
          }
        }
      } else {
        if (z > zoomMax || z < zoomMin) {
          const rz = z;
          z = clamp(z, zoomMin, zoomMax);
          x = preserveFocalPoint(currentCamera.x, x, rz, z);
          y = preserveFocalPoint(currentCamera.y, y, rz, z);
        }
      }
    }
    return { x, y, z };
  }
  /** @internal */
  _setCamera(point, opts) {
    const currentCamera = this.getCamera();
    const { x, y, z } = this.getConstrainedCamera(point, opts);
    if (currentCamera.x === x && currentCamera.y === y && currentCamera.z === z) {
      return this;
    }
    transact(() => {
      const camera = { ...currentCamera, x, y, z };
      this.run(
        () => {
          this.store.put([camera]);
        },
        { history: "ignore" }
      );
      const currentScreenPoint = this.inputs.getCurrentScreenPoint();
      const currentPagePoint = this.inputs.getCurrentPagePoint();
      if (currentScreenPoint.x / z - x !== currentPagePoint.x || currentScreenPoint.y / z - y !== currentPagePoint.y) {
        this.updatePointer({
          immediate: opts?.immediate,
          pointerId: INTERNAL_POINTER_IDS.CAMERA_MOVE
        });
      }
      this._tickCameraState();
    });
    return this;
  }
  /**
   * Set the current camera.
   *
   * @example
   * ```ts
   * editor.setCamera({ x: 0, y: 0})
   * editor.setCamera({ x: 0, y: 0, z: 1.5})
   * editor.setCamera({ x: 0, y: 0, z: 1.5}, { animation: { duration: 1000, easing: (t) => t * t } })
   * ```
   *
   * @param point - The new camera position.
   * @param opts - The camera move options.
   *
   * @public
   */
  setCamera(point, opts) {
    const { isLocked } = this._cameraOptions.__unsafe__getWithoutCapture();
    if (isLocked && !opts?.force) return this;
    this.stopCameraAnimation();
    if (this.getInstanceState().followingUserId) {
      this.stopFollowingUser();
    }
    const _point = Vec.Cast(point);
    if (!Number.isFinite(_point.x)) _point.x = 0;
    if (!Number.isFinite(_point.y)) _point.y = 0;
    if (_point.z === void 0 || !Number.isFinite(_point.z)) point.z = this.getZoomLevel();
    const camera = this.getConstrainedCamera(_point, opts);
    if (opts?.animation) {
      const { width, height } = this.getViewportScreenBounds();
      this._animateToViewport(
        new Box(-camera.x, -camera.y, width / camera.z, height / camera.z),
        opts
      );
    } else {
      this._setCamera(camera, {
        ...opts,
        // we already did the constraining, so we don't need to do it again
        force: true
      });
    }
    return this;
  }
  /**
   * Center the camera on a point (in the current page space).
   *
   * @example
   * ```ts
   * editor.centerOnPoint({ x: 100, y: 100 })
   * editor.centerOnPoint({ x: 100, y: 100 }, { animation: { duration: 200 } })
   * ```
   *
   * @param point - The point in the current page space to center on.
   * @param opts - The camera move options.
   *
   * @public
   */
  centerOnPoint(point, opts) {
    const { isLocked } = this.getCameraOptions();
    if (isLocked && !opts?.force) return this;
    const { width: pw, height: ph } = this.getViewportPageBounds();
    this.setCamera(new Vec(-(point.x - pw / 2), -(point.y - ph / 2), this.getCamera().z), opts);
    return this;
  }
  /**
   * Zoom the camera to fit the current page's content in the viewport.
   *
   * @example
   * ```ts
   * editor.zoomToFit()
   * editor.zoomToFit({ animation: { duration: 200 } })
   * ```
   *
   * @param opts - The camera move options.
   *
   * @public
   */
  zoomToFit(opts) {
    const ids = [...this.getCurrentPageShapeIds()].filter((id) => !this.isShapeHidden(id));
    if (ids.length <= 0) return this;
    const pageBounds = Box.Common(compact(ids.map((id) => this.getShapePageBounds(id))));
    this.zoomToBounds(pageBounds, opts);
    return this;
  }
  /**
   * Set the zoom back to 100%.
   *
   * @example
   * ```ts
   * editor.resetZoom()
   * editor.resetZoom(editor.getViewportScreenCenter(), { animation: { duration: 200 } })
   * editor.resetZoom(editor.getViewportScreenCenter(), { animation: { duration: 200 } })
   * ```
   *
   * @param point - The screen point to zoom out on. Defaults to the viewport screen center.
   * @param opts - The camera move options.
   *
   * @public
   */
  resetZoom(point = this.getViewportScreenCenter(), opts) {
    const { isLocked, constraints } = this.getCameraOptions();
    if (isLocked && !opts?.force) return this;
    const currentCamera = this.getCamera();
    const { x: cx, y: cy, z: cz } = currentCamera;
    const { x, y } = point;
    let z = 1;
    if (constraints) {
      const initialZoom = this.getInitialZoom();
      if (cz !== initialZoom) {
        z = initialZoom;
      }
    }
    this.setCamera(
      new Vec(cx + (x / z - x) - (x / cz - x), cy + (y / z - y) - (y / cz - y), z),
      opts
    );
    return this;
  }
  /**
   * Zoom the camera in.
   *
   * @example
   * ```ts
   * editor.zoomIn()
   * editor.zoomIn(editor.getViewportScreenCenter(), { animation: { duration: 200 } })
   * editor.zoomIn(editor.inputs.getCurrentScreenPoint(), { animation: { duration: 200 } })
   * ```
   *
   * @param point - The screen point to zoom in on. Defaults to the screen center
   * @param opts - The camera move options.
   *
   * @public
   */
  zoomIn(point = this.getViewportScreenCenter(), opts) {
    const { isLocked } = this.getCameraOptions();
    if (isLocked && !opts?.force) return this;
    const { x: cx, y: cy, z: cz } = this.getCamera();
    const { zoomSteps } = this.getCameraOptions();
    if (zoomSteps !== null && zoomSteps.length > 1) {
      const baseZoom = this.getBaseZoom();
      let zoom = last(zoomSteps) * baseZoom;
      for (let i = 1; i < zoomSteps.length; i++) {
        const z1 = zoomSteps[i - 1] * baseZoom;
        const z2 = zoomSteps[i] * baseZoom;
        if (z2 - cz <= (z2 - z1) / 2) continue;
        zoom = z2;
        break;
      }
      this.setCamera(
        new Vec(
          cx + (point.x / zoom - point.x) - (point.x / cz - point.x),
          cy + (point.y / zoom - point.y) - (point.y / cz - point.y),
          zoom
        ),
        opts
      );
    }
    return this;
  }
  /**
   * Zoom the camera out.
   *
   * @example
   * ```ts
   * editor.zoomOut()
   * editor.zoomOut(editor.getViewportScreenCenter(), { animation: { duration: 120 } })
   * editor.zoomOut(editor.inputs.getCurrentScreenPoint(), { animation: { duration: 120 } })
   * ```
   *
   * @param point - The point to zoom out on. Defaults to the viewport screen center.
   * @param opts - The camera move options.
   *
   * @public
   */
  zoomOut(point = this.getViewportScreenCenter(), opts) {
    const { isLocked } = this.getCameraOptions();
    if (isLocked && !opts?.force) return this;
    const { zoomSteps } = this.getCameraOptions();
    if (zoomSteps !== null && zoomSteps.length > 1) {
      const baseZoom = this.getBaseZoom();
      const { x: cx, y: cy, z: cz } = this.getCamera();
      let zoom = zoomSteps[0] * baseZoom;
      for (let i = zoomSteps.length - 1; i > 0; i--) {
        const z1 = zoomSteps[i - 1] * baseZoom;
        const z2 = zoomSteps[i] * baseZoom;
        if (z2 - cz >= (z2 - z1) / 2) continue;
        zoom = z1;
        break;
      }
      this.setCamera(
        new Vec(
          cx + (point.x / zoom - point.x) - (point.x / cz - point.x),
          cy + (point.y / zoom - point.y) - (point.y / cz - point.y),
          zoom
        ),
        opts
      );
    }
    return this;
  }
  /**
   * Zoom the camera to fit the current selection in the viewport.
   *
   * @example
   * ```ts
   * editor.zoomToSelection()
   * editor.zoomToSelection({ animation: { duration: 200 } })
   * ```
   *
   * @param opts - The camera move options.
   *
   * @public
   */
  zoomToSelection(opts) {
    const { isLocked } = this.getCameraOptions();
    if (isLocked && !opts?.force) return this;
    const selectionPageBounds = this.getSelectionPageBounds();
    if (selectionPageBounds) {
      const currentZoom = this.getZoomLevel();
      if (Math.abs(currentZoom - 1) < 0.01) {
        this.zoomToBounds(selectionPageBounds, opts);
      } else {
        this.zoomToBounds(selectionPageBounds, {
          targetZoom: 1,
          ...opts
        });
      }
    }
    return this;
  }
  /**
   * Zoom the camera to the current selection if offscreen.
   *
   * @public
   */
  zoomToSelectionIfOffscreen(padding = 16, opts) {
    const selectionPageBounds = this.getSelectionPageBounds();
    const viewportPageBounds = this.getViewportPageBounds();
    if (selectionPageBounds && !viewportPageBounds.contains(selectionPageBounds)) {
      const eb = selectionPageBounds.clone().expandBy(padding / this.getZoomLevel()).expand(viewportPageBounds);
      const nextBounds = viewportPageBounds.clone().translate({
        x: (eb.center.x - viewportPageBounds.center.x) * 2,
        y: (eb.center.y - viewportPageBounds.center.y) * 2
      });
      this.zoomToBounds(nextBounds, opts);
    }
  }
  /**
   * Zoom the camera to fit a bounding box (in the current page space).
   *
   * @example
   * ```ts
   * editor.zoomToBounds(myBounds)
   * editor.zoomToBounds(myBounds, { animation: { duration: 200 } })
   * editor.zoomToBounds(myBounds, { animation: { duration: 200 }, inset: 0, targetZoom: 1 })
   * ```
   *
   * @param bounds - The bounding box.
   * @param opts - The camera move options, target zoom, or custom inset amount.
   *
   * @public
   */
  zoomToBounds(bounds, opts) {
    const cameraOptions = this._cameraOptions.__unsafe__getWithoutCapture();
    if (cameraOptions.isLocked && !opts?.force) return this;
    const viewportScreenBounds = this.getViewportScreenBounds();
    const inset = opts?.inset ?? Math.min(this.options.zoomToFitPadding, viewportScreenBounds.width * 0.28);
    const baseZoom = this.getBaseZoom();
    const zoomMin = cameraOptions.zoomSteps[0];
    const zoomMax = last(cameraOptions.zoomSteps);
    let zoom = clamp(
      Math.min(
        (viewportScreenBounds.width - inset) / bounds.w,
        (viewportScreenBounds.height - inset) / bounds.h
      ),
      zoomMin * baseZoom,
      zoomMax * baseZoom
    );
    if (opts?.targetZoom !== void 0) {
      zoom = Math.min(opts.targetZoom, zoom);
    }
    this.setCamera(
      new Vec(
        -bounds.x + (viewportScreenBounds.width - bounds.w * zoom) / 2 / zoom,
        -bounds.y + (viewportScreenBounds.height - bounds.h * zoom) / 2 / zoom,
        zoom
      ),
      opts
    );
    return this;
  }
  /**
   * Stop the current camera animation, if any.
   *
   * @example
   * ```ts
   * editor.stopCameraAnimation()
   * ```
   *
   * @public
   */
  stopCameraAnimation() {
    this.emit("stop-camera-animation");
    return this;
  }
  /** @internal */
  _viewportAnimation = null;
  /** @internal */
  _animateViewport(ms) {
    if (!this._viewportAnimation) return;
    this._viewportAnimation.elapsed += ms;
    const { elapsed, easing, duration, start, end } = this._viewportAnimation;
    if (elapsed > duration) {
      this.off("tick", this._animateViewport);
      this._viewportAnimation = null;
      this._setCamera(new Vec(-end.x, -end.y, this.getViewportScreenBounds().width / end.width));
      return;
    }
    const remaining = duration - elapsed;
    const t = easing(1 - remaining / duration);
    const left = start.minX + (end.minX - start.minX) * t;
    const top = start.minY + (end.minY - start.minY) * t;
    const right = start.maxX + (end.maxX - start.maxX) * t;
    this._setCamera(new Vec(-left, -top, this.getViewportScreenBounds().width / (right - left)), {
      force: true
    });
  }
  /** @internal */
  _animateToViewport(targetViewportPage, opts = { animation: DEFAULT_ANIMATION_OPTIONS }) {
    const { animation, ...rest } = opts;
    if (!animation) return;
    const { duration = 0, easing = EASINGS.easeInOutCubic } = animation;
    const animationSpeed = this.user.getAnimationSpeed();
    const viewportPageBounds = this.getViewportPageBounds();
    this.stopCameraAnimation();
    if (this.getInstanceState().followingUserId) {
      this.stopFollowingUser();
    }
    if (duration === 0 || animationSpeed === 0) {
      return this._setCamera(
        new Vec(
          -targetViewportPage.x,
          -targetViewportPage.y,
          this.getViewportScreenBounds().width / targetViewportPage.width
        ),
        { ...rest }
      );
    }
    this._viewportAnimation = {
      elapsed: 0,
      duration: duration / animationSpeed,
      easing,
      start: viewportPageBounds.clone(),
      end: targetViewportPage.clone()
    };
    this.once("stop-camera-animation", () => {
      this.off("tick", this._animateViewport);
      this._viewportAnimation = null;
    });
    this.on("tick", this._animateViewport);
    return this;
  }
  /**
   * Slide the camera in a certain direction.
   *
   * @example
   * ```ts
   * editor.slideCamera({ speed: 1, direction: { x: 1, y: 0 }, friction: 0.1 })
   * ```
   *
   * @param opts - Options for the slide
   * @public
   */
  slideCamera(opts = {}) {
    const { isLocked } = this.getCameraOptions();
    if (isLocked && !opts?.force) return this;
    const animationSpeed = this.user.getAnimationSpeed();
    if (animationSpeed === 0) return this;
    this.stopCameraAnimation();
    const {
      speed,
      friction = this.options.cameraSlideFriction,
      direction,
      speedThreshold = 0.01
    } = opts;
    let currentSpeed = Math.min(speed, 1);
    const cancel = () => {
      this.off("tick", moveCamera);
      this.off("stop-camera-animation", cancel);
    };
    this.once("stop-camera-animation", cancel);
    const dirZ = direction.z ?? 0;
    const moveCamera = (elapsed) => {
      const { x: cx, y: cy, z: cz } = this.getCamera();
      const dx = direction.x * (currentSpeed * elapsed) / cz;
      const dy = direction.y * (currentSpeed * elapsed) / cz;
      let newCx = cx + dx;
      let newCy = cy + dy;
      let newCz = cz;
      if (dirZ !== 0) {
        newCz = cz * (1 + dirZ * currentSpeed * elapsed);
        const center = this.getViewportScreenCenter();
        newCx += center.x / newCz - center.x / cz;
        newCy += center.y / newCz - center.y / cz;
      }
      currentSpeed *= 1 - friction;
      if (currentSpeed < speedThreshold) {
        cancel();
      } else {
        this._setCamera(new Vec(newCx, newCy, newCz));
      }
    };
    this.on("tick", moveCamera);
    return this;
  }
  /**
   * Animate the camera to a user's cursor position. This also briefly show the user's cursor if it's not currently visible.
   *
   * @example
   * ```ts
   * editor.zoomToUser(myUserId)
   * editor.zoomToUser(myUserId, { animation: { duration: 200 } })
   * ```
   *
   * @param userId - The id of the user to animate to.
   * @param opts - The camera move options.
   * @public
   */
  zoomToUser(userId, opts = { animation: { duration: 500 } }) {
    const presence = this.getCollaborators().find((c) => c.userId === userId);
    if (!presence) return this;
    const cursor = presence.cursor;
    if (!cursor) return this;
    this.run(() => {
      if (this.getInstanceState().followingUserId !== null) {
        this.stopFollowingUser();
      }
      const isOnSamePage = presence.currentPageId === this.getCurrentPageId();
      if (!isOnSamePage) {
        this.setCurrentPage(presence.currentPageId);
      }
      if (opts && opts.animation && !isOnSamePage) {
        opts.animation = void 0;
      }
      this.centerOnPoint(cursor, opts);
      const { highlightedUserIds } = this.getInstanceState();
      this.updateInstanceState({ highlightedUserIds: [...highlightedUserIds, userId] });
      this.timers.setTimeout(() => {
        const highlightedUserIds2 = [...this.getInstanceState().highlightedUserIds];
        const index = highlightedUserIds2.indexOf(userId);
        if (index < 0) return;
        highlightedUserIds2.splice(index, 1);
        this.updateInstanceState({ highlightedUserIds: highlightedUserIds2 });
      }, this.options.collaboratorIdleTimeoutMs);
    });
    return this;
  }
  // Viewport
  /** @internal */
  _willSetInitialBounds = true;
  /**
   * Update the viewport. The viewport will measure the size and screen position of its container
   * element. This should be done whenever the container's position on the screen changes.
   *
   * @example
   * ```ts
   * editor.updateViewportScreenBounds(new Box(0, 0, 1280, 1024))
   * editor.updateViewportScreenBounds(new Box(0, 0, 1280, 1024), true)
   * ```
   *
   * @param screenBounds - The new screen bounds of the viewport.
   * @param center - Whether to preserve the viewport page center as the viewport changes.
   *
   * @public
   */
  updateViewportScreenBounds(screenBounds, center = false) {
    if (!(screenBounds instanceof Box)) {
      const rect = screenBounds.getBoundingClientRect();
      screenBounds = new Box(
        rect.left || rect.x,
        rect.top || rect.y,
        Math.max(rect.width, 1),
        Math.max(rect.height, 1)
      );
    } else {
      screenBounds.width = Math.max(screenBounds.width, 1);
      screenBounds.height = Math.max(screenBounds.height, 1);
    }
    const doc = this.getContainerDocument();
    if (!doc.body) return this;
    const insets = [
      // top
      screenBounds.minY !== 0,
      // right
      !approximately(doc.body.scrollWidth, screenBounds.maxX, 1),
      // bottom
      !approximately(doc.body.scrollHeight, screenBounds.maxY, 1),
      // left
      screenBounds.minX !== 0
    ];
    const { _willSetInitialBounds } = this;
    this._willSetInitialBounds = false;
    const { screenBounds: prevScreenBounds, insets: prevInsets } = this.getInstanceState();
    if (screenBounds.equals(prevScreenBounds) && insets.every((v, i) => v === prevInsets[i])) {
      return this;
    }
    if (_willSetInitialBounds) {
      this.updateInstanceState({ screenBounds: screenBounds.toJson(), insets });
      this.emit("resize", screenBounds.toJson());
      this.setCamera(this.getCamera());
    } else {
      if (center && !this.getInstanceState().followingUserId) {
        const before = this.getViewportPageBounds().center;
        this.updateInstanceState({ screenBounds: screenBounds.toJson(), insets });
        this.emit("resize", screenBounds.toJson());
        this.centerOnPoint(before);
      } else {
        this.updateInstanceState({ screenBounds: screenBounds.toJson(), insets });
        this.emit("resize", screenBounds.toJson());
        this._setCamera(Vec.From({ ...this.getCamera() }));
      }
    }
    return this;
  }
  getViewportScreenBounds() {
    const { x, y, w, h } = this.getInstanceState().screenBounds;
    return new Box(x, y, w, h);
  }
  getViewportScreenCenter() {
    const viewportScreenBounds = this.getViewportScreenBounds();
    return new Vec(viewportScreenBounds.w / 2, viewportScreenBounds.h / 2);
  }
  getViewportPageBounds() {
    const { w, h } = this.getViewportScreenBounds();
    const { x: cx, y: cy, z: cz } = this.getCamera();
    return new Box(-cx, -cy, w / cz, h / cz);
  }
  /**
   * Convert a point in screen space to a point in the current page space.
   *
   * @example
   * ```ts
   * editor.screenToPage({ x: 100, y: 100 })
   * ```
   *
   * @param point - The point in screen space.
   *
   * @public
   */
  screenToPage(point) {
    const { screenBounds } = this.store.unsafeGetWithoutCapture(TLINSTANCE_ID);
    const { x: cx, y: cy, z: cz = 1 } = this.getCamera();
    return new Vec(
      (point.x - screenBounds.x) / cz - cx,
      (point.y - screenBounds.y) / cz - cy,
      point.z ?? 0.5
    );
  }
  /**
   * Convert a point in the current page space to a point in current screen space.
   *
   * @example
   * ```ts
   * editor.pageToScreen({ x: 100, y: 100 })
   * ```
   *
   * @param point - The point in page space.
   *
   * @public
   */
  pageToScreen(point) {
    const { screenBounds } = this.store.unsafeGetWithoutCapture(TLINSTANCE_ID);
    const { x: cx, y: cy, z: cz = 1 } = this.getCamera();
    return new Vec(
      (point.x + cx) * cz + screenBounds.x,
      (point.y + cy) * cz + screenBounds.y,
      point.z ?? 0.5
    );
  }
  /**
   * Convert a point in the current page space to a point in current viewport space.
   *
   * @example
   * ```ts
   * editor.pageToViewport({ x: 100, y: 100 })
   * ```
   *
   * @param point - The point in page space.
   *
   * @public
   */
  pageToViewport(point) {
    const { x: cx, y: cy, z: cz = 1 } = this.getCamera();
    return new Vec((point.x + cx) * cz, (point.y + cy) * cz, point.z ?? 0.5);
  }
  // Collaborators
  /**
   * Returns a list of presence records for all peer collaborators.
   * This will return the latest presence record for each connected user.
   *
   * Convenience wrapper for {@link CollaboratorsManager.getCollaborators}.
   *
   * @public
   */
  getCollaborators() {
    return this.collaborators.getCollaborators();
  }
  /**
   * Returns a list of presence records for all peer collaborators on the current page.
   * This will return the latest presence record for each connected user.
   *
   * Convenience wrapper for {@link CollaboratorsManager.getCollaboratorsOnCurrentPage}.
   *
   * @public
   */
  getCollaboratorsOnCurrentPage() {
    return this.collaborators.getCollaboratorsOnCurrentPage();
  }
  /**
   * Returns a list of presence records for peer collaborators who should currently be
   * shown in the UI. Filters {@link Editor.getCollaborators} by activity state
   * (active / idle / inactive) and visibility rules such as following and highlighted
   * users. Re-evaluates on the collaborator visibility clock, so callers don't need to
   * drive their own activity timer.
   *
   * Convenience wrapper for {@link CollaboratorsManager.getVisibleCollaborators}.
   *
   * @public
   */
  getVisibleCollaborators() {
    return this.collaborators.getVisibleCollaborators();
  }
  /**
   * Returns a list of presence records for peer collaborators who should currently be
   * shown in the UI, filtered to those on the current page.
   *
   * Convenience wrapper for {@link CollaboratorsManager.getVisibleCollaboratorsOnCurrentPage}.
   *
   * @public
   */
  getVisibleCollaboratorsOnCurrentPage() {
    return this.collaborators.getVisibleCollaboratorsOnCurrentPage();
  }
  // Attribution
  /**
   * Get the current user's ID for attribution purposes.
   * Also ensures a `user:` record exists in the store for the current user.
   * Returns `null` when the user store has no current user.
   *
   * @public
   */
  getAttributionUserId() {
    const user = this.store.props.users.currentUser.get();
    if (!user) return null;
    this._ensureUserRecord(user);
    return UserRecordType.parseId(user.id);
  }
  /**
   * Ensure a user record exists in the store for the given user,
   * updating it if the data has changed.
   *
   * @internal
   */
  _ensureUserRecord(user) {
    const existing = this.store.get(user.id);
    if (existing && existing.name === user.name && existing.color === user.color && existing.imageUrl === user.imageUrl && existing.meta === user.meta) {
      return;
    }
    this.run(
      () => {
        this.store.put([user]);
      },
      { history: "ignore" }
    );
  }
  /**
   * Resolve a display name for a user ID. Asks the
   * {@link @tldraw/tlschema#TLUserStore} first (the app's source of truth),
   * falling back to the `user:` record in the store.
   *
   * @public
   */
  getAttributionDisplayName(userId) {
    if (!userId) return null;
    return this.store.props.users.resolve(userId).get()?.name ?? this.store.get(createUserId(userId))?.name ?? null;
  }
  /**
   * Resolve a user record by ID. Asks the
   * {@link @tldraw/tlschema#TLUserStore} first (the app's source of truth),
   * falling back to the `user:` record in the store.
   *
   * @public
   */
  getAttributionUser(userId) {
    if (!userId) return null;
    return this.store.props.users.resolve(userId).get() ?? this.store.get(createUserId(userId)) ?? null;
  }
  /**
   * Collect user IDs referenced by a set of shapes via shape-specific props
   * (e.g. `textLastEditedBy` on notes).
   *
   * @internal
   */
  _getReferencedUserIds(shapes) {
    const userIds = /* @__PURE__ */ new Set();
    for (const shape of shapes) {
      const util = this.getShapeUtil(shape);
      for (const id of util.getReferencedUserIds(shape)) {
        userIds.add(id);
      }
    }
    return userIds;
  }
  // Following
  // When we are 'locked on' to a user, our camera is derived from their camera.
  _isLockedOnFollowingUser = atom("isLockedOnFollowingUser", false);
  /**
   * Start viewport-following a user.
   *
   * @example
   * ```ts
   * editor.startFollowingUser(myUserId)
   * ```
   *
   * @param userId - The id of the user to follow.
   *
   * @public
   */
  startFollowingUser(userId) {
    this.stopFollowingUser();
    const thisUserId = this.user.getExternalId();
    if (!thisUserId) {
      console.warn("You should set the userId for the current instance before following a user");
    }
    const leaderPresence = this._getFollowingPresence(userId);
    if (!leaderPresence) {
      return this;
    }
    const latestLeaderPresence = computed("latestLeaderPresence", () => {
      return this._getFollowingPresence(userId);
    });
    transact(() => {
      this.updateInstanceState({ followingUserId: userId }, { history: "ignore" });
      const dispose = react("update current page", () => {
        const leaderPresence2 = latestLeaderPresence.get();
        if (!leaderPresence2) {
          this.stopFollowingUser();
          return;
        }
        if (leaderPresence2.currentPageId !== this.getCurrentPageId() && this.getPage(leaderPresence2.currentPageId)) {
          this.run(
            () => {
              this.store.put([
                { ...this.getInstanceState(), currentPageId: leaderPresence2.currentPageId }
              ]);
              this._isLockedOnFollowingUser.set(true);
            },
            { history: "ignore" }
          );
        }
      });
      const cancel = () => {
        dispose();
        this._isLockedOnFollowingUser.set(false);
        this.off("frame", moveTowardsUser);
        this.off("stop-following", cancel);
      };
      const moveTowardsUser = () => {
        const leaderPresence2 = latestLeaderPresence.get();
        if (!leaderPresence2) {
          this.stopFollowingUser();
          return;
        }
        if (this._isLockedOnFollowingUser.get()) return;
        const animationSpeed = this.user.getAnimationSpeed();
        if (animationSpeed === 0) {
          this._isLockedOnFollowingUser.set(true);
          return;
        }
        const targetViewport = this.getViewportPageBoundsForFollowing();
        if (!targetViewport) {
          this.stopFollowingUser();
          return;
        }
        const currentViewport = this.getViewportPageBounds();
        const diffX = Math.abs(targetViewport.minX - currentViewport.minX) + Math.abs(targetViewport.maxX - currentViewport.maxX);
        const diffY = Math.abs(targetViewport.minY - currentViewport.minY) + Math.abs(targetViewport.maxY - currentViewport.maxY);
        if (diffX < this.options.followChaseViewportSnap && diffY < this.options.followChaseViewportSnap) {
          this._isLockedOnFollowingUser.set(true);
          return;
        }
        const t = clamp(animationSpeed * 0.5, 0.1, 0.8);
        const nextViewport = new Box(
          lerp(currentViewport.minX, targetViewport.minX, t),
          lerp(currentViewport.minY, targetViewport.minY, t),
          lerp(currentViewport.width, targetViewport.width, t),
          lerp(currentViewport.height, targetViewport.height, t)
        );
        const nextCamera = new Vec(
          -nextViewport.x,
          -nextViewport.y,
          this.getViewportScreenBounds().width / nextViewport.width
        );
        this.stopCameraAnimation();
        this._setCamera(nextCamera);
      };
      this.once("stop-following", cancel);
      this.addListener("frame", moveTowardsUser);
      moveTowardsUser();
    });
    return this;
  }
  /**
   * Stop viewport-following a user.
   *
   * @example
   * ```ts
   * editor.stopFollowingUser()
   * ```
   * @public
   */
  stopFollowingUser() {
    this.run(
      () => {
        this.store.put([this.getCamera()]);
        this._isLockedOnFollowingUser.set(false);
        this.updateInstanceState({ followingUserId: null });
        this.emit("stop-following");
      },
      { history: "ignore" }
    );
    return this;
  }
  /** @internal */
  getUnorderedRenderingShapes(useEditorState) {
    const renderingShapes = [];
    let nextIndex = this.options.maxShapesPerPage * 2;
    let nextBackgroundIndex = this.options.maxShapesPerPage;
    const erasingShapeIds = this.getErasingShapeIds();
    const addShapeById = (id, opacity, isAncestorErasing) => {
      const shape = this.getShape(id);
      if (!shape) return;
      if (this.isShapeHidden(shape)) {
        const isErasing = isAncestorErasing || erasingShapeIds.includes(id);
        for (const childId of this.getSortedChildIdsForParent(id)) {
          addShapeById(childId, opacity, isErasing);
        }
        return;
      }
      opacity *= shape.opacity;
      let isShapeErasing = false;
      const util = this.getShapeUtil(shape);
      if (useEditorState) {
        isShapeErasing = !isAncestorErasing && erasingShapeIds.includes(id);
        if (isShapeErasing) {
          opacity *= 0.32;
        }
      }
      renderingShapes.push({
        id,
        shape,
        util,
        index: nextIndex,
        backgroundIndex: nextBackgroundIndex,
        opacity
      });
      nextIndex += 1;
      nextBackgroundIndex += 1;
      const childIds = this.getSortedChildIdsForParent(id);
      if (!childIds.length) return;
      let backgroundIndexToRestore = null;
      if (util.providesBackgroundForChildren(shape)) {
        backgroundIndexToRestore = nextBackgroundIndex;
        nextBackgroundIndex = nextIndex;
        nextIndex += this.options.maxShapesPerPage;
      }
      for (const childId of childIds) {
        addShapeById(childId, opacity, isAncestorErasing || isShapeErasing);
      }
      if (backgroundIndexToRestore !== null) {
        nextBackgroundIndex = backgroundIndexToRestore;
      }
    };
    const pages = useEditorState ? [this.getCurrentPage()] : this.getPages();
    for (const page of pages) {
      for (const childId of this.getSortedChildIdsForParent(page.id)) {
        addShapeById(childId, 1, false);
      }
    }
    return renderingShapes;
  }
  // Camera state
  // Camera state does two things: first, it allows us to subscribe to whether
  // the camera is moving or not; and second, it allows us to update the rendering
  // shapes on the canvas. Changing the rendering shapes may cause shapes to
  // unmount / remount in the DOM, which is expensive; and computing visibility is
  // also expensive in large projects. For this reason, we use a second bounding
  // box just for rendering, and we only update after the camera stops moving.
  _cameraStateTimeoutRemaining = 0;
  _decayCameraStateTimeout(elapsed) {
    this._cameraStateTimeoutRemaining -= elapsed;
    if (this._cameraStateTimeoutRemaining > 0) return;
    this.off("tick", this._decayCameraStateTimeout);
    this._setCameraState("idle");
  }
  _tickCameraState() {
    this._cameraStateTimeoutRemaining = this.options.cameraMovingTimeoutMs;
    if (this.getInstanceState().cameraState !== "idle") return;
    this._setCameraState("moving");
    this._debouncedZoomLevel.set(unsafe__withoutCapture(() => this.getCamera().z));
    this.on("tick", this._decayCameraStateTimeout);
  }
  _setCameraState(cameraState) {
    this.updateInstanceState({ cameraState }, { history: "ignore" });
  }
  /**
   * Whether the camera is moving or idle.
   *
   * @example
   * ```ts
   * editor.getCameraState()
   * ```
   *
   * @public
   */
  getCameraState() {
    return this.getInstanceState().cameraState;
  }
  getRenderingShapes() {
    const renderingShapes = this.getUnorderedRenderingShapes(true);
    if (renderingShapes.length <= RENDERING_SHAPES_SORT_CACHE_THRESHOLD) {
      this._renderingShapesSortCache = null;
      return renderingShapes.sort(sortById);
    }
    const cache = this._renderingShapesSortCache;
    if (cache !== null && cache.size === renderingShapes.length) {
      const sorted = new Array(renderingShapes.length);
      let allMatched = true;
      for (let i = 0; i < renderingShapes.length; i++) {
        const entry = renderingShapes[i];
        const pos = cache.get(entry.id);
        if (pos === void 0) {
          allMatched = false;
          break;
        }
        sorted[pos] = entry;
      }
      if (allMatched) return sorted;
    }
    renderingShapes.sort(sortById);
    const positionById = /* @__PURE__ */ new Map();
    for (let i = 0; i < renderingShapes.length; i++) {
      positionById.set(renderingShapes[i].id, i);
    }
    this._renderingShapesSortCache = positionById;
    return renderingShapes;
  }
  _renderingShapesSortCache = null;
  _getAllPagesQuery() {
    return this.store.query.records("page");
  }
  getPages() {
    return Array.from(this._getAllPagesQuery().get()).sort(sortByIndex);
  }
  /**
   * The current page.
   *
   * @example
   * ```ts
   * editor.getCurrentPage()
   * ```
   *
   * @public
   */
  getCurrentPage() {
    return this.getPage(this.getCurrentPageId());
  }
  getCurrentPageId() {
    return this.getInstanceState().currentPageId;
  }
  /**
   * Get a page.
   *
   * @example
   * ```ts
   * editor.getPage(myPage.id)
   * editor.getPage(myPage)
   * ```
   *
   * @param page - The page (or the page id) to get.
   *
   * @public
   */
  getPage(page) {
    return this.store.get(typeof page === "string" ? page : page.id);
  }
  /* @internal */
  _currentPageShapeIds;
  /**
   * An array of all of the shapes on the current page.
   *
   * @example
   * ```ts
   * editor.getCurrentPageIds()
   * ```
   *
   * @public
   */
  getCurrentPageShapeIds() {
    return this._currentPageShapeIds.get();
  }
  getCurrentPageShapeIdsSorted() {
    return Array.from(this.getCurrentPageShapeIds()).sort();
  }
  /**
   * Get the ids of shapes on a page.
   *
   * @example
   * ```ts
   * const idsOnPage1 = editor.getPageShapeIds('page1')
   * const idsOnPage2 = editor.getPageShapeIds(myPage2)
   * ```
   *
   * @param page - The page (or the page id) to get the shape ids for.
   *
   * @public
   **/
  getPageShapeIds(page) {
    const pageId = typeof page === "string" ? page : page.id;
    const result = this.store.query.exec("shape", { parentId: { eq: pageId } });
    return this.getShapeAndDescendantIds(result.map((s) => s.id));
  }
  /**
   * Set the current page.
   *
   * @example
   * ```ts
   * editor.setCurrentPage('page1')
   * editor.setCurrentPage(myPage1)
   * ```
   *
   * @param page - The page (or the page id) to set as the current page.
   *
   * @public
   */
  setCurrentPage(page) {
    const pageId = typeof page === "string" ? page : page.id;
    if (!this.store.has(pageId)) {
      console.error("Tried to set the current page id to a page that doesn't exist.");
      return this;
    }
    this.stopFollowingUser();
    this.complete();
    return this.run(
      () => {
        this.store.put([{ ...this.getInstanceState(), currentPageId: pageId }]);
        this.setCamera(this.getCamera());
      },
      { history: "record-preserveRedoStack" }
    );
  }
  /**
   * Update a page.
   *
   * @example
   * ```ts
   * editor.updatePage({ id: 'page2', name: 'Page 2' })
   * ```
   *
   * @param partial - The partial of the shape to update.
   *
   * @public
   */
  updatePage(partial) {
    if (this.getIsReadonly()) return this;
    const prev = this.getPage(partial.id);
    if (!prev) return this;
    return this.run(() => this.store.update(partial.id, (page) => ({ ...page, ...partial })));
  }
  /**
   * Create a page whilst ensuring that the page name is unique.
   *
   * @example
   * ```ts
   * editor.createPage(myPage)
   * editor.createPage({ name: 'Page 2' })
   * ```
   *
   * @param page - The page (or page partial) to create.
   *
   * @public
   */
  createPage(page) {
    this.run(() => {
      if (this.getIsReadonly()) return;
      if (this.getPages().length >= this.options.maxPages) return;
      const pages = this.getPages();
      const name = getIncrementedName(
        page.name ?? "Page 1",
        pages.map((p) => p.name)
      );
      let index = page.index;
      if (!index || pages.some((p) => p.index === index)) {
        index = getIndexAbove(pages[pages.length - 1].index);
      }
      const newPage = PageRecordType.create({
        meta: {},
        ...page,
        name,
        index
      });
      this.store.put([newPage]);
    });
    return this;
  }
  /**
   * Delete a page.
   *
   * @example
   * ```ts
   * editor.deletePage('page1')
   * ```
   *
   * @param page - The page (or the page id) to delete.
   *
   * @public
   */
  deletePage(page) {
    const id = typeof page === "string" ? page : page.id;
    this.run(
      () => {
        if (this.getIsReadonly()) return;
        const pages = this.getPages();
        if (pages.length === 1) return;
        const deletedPage = this.getPage(id);
        if (!deletedPage) return;
        if (id === this.getCurrentPageId()) {
          const index = pages.findIndex((page2) => page2.id === id);
          const next = pages[index - 1] ?? pages[index + 1];
          this.setCurrentPage(next.id);
        }
        const shapes = this.getSortedChildIdsForParent(deletedPage.id);
        this.deleteShapes(shapes);
        this.store.remove([deletedPage.id]);
      },
      { ignoreShapeLock: true }
    );
    return this;
  }
  /**
   * Duplicate a page.
   *
   * @param page - The page (or the page id) to duplicate. Defaults to the current page.
   * @param createId - The id of the new page. Defaults to a new id.
   *
   * @public
   */
  duplicatePage(page, createId = PageRecordType.createId()) {
    if (this.getPages().length >= this.options.maxPages) return this;
    const id = typeof page === "string" ? page : page.id;
    const freshPage = this.getPage(id);
    if (!freshPage) return this;
    const prevCamera = { ...this.getCamera() };
    const content = this.getContentFromCurrentPage(this.getSortedChildIdsForParent(freshPage.id));
    this.run(() => {
      const pages = this.getPages();
      const index = getIndexBetween(freshPage.index, pages[pages.indexOf(freshPage) + 1]?.index);
      this.createPage({ name: freshPage.name + " Copy", id: createId, index });
      this.setCurrentPage(createId);
      this.setCamera(prevCamera);
      if (content) {
        return this.putContentOntoCurrentPage(content);
      }
    });
    return this;
  }
  /**
   * Rename a page.
   *
   * @example
   * ```ts
   * editor.renamePage('page1', 'My Page')
   * ```
   *
   * @param page - The page (or the page id) to rename.
   * @param name - The new name.
   *
   * @public
   */
  renamePage(page, name) {
    const id = typeof page === "string" ? page : page.id;
    if (this.getIsReadonly()) return this;
    this.updatePage({ id, name });
    return this;
  }
  _getAllAssetsQuery() {
    return this.store.query.records("asset");
  }
  /**
   * Get all assets in the editor.
   *
   * @public
   */
  getAssets() {
    return this._getAllAssetsQuery().get();
  }
  /**
   * Create one or more assets.
   *
   * @example
   * ```ts
   * editor.createAssets([...myAssets])
   * ```
   *
   * @param assets - The assets to create.
   *
   * @public
   */
  createAssets(assets) {
    if (this.getIsReadonly()) return this;
    if (assets.length <= 0) return this;
    this.run(() => this.store.put(assets), { history: "ignore" });
    return this;
  }
  /**
   * Update one or more assets.
   *
   * @example
   * ```ts
   * editor.updateAssets([{ id: 'asset1', name: 'New name' }])
   * ```
   *
   * @param assets - The assets to update.
   *
   * @public
   */
  updateAssets(assets) {
    if (this.getIsReadonly()) return this;
    if (assets.length <= 0) return this;
    this.run(
      () => {
        this.store.put(
          assets.map((partial) => ({
            ...this.store.get(partial.id),
            ...partial
          }))
        );
      },
      { history: "ignore" }
    );
    return this;
  }
  /**
   * Delete one or more assets.
   *
   * @example
   * ```ts
   * editor.deleteAssets(['asset1', 'asset2'])
   * ```
   *
   * @param assets - The assets (or asset ids) to delete.
   *
   * @public
   */
  deleteAssets(assets) {
    if (this.getIsReadonly()) return this;
    const ids = typeof assets[0] === "string" ? assets : assets.map((a) => a.id);
    if (ids.length <= 0) return this;
    this.run(
      () => {
        this.store.props.assets.remove?.(ids);
        this.store.remove(ids);
      },
      { history: "ignore" }
    );
    return this;
  }
  /**
   * Get an asset by its id.
   *
   * @example
   * ```ts
   * editor.getAsset('asset1')
   * ```
   *
   * @param asset - The asset (or asset id) to get.
   *
   * @public
   */
  getAsset(asset) {
    return this.store.get(typeof asset === "string" ? asset : asset.id);
  }
  async resolveAssetUrl(assetId, context) {
    if (!assetId) return null;
    const asset = this.getAsset(assetId);
    if (!asset) return null;
    const {
      screenScale = 1,
      shouldResolveToOriginal = false,
      dpr = this.getInstanceState().devicePixelRatio
    } = context;
    const zoomStepFunction = (zoom) => Math.pow(2, Math.ceil(Math.log2(zoom)));
    const steppedScreenScale = zoomStepFunction(screenScale);
    const networkEffectiveType = "connection" in navigator ? navigator.connection?.effectiveType ?? null : null;
    return await this.store.props.assets.resolve(asset, {
      screenScale: screenScale || 1,
      steppedScreenScale,
      dpr,
      networkEffectiveType,
      shouldResolveToOriginal
    });
  }
  /**
   * Upload an asset to the store's asset service, returning a URL that can be used to resolve the
   * asset.
   */
  async uploadAsset(asset, file, abortSignal) {
    return await this.store.props.assets.upload(asset, file, abortSignal);
  }
  /* --------------------- Shapes --------------------- */
  _shapeGeometryCaches = {};
  /**
   * Get the geometry of a shape in shape-space.
   *
   * @example
   * ```ts
   * editor.getShapeGeometry(myShape)
   * editor.getShapeGeometry(myShapeId)
   * editor.getShapeGeometry(myShapeId, { context: "arrow" })
   * ```
   *
   * @param shape - The shape (or shape id) to get the geometry for.
   * @param opts - Additional options about the request for geometry. Passed to {@link ShapeUtil.getGeometry}.
   *
   * @public
   */
  getShapeGeometry(shape, opts) {
    const context = opts?.context ?? "none";
    if (!this._shapeGeometryCaches[context]) {
      this._shapeGeometryCaches[context] = this.store.createComputedCache(
        "bounds",
        (shape2) => {
          this.fonts.trackFontsForShape(shape2);
          return this.getShapeUtil(shape2).getGeometry(shape2, opts);
        },
        { areRecordsEqual: areShapesContentEqual }
      );
    }
    return this._shapeGeometryCaches[context].get(
      typeof shape === "string" ? shape : shape.id
    );
  }
  _getShapeHandlesCache() {
    return this.store.createComputedCache(
      "handles",
      (shape) => {
        return this.getShapeUtil(shape).getHandles?.(shape);
      },
      {
        areRecordsEqual: areShapesContentEqual
      }
    );
  }
  /**
   * Get the handles (if any) for a shape.
   *
   * @example
   * ```ts
   * editor.getShapeHandles(myShape)
   * editor.getShapeHandles(myShapeId)
   * ```
   *
   * @param shape - The shape (or shape id) to get the handles for.
   * @public
   */
  getShapeHandles(shape) {
    return this._getShapeHandlesCache().get(typeof shape === "string" ? shape : shape.id);
  }
  /**
   * Get the local transform for a shape as a matrix model. This transform reflects both its
   * translation (x, y) from from either its parent's top left corner, if the shape's parent is
   * another shape, or else from the 0,0 of the page, if the shape's parent is the page; and the
   * shape's rotation.
   *
   * @example
   * ```ts
   * editor.getShapeLocalTransform(myShape)
   * ```
   *
   * @param shape - The shape to get the local transform for.
   *
   * @public
   */
  getShapeLocalTransform(shape) {
    const id = typeof shape === "string" ? shape : shape.id;
    const freshShape = this.getShape(id);
    if (!freshShape) throw Error("Editor.getTransform: shape not found");
    return Mat.Identity().translate(freshShape.x, freshShape.y).rotate(freshShape.rotation);
  }
  _getShapePageTransformCache() {
    return this.store.createComputedCache("pageTransformCache", (shape) => {
      if (isPageId(shape.parentId)) {
        return this.getShapeLocalTransform(shape);
      }
      const parentTransform = this._getShapePageTransformCache().get(shape.parentId) ?? Mat.Identity();
      return Mat.Compose(parentTransform, this.getShapeLocalTransform(shape));
    });
  }
  /**
   * Get the local transform of a shape's parent as a matrix model.
   *
   * @example
   * ```ts
   * editor.getShapeParentTransform(myShape)
   * ```
   *
   * @param shape - The shape (or shape id) to get the parent transform for.
   *
   * @public
   */
  getShapeParentTransform(shape) {
    const id = typeof shape === "string" ? shape : shape.id;
    const freshShape = this.getShape(id);
    if (!freshShape || isPageId(freshShape.parentId)) return Mat.Identity();
    return this._getShapePageTransformCache().get(freshShape.parentId) ?? Mat.Identity();
  }
  /**
   * Get the transform of a shape in the current page space.
   *
   * @example
   * ```ts
   * editor.getShapePageTransform(myShape)
   * editor.getShapePageTransform(myShapeId)
   * ```
   *
   * @param shape - The shape (or shape id) to get the page transform for.
   *
   * @public
   */
  getShapePageTransform(shape) {
    const id = typeof shape === "string" ? shape : shape.id;
    return this._getShapePageTransformCache().get(id) ?? Mat.Identity();
  }
  _getShapePageBoundsCache() {
    return this.store.createComputedCache("pageBoundsCache", (shape) => {
      const pageTransform = this.getShapePageTransform(shape);
      if (!pageTransform) return void 0;
      return Box.FromPoints(
        pageTransform.applyToPoints(this.getShapeGeometry(shape).boundsVertices)
      );
    });
  }
  /**
   * Get the bounds of a shape in the current page space.
   *
   * @example
   * ```ts
   * editor.getShapePageBounds(myShape)
   * editor.getShapePageBounds(myShapeId)
   * ```
   *
   * @param shape - The shape (or shape id) to get the bounds for.
   *
   * @public
   */
  getShapePageBounds(shape) {
    return this._getShapePageBoundsCache().get(typeof shape === "string" ? shape : shape.id);
  }
  _getShapeClipPathCache() {
    return this.store.createComputedCache("clipPathCache", (shape) => {
      const pageMask = this._getShapeMaskCache().get(shape.id);
      if (!pageMask) return void 0;
      if (pageMask.length === 0) {
        return `polygon(0px 0px, 0px 0px, 0px 0px)`;
      }
      const pageTransform = this._getShapePageTransformCache().get(shape.id);
      if (!pageTransform) return void 0;
      const localMask = Mat.applyToPoints(Mat.Inverse(pageTransform), pageMask);
      return `polygon(${localMask.map((p) => `${p.x}px ${p.y}px`).join(",")})`;
    });
  }
  /**
   * Get the clip path for a shape.
   *
   * @example
   * ```ts
   * const clipPath = editor.getShapeClipPath(shape)
   * const clipPath = editor.getShapeClipPath(shape.id)
   * ```
   *
   * @param shape - The shape (or shape id) to get the clip path for.
   *
   * @returns The clip path or undefined.
   *
   * @public
   */
  getShapeClipPath(shape) {
    return this._getShapeClipPathCache().get(typeof shape === "string" ? shape : shape.id);
  }
  _getShapeMaskCache() {
    return this.store.createComputedCache("pageMaskCache", (shape) => {
      if (isPageId(shape.parentId)) return void 0;
      const clipPaths = [];
      for (const ancestor of this.getShapeAncestors(shape.id)) {
        const util = this.getShapeUtil(ancestor);
        const clipPath = util.getClipPath?.(ancestor);
        if (!clipPath) continue;
        if (util.shouldClipChild?.(shape) === false) continue;
        const pageTransform = this.getShapePageTransform(ancestor.id);
        clipPaths.push(pageTransform.applyToPoints(clipPath));
      }
      if (clipPaths.length === 0) return void 0;
      const pageMask = clipPaths.reduce((acc, b) => {
        const intersection = intersectPolygonPolygon(acc, b);
        if (intersection) {
          return intersection.map(Vec.Cast);
        }
        return [];
      });
      return pageMask;
    });
  }
  /**
   * Get the mask (in the current page space) for a shape.
   *
   * @example
   * ```ts
   * const pageMask = editor.getShapeMask(shape.id)
   * ```
   *
   * @param shape - The shape (or the shape id) of the shape to get the mask for.
   *
   * @returns The mask for the shape.
   *
   * @public
   */
  getShapeMask(shape) {
    return this._getShapeMaskCache().get(typeof shape === "string" ? shape : shape.id);
  }
  /**
   * Get the bounds of a shape in the current page space, incorporating any masks. For example, if the
   * shape were the child of a frame and was half way out of the frame, the bounds would be the half
   * of the shape that was in the frame.
   *
   * @example
   * ```ts
   * editor.getShapeMaskedPageBounds(myShape)
   * editor.getShapeMaskedPageBounds(myShapeId)
   * ```
   *
   * @param shape - The shape to get the masked bounds for.
   *
   * @public
   */
  getShapeMaskedPageBounds(shape) {
    if (typeof shape !== "string") shape = shape.id;
    return this._getShapeMaskedPageBoundsCache().get(shape);
  }
  _getShapeMaskedPageBoundsCache() {
    return this.store.createComputedCache("shapeMaskedPageBoundsCache", (shape) => {
      const pageBounds = this._getShapePageBoundsCache().get(shape.id);
      if (!pageBounds) return;
      const pageMask = this._getShapeMaskCache().get(shape.id);
      if (pageMask) {
        if (pageMask.length === 0) return void 0;
        const { corners } = pageBounds;
        if (corners.every((p, i) => p && Vec.Equals(p, pageMask[i]))) return pageBounds.clone();
        const intersection = intersectPolygonPolygon(pageMask, corners);
        if (!intersection) return;
        return Box.FromPoints(intersection);
      }
      return pageBounds;
    });
  }
  /**
   * Get the ancestors of a shape.
   *
   * @example
   * ```ts
   * const ancestors = editor.getShapeAncestors(myShape)
   * const ancestors = editor.getShapeAncestors(myShapeId)
   * ```
   *
   * @param shape - The shape (or shape id) to get the ancestors for.
   * @param acc - The accumulator.
   *
   * @public
   */
  getShapeAncestors(shape, acc = []) {
    const id = typeof shape === "string" ? shape : shape.id;
    const freshShape = this.getShape(id);
    if (!freshShape) return acc;
    const parentId = freshShape.parentId;
    if (isPageId(parentId)) {
      acc.reverse();
      return acc;
    }
    const parent = this.store.get(parentId);
    if (!parent) return acc;
    acc.push(parent);
    return this.getShapeAncestors(parent, acc);
  }
  /**
   * Find the first ancestor matching the given predicate
   *
   * @example
   * ```ts
   * const ancestor = editor.findShapeAncestor(myShape)
   * const ancestor = editor.findShapeAncestor(myShape.id)
   * const ancestor = editor.findShapeAncestor(myShape.id, (shape) => shape.type === 'frame')
   * ```
   *
   * @param shape - The shape to check the ancestors for.
   * @param predicate - The predicate to match.
   *
   * @public
   */
  findShapeAncestor(shape, predicate) {
    const id = typeof shape === "string" ? shape : shape.id;
    const freshShape = this.getShape(id);
    if (!freshShape) return;
    const parentId = freshShape.parentId;
    if (isPageId(parentId)) return;
    const parent = this.getShape(parentId);
    if (!parent) return;
    return predicate(parent) ? parent : this.findShapeAncestor(parent, predicate);
  }
  /**
   * Returns true if the the given shape has the given ancestor.
   *
   * @param shape - The shape.
   * @param ancestorId - The id of the ancestor.
   *
   * @public
   */
  hasAncestor(shape, ancestorId) {
    const id = typeof shape === "string" ? shape : shape?.id;
    const freshShape = id && this.getShape(id);
    if (!freshShape) return false;
    if (freshShape.parentId === ancestorId) return true;
    return this.hasAncestor(this.getShapeParent(freshShape), ancestorId);
  }
  /**
   * Get the common ancestor of two or more shapes that matches a predicate.
   *
   * @param shapes - The shapes (or shape ids) to check.
   * @param predicate - The predicate to match.
   */
  findCommonAncestor(shapes, predicate) {
    if (shapes.length === 0) {
      return;
    }
    const ids = typeof shapes[0] === "string" ? shapes : shapes.map((s) => s.id);
    const freshShapes = compact(ids.map((id) => this.getShape(id)));
    if (freshShapes.length === 1) {
      const parentId = freshShapes[0].parentId;
      if (isPageId(parentId)) {
        return;
      }
      return predicate ? this.findShapeAncestor(freshShapes[0], predicate)?.id : parentId;
    }
    const [nodeA, ...others] = freshShapes;
    let ancestor = this.getShapeParent(nodeA);
    while (ancestor) {
      if (predicate && !predicate(ancestor)) {
        ancestor = this.getShapeParent(ancestor);
        continue;
      }
      if (others.every((shape) => this.hasAncestor(shape, ancestor.id))) {
        return ancestor.id;
      }
      ancestor = this.getShapeParent(ancestor);
    }
    return void 0;
  }
  /**
   * Check whether a shape or its parent is locked.
   *
   * @param shape - The shape (or shape id) to check.
   *
   * @public
   */
  isShapeOrAncestorLocked(shape) {
    const _shape = shape && this.getShape(shape);
    if (_shape === void 0) return false;
    if (_shape.isLocked) return true;
    return this.isShapeOrAncestorLocked(this.getShapeParent(_shape));
  }
  getNotVisibleShapes() {
    return this._notVisibleShapes.get();
  }
  _notVisibleShapes = notVisibleShapes(this);
  _culledShapesCache = null;
  getCulledShapes() {
    const notVisibleShapes2 = this.getNotVisibleShapes();
    const selectedShapeIds = this.getSelectedShapeIds();
    const editingId = this.getEditingShapeId();
    const nextValue = new Set(notVisibleShapes2);
    if (editingId) {
      nextValue.delete(editingId);
    }
    selectedShapeIds.forEach((id) => {
      nextValue.delete(id);
    });
    const prevValue = this._culledShapesCache;
    if (prevValue) {
      if (prevValue.size !== nextValue.size) {
        this._culledShapesCache = nextValue;
        return nextValue;
      }
      for (const id of prevValue) {
        if (!nextValue.has(id)) {
          this._culledShapesCache = nextValue;
          return nextValue;
        }
      }
      return prevValue;
    }
    this._culledShapesCache = nextValue;
    return nextValue;
  }
  getCurrentPageBounds() {
    let commonBounds;
    this.getCurrentPageShapeIdsSorted().forEach((shapeId) => {
      if (this.isShapeHidden(shapeId)) return;
      const bounds = this.getShapeMaskedPageBounds(shapeId);
      if (!bounds) return;
      if (!commonBounds) {
        commonBounds = bounds.clone();
      } else {
        commonBounds = commonBounds.expand(bounds);
      }
    });
    return commonBounds;
  }
  getHitTestMargin() {
    const { hitTestMargin, coarseHitTestMargin } = this.options;
    const margin = this.getInstanceState().isCoarsePointer ? coarseHitTestMargin : hitTestMargin;
    return margin / this.getZoomLevel();
  }
  /**
   * Get the top-most selected shape at the given point, ignoring groups.
   *
   * @param point - The point to check.
   *
   * @returns The top-most selected shape at the given point, or undefined if there is no shape at the point.
   */
  getSelectedShapeAtPoint(point) {
    const selectedShapeIds = this.getSelectedShapeIds();
    const margin = this.options.hitTestMargin / this.getZoomLevel();
    const sortedShapes = this.getCurrentPageShapesSorted();
    for (let i = sortedShapes.length - 1; i >= 0; i--) {
      const shape = sortedShapes[i];
      if (shape.type === "group") continue;
      if (!selectedShapeIds.includes(shape.id)) continue;
      if (this.getShapeGeometry(shape).hitTestPoint(
        this.getPointInShapeSpace(shape, point),
        margin,
        true
      )) {
        return shape;
      }
    }
    return void 0;
  }
  /**
   * Get the shape at the current point.
   *
   * @param point - The point to check.
   * @param opts - Options for the check: `hitInside` to check if the point is inside the shape, `margin` to check if the point is within a margin of the shape, `hitFrameInside` to check if the point is inside the frame, and `filter` to filter the shapes to check.
   *
   * @returns The shape at the given point, or undefined if there is no shape at the point.
   */
  getShapeAtPoint(point, opts = {}) {
    const viewportPageBounds = this.getViewportPageBounds();
    const {
      filter,
      margin = 0,
      hitLocked = false,
      hitLabels = false,
      hitInside = false,
      hitFrameInside = false
    } = opts;
    const [innerMargin, outerMargin] = Array.isArray(margin) ? margin : [margin, margin];
    let inHollowSmallestArea = Infinity;
    let inHollowSmallestAreaHit = null;
    let inMarginClosestToEdgeDistance = Infinity;
    let inMarginClosestToEdgeHit = null;
    const searchMargin = Math.max(innerMargin, outerMargin, this.getHitTestMargin());
    const candidateIds = this._spatialIndex.getShapeIdsAtPoint(point, searchMargin);
    const shapesToCheck = (opts.renderingOnly ? this.getCurrentPageRenderingShapesSorted() : this.getCurrentPageShapesSorted()).filter((shape) => {
      if (!candidateIds.has(shape.id) && !this.isShapeFrameLike(shape)) return false;
      if (shape.isLocked && !hitLocked || this.isShapeHidden(shape) || this.isShapeOfType(shape, "group"))
        return false;
      const pageMask = this.getShapeMask(shape);
      if (pageMask && !pointInPolygon(point, pageMask)) return false;
      if (filter && !filter(shape)) return false;
      return true;
    });
    for (let i = shapesToCheck.length - 1; i >= 0; i--) {
      const shape = shapesToCheck[i];
      const geometry = this.getShapeGeometry(shape);
      const isGroup = geometry instanceof Group2d;
      const pointInShapeSpace = this.getPointInShapeSpace(shape, point);
      const shapeUtil = this.getShapeUtil(shape);
      const isShapeFrameLike = this.isShapeFrameLike(shape);
      if (isShapeFrameLike || (this.isShapeOfType(shape, "note") || this.isShapeOfType(shape, "arrow") || this.isShapeOfType(shape, "geo") && shape.props.fill === "none") && shapeUtil.getText(shape)?.trim()) {
        for (const childGeometry of geometry.children) {
          if (childGeometry.isLabel && childGeometry.isPointInBounds(pointInShapeSpace)) {
            return shape;
          }
        }
      }
      if (isShapeFrameLike) {
        const distance2 = geometry.distanceToPoint(pointInShapeSpace, hitFrameInside);
        if (hitFrameInside ? distance2 > 0 && distance2 <= outerMargin || distance2 <= 0 && distance2 > -innerMargin : distance2 > 0 && distance2 <= outerMargin) {
          return inMarginClosestToEdgeHit || shape;
        }
        if (geometry.hitTestPoint(pointInShapeSpace, 0, true)) {
          return inMarginClosestToEdgeHit || inHollowSmallestAreaHit || (hitFrameInside ? shape : void 0);
        }
        continue;
      }
      let distance;
      if (isGroup) {
        let minDistance = Infinity;
        for (const childGeometry of geometry.children) {
          if (childGeometry.isLabel && !hitLabels) continue;
          const tDistance = childGeometry.distanceToPoint(pointInShapeSpace, hitInside);
          if (tDistance < minDistance) {
            minDistance = tDistance;
          }
        }
        distance = minDistance;
      } else {
        if (outerMargin === 0 && (geometry.bounds.w < 1 || geometry.bounds.h < 1)) {
          distance = geometry.distanceToPoint(pointInShapeSpace, hitInside);
        } else {
          if (geometry.bounds.containsPoint(pointInShapeSpace, outerMargin)) {
            distance = geometry.distanceToPoint(pointInShapeSpace, hitInside);
          } else {
            distance = Infinity;
          }
        }
      }
      if (geometry.isClosed) {
        if (distance <= outerMargin || hitInside && distance <= 0 && distance > -innerMargin) {
          if (geometry.isFilled || isGroup && geometry.children[0].isFilled) {
            if (geometry.ignoreHit(pointInShapeSpace)) {
              continue;
            }
            return inMarginClosestToEdgeHit || shape;
          } else {
            if (this.getShapePageBounds(shape).contains(viewportPageBounds)) continue;
            if (hitInside ? (
              // On hitInside, the distance will be negative for hits inside
              // If the distance is positive, check against the outer margin
              (// If the distance is negative, check against the inner margin
              distance > 0 && distance <= outerMargin || distance <= 0 && distance > -innerMargin)
            ) : (
              // If hitInside is false, then sadly _we do not know_ whether the
              // point is inside or outside of the shape, so we check against
              // the max of the two margins
              (Math.abs(distance) <= Math.max(innerMargin, outerMargin))
            )) {
              if (Math.abs(distance) < inMarginClosestToEdgeDistance) {
                inMarginClosestToEdgeDistance = Math.abs(distance);
                inMarginClosestToEdgeHit = shape;
              }
            } else if (!inMarginClosestToEdgeHit) {
              const { area } = geometry;
              if (area < inHollowSmallestArea) {
                inHollowSmallestArea = area;
                inHollowSmallestAreaHit = shape;
              }
            }
          }
        }
      } else {
        if (distance < this.getHitTestMargin()) {
          return shape;
        }
      }
    }
    return inMarginClosestToEdgeHit || inHollowSmallestAreaHit || void 0;
  }
  /**
   * Get the shapes, if any, at a given page point.
   *
   * @example
   * ```ts
   * editor.getShapesAtPoint({ x: 100, y: 100 })
   * editor.getShapesAtPoint({ x: 100, y: 100 }, { hitInside: true, margin: 8 })
   * ```
   *
   * @param point - The page point to test.
   * @param opts - The options for the hit point testing.
   *
   * @returns An array of shapes at the given point, sorted in reverse order of their absolute z-index (top-most shape first).
   *
   * @public
   */
  getShapesAtPoint(point, opts = {}) {
    const margin = opts.margin ?? 0;
    const candidateIds = this._spatialIndex.getShapeIdsAtPoint(point, margin);
    return this.getCurrentPageShapesSorted().filter((shape) => {
      if (this.isShapeHidden(shape)) return false;
      if (!candidateIds.has(shape.id) && !this.isShapeFrameLike(shape)) return false;
      return this.isPointInShape(shape, point, opts);
    }).reverse();
  }
  /**
   * Get shape IDs within the given bounds.
   *
   * Note: Uses shape page bounds only. Frames with labels outside their bounds
   * may not be included even if the label is within the search bounds.
   *
   * Note: Results are unordered. If you need z-order, combine with sorted shapes:
   * ```ts
   * const candidates = editor.getShapeIdsInsideBounds(bounds)
   * const sorted = editor.getCurrentPageShapesSorted().filter(s => candidates.has(s.id))
   * ```
   *
   * @param bounds - The bounds to search within.
   * @returns Unordered set of shape IDs within the given bounds.
   *
   * @public
   */
  getShapeIdsInsideBounds(bounds) {
    return this._spatialIndex.getShapeIdsInsideBounds(bounds);
  }
  /**
   * Test whether a point (in the current page space) will will a shape. This method takes into account masks,
   * such as when a shape is the child of a frame and is partially clipped by the frame.
   *
   * @example
   * ```ts
   * editor.isPointInShape({ x: 100, y: 100 }, myShape)
   * ```
   *
   * @param shape - The shape to test against.
   * @param point - The page point to test (in the current page space).
   * @param opts - The options for the hit point testing.
   *
   * @public
   */
  isPointInShape(shape, point, opts = {}) {
    const { hitInside = false, margin = 0 } = opts;
    const id = typeof shape === "string" ? shape : shape.id;
    const pageMask = this.getShapeMask(id);
    if (pageMask && !pointInPolygon(point, pageMask)) return false;
    return this.getShapeGeometry(id).hitTestPoint(
      this.getPointInShapeSpace(shape, point),
      margin,
      hitInside
    );
  }
  /**
   * Convert a point in the current page space to a point in the local space of a shape. For example, if a
   * shape's page point were `{ x: 100, y: 100 }`, a page point at `{ x: 110, y: 110 }` would be at
   * `{ x: 10, y: 10 }` in the shape's local space.
   *
   * @example
   * ```ts
   * editor.getPointInShapeSpace(myShape, { x: 100, y: 100 })
   * ```
   *
   * @param shape - The shape to get the point in the local space of.
   * @param point - The page point to get in the local space of the shape.
   *
   * @public
   */
  getPointInShapeSpace(shape, point) {
    const id = typeof shape === "string" ? shape : shape.id;
    return this._getShapePageTransformCache().get(id).clone().invert().applyToPoint(point);
  }
  /**
   * Convert a delta in the current page space to a point in the local space of a shape's parent.
   *
   * @example
   * ```ts
   * editor.getPointInParentSpace(myShape.id, { x: 100, y: 100 })
   * ```
   *
   * @param shape - The shape to get the point in the local space of.
   * @param point - The page point to get in the local space of the shape.
   *
   * @public
   */
  getPointInParentSpace(shape, point) {
    const id = typeof shape === "string" ? shape : shape.id;
    const freshShape = this.getShape(id);
    if (!freshShape) return new Vec(0, 0);
    if (isPageId(freshShape.parentId)) return Vec.From(point);
    const parentTransform = this.getShapePageTransform(freshShape.parentId);
    if (!parentTransform) return Vec.From(point);
    return parentTransform.clone().invert().applyToPoint(point);
  }
  getCurrentPageShapes() {
    return Array.from(this.getCurrentPageShapeIds(), (id) => this.store.get(id));
  }
  getCurrentPageShapesSorted() {
    const result = [];
    const topLevelShapes = this.getSortedChildIdsForParent(this.getCurrentPageId());
    for (let i = 0, n = topLevelShapes.length; i < n; i++) {
      pushShapeWithDescendants(this, topLevelShapes[i], result);
    }
    return result;
  }
  getCurrentPageRenderingShapesSorted() {
    const culledShapes = this.getCulledShapes();
    return this.getCurrentPageShapesSorted().filter(
      ({ id }) => !culledShapes.has(id) && !this.isShapeHidden(id)
    );
  }
  isShapeOfType(arg, type) {
    const shape = typeof arg === "string" ? this.getShape(arg) : arg;
    if (!shape) return false;
    return shape.type === type;
  }
  /**
   * Get whether a shape behaves like a frame — a container that has child
   * shapes, requires full-brush selection, blocks erasure from inside, etc.
   *
   * @example
   * ```ts
   * const isFrameLike = editor.isShapeFrameLike(someShape)
   * ```
   *
   * @param shape - The shape (or shape id) to test.
   *
   * @public
   */
  isShapeFrameLike(shape) {
    const _shape = typeof shape === "string" ? this.getShape(shape) : shape;
    if (!_shape) return false;
    return this.getShapeUtil(_shape).isFrameLike(_shape);
  }
  /**
   * Get a shape by its id.
   *
   * @example
   * ```ts
   * editor.getShape('box1')
   * ```
   *
   * @param shape - The shape (or the id of the shape) to get.
   *
   * @public
   */
  getShape(shape) {
    const id = typeof shape === "string" ? shape : shape.id;
    if (!isShapeId(id)) return void 0;
    return this.store.get(id);
  }
  /**
   * Get the parent shape for a given shape. Returns undefined if the shape is the direct child of
   * the page.
   *
   * @example
   * ```ts
   * editor.getShapeParent(myShape)
   * ```
   *
   * @public
   */
  getShapeParent(shape) {
    const id = typeof shape === "string" ? shape : shape?.id;
    if (!id) return void 0;
    const freshShape = this.getShape(id);
    if (freshShape === void 0 || !isShapeId(freshShape.parentId)) return void 0;
    return this.getShape(freshShape.parentId);
  }
  /**
   * If siblingShape and targetShape are siblings, this returns targetShape. If targetShape has an
   * ancestor who is a sibling of siblingShape, this returns that ancestor. Otherwise, this returns
   * undefined.
   *
   * @internal
   */
  getShapeNearestSibling(siblingShape, targetShape) {
    if (!targetShape) {
      return void 0;
    }
    if (targetShape.parentId === siblingShape.parentId) {
      return targetShape;
    }
    const ancestor = this.findShapeAncestor(
      targetShape,
      (ancestor2) => ancestor2.parentId === siblingShape.parentId
    );
    return ancestor;
  }
  /**
   * Get whether the given shape is the descendant of the given page.
   *
   * @example
   * ```ts
   * editor.isShapeInPage(myShape)
   * editor.isShapeInPage(myShape, 'page1')
   * ```
   *
   * @param shape - The shape to check.
   * @param pageId - The id of the page to check against. Defaults to the current page.
   *
   * @public
   */
  isShapeInPage(shape, pageId = this.getCurrentPageId()) {
    const id = typeof shape === "string" ? shape : shape.id;
    const shapeToCheck = this.getShape(id);
    if (!shapeToCheck) return false;
    let shapeIsInPage = false;
    if (shapeToCheck.parentId === pageId) {
      shapeIsInPage = true;
    } else {
      let parent = this.getShape(shapeToCheck.parentId);
      isInPageSearch: while (parent) {
        if (parent.parentId === pageId) {
          shapeIsInPage = true;
          break isInPageSearch;
        }
        parent = this.getShape(parent.parentId);
      }
    }
    return shapeIsInPage;
  }
  /**
   * Get the id of the containing page for a given shape.
   *
   * @param shape - The shape to get the page id for.
   *
   * @returns The id of the page that contains the shape, or undefined if the shape is undefined.
   *
   * @public
   */
  getAncestorPageId(shape) {
    const id = typeof shape === "string" ? shape : shape?.id;
    const _shape = id && this.getShape(id);
    if (!_shape) return void 0;
    if (isPageId(_shape.parentId)) {
      return _shape.parentId;
    } else {
      return this.getAncestorPageId(this.getShape(_shape.parentId));
    }
  }
  // Parents and children
  /**
   * A cache of parents to children.
   *
   * @internal
   */
  _parentIdsToChildIds;
  /**
   * Reparent shapes to a new parent. This operation preserves the shape's current page positions /
   * rotations.
   *
   * @example
   * ```ts
   * editor.reparentShapes([box1, box2], 'frame1')
   * editor.reparentShapes([box1.id, box2.id], 'frame1')
   * editor.reparentShapes([box1.id, box2.id], 'frame1', 4)
   * ```
   *
   * @param shapes - The shapes (or shape ids) of the shapes to reparent.
   * @param parentId - The id of the new parent shape.
   * @param insertIndex - The index to insert the children.
   *
   * @public
   */
  reparentShapes(shapes, parentId, insertIndex) {
    const ids = typeof shapes[0] === "string" ? shapes : shapes.map((s) => s.id);
    if (ids.length === 0) return this;
    const changes = [];
    const parentTransform = isPageId(parentId) ? Mat.Identity() : this.getShapePageTransform(parentId);
    const parentPageRotation = parentTransform.rotation();
    let indices = [];
    const sibs = compact(this.getSortedChildIdsForParent(parentId).map((id) => this.getShape(id)));
    if (insertIndex) {
      const sibWithInsertIndex = sibs.find((s) => s.index === insertIndex);
      if (sibWithInsertIndex) {
        const sibAbove = sibs[sibs.indexOf(sibWithInsertIndex) + 1];
        if (sibAbove) {
          indices = getIndicesBetween(insertIndex, sibAbove.index, ids.length);
        } else {
          indices = getIndicesAbove(insertIndex, ids.length);
        }
      } else {
        const sibAbove = sibs.sort(sortByIndex).find((s) => s.index > insertIndex);
        if (sibAbove) {
          indices = getIndicesBetween(insertIndex, sibAbove.index, ids.length);
        } else {
          indices = getIndicesAbove(insertIndex, ids.length);
        }
      }
    } else {
      const sib = sibs.length && sibs[sibs.length - 1];
      indices = sib ? getIndicesAbove(sib.index, ids.length) : getIndices(ids.length);
    }
    const invertedParentTransform = parentTransform.clone().invert();
    const shapesToReparent = compact(ids.map((id) => this.getShape(id))).sort(sortByIndex);
    this.run(
      () => {
        for (let i = 0; i < shapesToReparent.length; i++) {
          const shape = shapesToReparent[i];
          const pageTransform = this.getShapePageTransform(shape);
          if (!pageTransform) continue;
          const pagePoint = pageTransform.point();
          if (!pagePoint) continue;
          const newPoint = invertedParentTransform.applyToPoint(pagePoint);
          const newRotation = pageTransform.rotation() - parentPageRotation;
          if (shape.id === parentId) {
            throw Error("Attempted to reparent a shape to itself!");
          }
          changes.push({
            id: shape.id,
            type: shape.type,
            parentId,
            x: newPoint.x,
            y: newPoint.y,
            rotation: newRotation,
            index: indices[i]
          });
        }
        this.updateShapes(changes);
      },
      { ignoreShapeLock: true }
    );
    return this;
  }
  /**
   * Get the index above the highest child of a given parent.
   *
   * @param parent - The parent (or the id) of the parent.
   *
   * @returns The index.
   *
   * @public
   */
  getHighestIndexForParent(parent) {
    const parentId = typeof parent === "string" ? parent : parent.id;
    const children = this._parentIdsToChildIds.get()[parentId];
    if (!children || children.length === 0) {
      return getIndexAbove(ZERO_INDEX_KEY);
    }
    const shape = this.getShape(children[children.length - 1]);
    return getIndexAbove(shape.index);
  }
  /**
   * Get an array of all the children of a shape.
   *
   * @example
   * ```ts
   * editor.getSortedChildIdsForParent('frame1')
   * ```
   *
   * @param parent - The parent (or the id) of the parent shape.
   *
   * @public
   */
  getSortedChildIdsForParent(parent) {
    const parentId = typeof parent === "string" ? parent : parent.id;
    const ids = this._parentIdsToChildIds.get()[parentId];
    if (!ids) return EMPTY_ARRAY;
    return ids;
  }
  /**
   * Run a visitor function for all descendants of a shape.
   *
   * @example
   * ```ts
   * editor.visitDescendants('frame1', myCallback)
   * ```
   *
   * @param parent - The parent (or the id) of the parent shape.
   * @param visitor - The visitor function.
   *
   * @public
   */
  visitDescendants(parent, visitor) {
    const children = this.getSortedChildIdsForParent(parent);
    for (const id of children) {
      if (visitor(id) === false) continue;
      this.visitDescendants(id, visitor);
    }
    return this;
  }
  /**
   * Get the shape ids of all descendants of the given shapes (including the shapes themselves). IDs are returned in z-index order.
   *
   * @param ids - The ids of the shapes to get descendants of.
   *
   * @returns The descendant ids.
   *
   * @public
   */
  getShapeAndDescendantIds(ids) {
    const shapeIds = /* @__PURE__ */ new Set();
    for (const shape of ids.map((id) => this.getShape(id)).sort(sortByIndex)) {
      shapeIds.add(shape.id);
      this.visitDescendants(shape, (descendantId) => {
        shapeIds.add(descendantId);
      });
    }
    return shapeIds;
  }
  /**
   * Get the shape that some shapes should be dropped on at a given point.
   *
   * @param point - The point to find the parent for.
   * @param droppingShapes - The shapes that are being dropped.
   *
   * @returns The shape to drop on.
   *
   * @public
   */
  getDraggingOverShape(point, droppingShapes) {
    const draggingShapes = compact(droppingShapes.map((s) => this.getShape(s))).filter(
      (s) => !s.isLocked && !this.isShapeHidden(s)
    );
    const maybeDraggingOverShapes = this.getShapesAtPoint(point, {
      hitInside: true,
      margin: 0
    }).filter(
      (s) => !droppingShapes.includes(s) && !s.isLocked && !this.isShapeHidden(s) && !draggingShapes.includes(s)
    );
    for (const maybeDraggingOverShape of maybeDraggingOverShapes) {
      const shapeUtil = this.getShapeUtil(maybeDraggingOverShape);
      if (shapeUtil.onDragShapesOver || shapeUtil.onDragShapesIn || shapeUtil.onDragShapesOut || shapeUtil.onDropShapesOver) {
        return maybeDraggingOverShape;
      }
    }
  }
  /**
   * Get the shape that should be selected when you click on a given shape, assuming there is
   * nothing already selected. It will not return anything higher than or including the current
   * focus layer.
   *
   * @param shape - The shape to get the outermost selectable shape for.
   * @param filter - A function to filter the selectable shapes.
   *
   * @returns The outermost selectable shape.
   *
   * @public
   */
  getOutermostSelectableShape(shape, filter) {
    const id = typeof shape === "string" ? shape : shape.id;
    const freshShape = this.getShape(id);
    let match = freshShape;
    let node = freshShape;
    const focusedGroup = this.getFocusedGroup();
    while (node) {
      if (this.isShapeOfType(node, "group") && focusedGroup?.id !== node.id && !this.hasAncestor(focusedGroup, node.id) && (filter?.(node) ?? true)) {
        match = node;
      } else if (focusedGroup?.id === node.id) {
        break;
      }
      node = this.getShapeParent(node);
    }
    return match;
  }
  _getBindingsIndexCache() {
    const index = bindingsIndex(this);
    return this.store.createComputedCache(
      "bindingsIndex",
      (shape) => {
        return index.get().get(shape.id);
      },
      // we can ignore the shape equality check here because the index is
      // computed incrementally based on what bindings are in the store
      { areRecordsEqual: () => true }
    );
  }
  /**
   * Get a binding from the store by its ID if it exists.
   */
  getBinding(id) {
    return this.store.get(id);
  }
  getBindingsFromShape(shape, type) {
    const id = typeof shape === "string" ? shape : shape.id;
    return this.getBindingsInvolvingShape(id).filter(
      (b) => b.fromId === id && b.type === type
    );
  }
  getBindingsToShape(shape, type) {
    const id = typeof shape === "string" ? shape : shape.id;
    return this.getBindingsInvolvingShape(id).filter(
      (b) => b.toId === id && b.type === type
    );
  }
  getBindingsInvolvingShape(shape, type) {
    const id = typeof shape === "string" ? shape : shape.id;
    const result = this._getBindingsIndexCache().get(id) ?? EMPTY_ARRAY;
    if (!type) return result;
    return result.filter((b) => b.type === type);
  }
  /**
   * Create bindings from a list of partial bindings. You can omit the ID and most props of a
   * binding, but the `type`, `toId`, and `fromId` must all be provided.
   */
  createBindings(partials) {
    const bindings = [];
    for (const partial of partials) {
      const fromShape = this.getShape(partial.fromId);
      const toShape = this.getShape(partial.toId);
      if (!fromShape || !toShape) continue;
      if (!this.canBindShapes({ fromShape, toShape, binding: partial })) continue;
      const util = this.getBindingUtil(partial.type);
      const defaultProps = util.getDefaultProps();
      const binding = this.store.schema.types.binding.create({
        ...partial,
        id: partial.id ?? createBindingId(),
        props: {
          ...defaultProps,
          ...partial.props
        }
      });
      bindings.push(binding);
    }
    this.store.put(bindings);
    return this;
  }
  /**
   * Create a single binding from a partial. You can omit the ID and most props of a binding, but
   * the `type`, `toId`, and `fromId` must all be provided.
   */
  createBinding(partial) {
    return this.createBindings([partial]);
  }
  /**
   * Update bindings from a list of partial bindings. Each partial must include an ID, which will
   * be used to match the binding to it's existing record. If there is no existing record, that
   * binding is skipped. The changes from the partial are merged into the existing record.
   */
  updateBindings(partials) {
    const updated = [];
    for (const partial of partials) {
      if (!partial) continue;
      const current = this.getBinding(partial.id);
      if (!current) continue;
      const updatedBinding = applyPartialToRecordWithProps(current, partial);
      if (updatedBinding === current) continue;
      const fromShape = this.getShape(updatedBinding.fromId);
      const toShape = this.getShape(updatedBinding.toId);
      if (!fromShape || !toShape) continue;
      if (!this.canBindShapes({ fromShape, toShape, binding: updatedBinding })) continue;
      updated.push(updatedBinding);
    }
    this.store.put(updated);
    return this;
  }
  /**
   * Update a binding from a partial binding. Each partial must include an ID, which will be used
   * to match the binding to it's existing record. If there is no existing record, that binding is
   * skipped. The changes from the partial are merged into the existing record.
   */
  updateBinding(partial) {
    return this.updateBindings([partial]);
  }
  /**
   * Delete several bindings by their IDs. If a binding ID doesn't exist, it's ignored.
   */
  deleteBindings(bindings, { isolateShapes = false } = {}) {
    const ids = bindings.map((binding) => typeof binding === "string" ? binding : binding.id);
    if (isolateShapes) {
      this.store.atomic(() => {
        for (const id of ids) {
          const binding = this.getBinding(id);
          if (!binding) continue;
          const util = this.getBindingUtil(binding);
          util.onBeforeIsolateFromShape?.({ binding, removedShape: this.getShape(binding.toId) });
          util.onBeforeIsolateToShape?.({ binding, removedShape: this.getShape(binding.fromId) });
          this.store.remove([id]);
        }
      });
    } else {
      this.store.remove(ids);
    }
    return this;
  }
  /**
   * Delete a binding by its ID. If the binding doesn't exist, it's ignored.
   */
  deleteBinding(binding, opts) {
    return this.deleteBindings([binding], opts);
  }
  canBindShapes({
    fromShape,
    toShape,
    binding
  }) {
    const fromShapeType = typeof fromShape === "string" ? fromShape : fromShape.type;
    const toShapeType = typeof toShape === "string" ? toShape : toShape.type;
    const bindingType = typeof binding === "string" ? binding : binding.type;
    const canBindOpts = {
      fromShape: typeof fromShape === "string" ? { type: fromShape } : fromShape,
      toShape: typeof toShape === "string" ? { type: toShape } : toShape,
      bindingType,
      fromShapeType,
      toShapeType
    };
    if (fromShapeType === toShapeType) {
      return this.getShapeUtil(fromShapeType).canBind(canBindOpts);
    }
    return this.getShapeUtil(fromShapeType).canBind(canBindOpts) && this.getShapeUtil(toShapeType).canBind(canBindOpts);
  }
  /* -------------------- Commands -------------------- */
  /**
   * Rotate shapes by a delta in radians.
   *
   * @example
   * ```ts
   * editor.rotateShapesBy(editor.getSelectedShapeIds(), Math.PI)
   * editor.rotateShapesBy(editor.getSelectedShapeIds(), Math.PI / 2)
   * ```
   *
   * @param shapes - The shapes (or shape ids) of the shapes to move.
   * @param delta - The delta in radians to apply to the selection rotation.
   * @param opts - The options for the rotation.
   */
  rotateShapesBy(shapes, delta, opts) {
    const ids = typeof shapes[0] === "string" ? shapes : shapes.map((s) => s.id);
    if (ids.length <= 0) return this;
    const snapshot = getRotationSnapshot({ editor: this, ids });
    if (!snapshot) return this;
    applyRotationToSnapshotShapes({
      delta,
      snapshot,
      editor: this,
      stage: "one-off",
      centerOverride: opts?.center
    });
    return this;
  }
  // Gets a shape partial that includes life cycle changes: on translate start, on translate, on translate end
  getChangesToTranslateShape(initialShape, newShapeCoords) {
    let workingShape = initialShape;
    const util = this.getShapeUtil(initialShape);
    const afterTranslateStart = util.onTranslateStart?.(workingShape);
    if (afterTranslateStart) {
      workingShape = applyPartialToRecordWithProps(workingShape, afterTranslateStart);
    }
    workingShape = applyPartialToRecordWithProps(workingShape, {
      id: initialShape.id,
      type: initialShape.type,
      x: newShapeCoords.x,
      y: newShapeCoords.y
    });
    const afterTranslate = util.onTranslate?.(initialShape, workingShape);
    if (afterTranslate) {
      workingShape = applyPartialToRecordWithProps(workingShape, afterTranslate);
    }
    const afterTranslateEnd = util.onTranslateEnd?.(initialShape, workingShape);
    if (afterTranslateEnd) {
      workingShape = applyPartialToRecordWithProps(workingShape, afterTranslateEnd);
    }
    return workingShape;
  }
  /**
   * Move shapes by a delta.
   *
   * @example
   * ```ts
   * editor.nudgeShapes(['box1', 'box2'], { x: 8, y: 8 })
   * ```
   *
   * @param shapes - The shapes (or shape ids) to move.
   * @param offset - The offset to apply to the shapes.
   */
  nudgeShapes(shapes, offset) {
    const ids = typeof shapes[0] === "string" ? shapes : shapes.map((s) => s.id);
    if (ids.length <= 0) return this;
    const changes = [];
    for (const id of ids) {
      const shape = this.getShape(id);
      const localDelta = Vec.From(offset);
      const parentTransform = this.getShapeParentTransform(shape);
      if (parentTransform) localDelta.rot(-parentTransform.rotation());
      changes.push(this.getChangesToTranslateShape(shape, localDelta.add(shape)));
    }
    this.updateShapes(changes);
    return this;
  }
  /**
   * Duplicate shapes.
   *
   * @example
   * ```ts
   * editor.duplicateShapes(['box1', 'box2'], { x: 8, y: 8 })
   * editor.duplicateShapes(editor.getSelectedShapes(), { x: 8, y: 8 })
   * ```
   *
   * @param shapes - The shapes (or shape ids) to duplicate.
   * @param offset - The offset (in pixels) to apply to the duplicated shapes.
   *
   * @public
   */
  duplicateShapes(shapes, offset) {
    this.run(() => {
      const _ids = typeof shapes[0] === "string" ? shapes : shapes.map((s) => s.id);
      const ids = this._shouldIgnoreShapeLock ? _ids : this._getUnlockedShapeIds(_ids);
      if (ids.length <= 0) return this;
      const initialIds = new Set(ids);
      const shapeIdSet = this.getShapeAndDescendantIds(ids);
      const orderedShapeIds = [...shapeIdSet].reverse();
      const shapeIds = /* @__PURE__ */ new Map();
      for (const shapeId of shapeIdSet) {
        shapeIds.set(shapeId, createShapeId());
      }
      const { shapesToCreateWithOriginals, bindingsToCreate } = withIsolatedShapes(
        this,
        shapeIdSet,
        (bindingIdsToMaintain) => {
          const bindingsToCreate2 = [];
          for (const originalId of bindingIdsToMaintain) {
            const originalBinding = this.getBinding(originalId);
            if (!originalBinding) continue;
            const duplicatedId = createBindingId();
            bindingsToCreate2.push({
              ...originalBinding,
              id: duplicatedId,
              fromId: assertExists(shapeIds.get(originalBinding.fromId)),
              toId: assertExists(shapeIds.get(originalBinding.toId))
            });
          }
          const shapesToCreateWithOriginals2 = [];
          for (const originalId of orderedShapeIds) {
            const duplicatedId = assertExists(shapeIds.get(originalId));
            const originalShape = this.getShape(originalId);
            if (!originalShape) continue;
            let ox = 0;
            let oy = 0;
            if (offset && initialIds.has(originalId)) {
              const parentTransform = this.getShapeParentTransform(originalShape);
              const vec = new Vec(offset.x, offset.y).rot(-parentTransform.rotation());
              ox = vec.x;
              oy = vec.y;
            }
            shapesToCreateWithOriginals2.push({
              shape: {
                ...originalShape,
                id: duplicatedId,
                x: originalShape.x + ox,
                y: originalShape.y + oy,
                // Use a dummy index for now, it will get updated outside of the `withIsolatedShapes`
                index: "a1",
                parentId: shapeIds.get(originalShape.parentId) ?? originalShape.parentId
              },
              originalShape
            });
          }
          return { shapesToCreateWithOriginals: shapesToCreateWithOriginals2, bindingsToCreate: bindingsToCreate2 };
        }
      );
      shapesToCreateWithOriginals.forEach(({ shape, originalShape }) => {
        const parentId = originalShape.parentId;
        const siblings = this.getSortedChildIdsForParent(parentId);
        const currentIndex = siblings.indexOf(originalShape.id);
        const siblingAboveId = siblings[currentIndex + 1];
        const siblingAbove = siblingAboveId ? this.getShape(siblingAboveId) : void 0;
        const index = getIndexBetween(originalShape.index, siblingAbove?.index);
        shape.index = index;
      });
      const shapesToCreate = shapesToCreateWithOriginals.map(({ shape, originalShape }) => {
        return this.getShapeUtil(shape).onBeforeDuplicate?.(originalShape, shape) ?? shape;
      });
      if (!this.canCreateShapes(shapesToCreate)) {
        alertMaxShapes(this);
        return;
      }
      this.createShapes(shapesToCreate);
      this.createBindings(bindingsToCreate);
      this.setSelectedShapes(
        compact(
          ids.map((oldId) => {
            const newId = shapeIds.get(oldId);
            if (!newId) return null;
            if (!this.getShape(newId)) return null;
            return newId;
          })
        )
      );
      if (offset !== void 0) {
        const selectionPageBounds = this.getSelectionPageBounds();
        const viewportPageBounds = this.getViewportPageBounds();
        if (selectionPageBounds && !viewportPageBounds.contains(selectionPageBounds)) {
          this.centerOnPoint(selectionPageBounds.center, {
            animation: { duration: this.options.animationMediumMs }
          });
        }
      }
    });
    return this;
  }
  /**
   * Move shapes to page.
   *
   * @example
   * ```ts
   * editor.moveShapesToPage(['box1', 'box2'], 'page1')
   * ```
   *
   * @param shapes - The shapes (or shape ids) of the shapes to move.
   * @param pageId - The id of the page where the shapes will be moved.
   *
   * @public
   */
  moveShapesToPage(shapes, pageId) {
    const ids = typeof shapes[0] === "string" ? shapes : shapes.map((s) => s.id);
    if (ids.length === 0) return this;
    if (this.getIsReadonly()) return this;
    const currentPageId = this.getCurrentPageId();
    if (pageId === currentPageId) return this;
    if (!this.store.has(pageId)) return this;
    const content = this.getContentFromCurrentPage(ids);
    if (!content) return this;
    if (this.getPageShapeIds(pageId).size + content.shapes.length > this.options.maxShapesPerPage) {
      alertMaxShapes(this, pageId);
      return this;
    }
    const fromPageZ = this.getCamera().z;
    this.run(() => {
      this.deleteShapes(ids);
      this.setCurrentPage(pageId);
      this.setFocusedGroup(null);
      this.selectNone();
      this.putContentOntoCurrentPage(content, {
        select: true,
        preserveIds: true,
        preservePosition: true
      });
      this.setCamera({ ...this.getCamera(), z: fromPageZ });
      this.centerOnPoint(this.getSelectionRotatedPageBounds().center);
    });
    return this;
  }
  /**
   * Toggle the lock state of one or more shapes. If there is a mix of locked and unlocked shapes, all shapes will be locked.
   *
   * @param shapes - The shapes (or shape ids) to toggle.
   *
   * @public
   */
  toggleLock(shapes) {
    const ids = typeof shapes[0] === "string" ? shapes : shapes.map((s) => s.id);
    if (this.getIsReadonly() || ids.length === 0) return this;
    let allLocked = true, allUnlocked = true;
    const shapesToToggle = [];
    for (const id of ids) {
      const shape = this.getShape(id);
      if (shape) {
        shapesToToggle.push(shape);
        if (shape.isLocked) {
          allUnlocked = false;
        } else {
          allLocked = false;
        }
      }
    }
    this.run(() => {
      if (allUnlocked) {
        this.updateShapes(
          shapesToToggle.map((shape) => ({ id: shape.id, type: shape.type, isLocked: true }))
        );
        this.setSelectedShapes([]);
      } else if (allLocked) {
        this.updateShapes(
          shapesToToggle.map((shape) => ({ id: shape.id, type: shape.type, isLocked: false }))
        );
      } else {
        this.updateShapes(
          shapesToToggle.map((shape) => ({ id: shape.id, type: shape.type, isLocked: true }))
        );
      }
    });
    return this;
  }
  /**
   * Send shapes to the back of the page's object list.
   *
   * @example
   * ```ts
   * editor.sendToBack(['id1', 'id2'])
   * editor.sendToBack(box1, box2)
   * ```
   *
   * @param shapes - The shapes (or shape ids) to move.
   *
   * @public
   */
  sendToBack(shapes) {
    const ids = typeof shapes[0] === "string" ? shapes : shapes.map((s) => s.id);
    const changes = getReorderingShapesChanges(this, "toBack", ids, {
      considerAllShapes: true
    });
    if (changes) this.updateShapes(changes);
    return this;
  }
  /**
   * Send shapes backward in the page's object list.
   *
   * @example
   * ```ts
   * editor.sendBackward(['id1', 'id2'])
   * editor.sendBackward([box1, box2])
   * ```
   *
   * By default, the operation will only consider overlapping shapes.
   * To consider all shapes, pass `{ considerAllShapes: true }` in the options.
   *
   * @example
   * ```ts
   * editor.sendBackward(['id1', 'id2'], { considerAllShapes: true })
   * ```
   *
   * @param shapes - The shapes (or shape ids) to move.
   * @param opts - The options for the backward operation.
   *
   * @public
   */
  sendBackward(shapes, opts = {}) {
    const ids = typeof shapes[0] === "string" ? shapes : shapes.map((s) => s.id);
    const changes = getReorderingShapesChanges(this, "backward", ids, opts);
    if (changes) this.updateShapes(changes);
    return this;
  }
  /**
   * Bring shapes forward in the page's object list.
   *
   * @example
   * ```ts
   * editor.bringForward(['id1', 'id2'])
   * editor.bringForward(box1,  box2)
   * ```
   *
   * By default, the operation will only consider overlapping shapes.
   * To consider all shapes, pass `{ considerAllShapes: true }` in the options.
   *
   * @example
   * ```ts
   * editor.bringForward(['id1', 'id2'], { considerAllShapes: true })
   * ```
   *
   * @param shapes - The shapes (or shape ids) to move.
   * @param opts - The options for the forward operation.
   *
   * @public
   */
  bringForward(shapes, opts = {}) {
    const ids = typeof shapes[0] === "string" ? shapes : shapes.map((s) => s.id);
    const changes = getReorderingShapesChanges(this, "forward", ids, opts);
    if (changes) this.updateShapes(changes);
    return this;
  }
  /**
   * Bring shapes to the front of the page's object list.
   *
   * @example
   * ```ts
   * editor.bringToFront(['id1', 'id2'])
   * editor.bringToFront([box1, box2])
   * ```
   *
   * @param shapes - The shapes (or shape ids) to move.
   *
   * @public
   */
  bringToFront(shapes) {
    const ids = typeof shapes[0] === "string" ? shapes : shapes.map((s) => s.id);
    const changes = getReorderingShapesChanges(this, "toFront", ids);
    if (changes) this.updateShapes(changes);
    return this;
  }
  /**
   * Shared clustering logic for layout methods. Resolves shapes, optionally filters to
   * axis-aligned shapes, checks canBeLaidOut, and groups shapes into clusters via arrow bindings.
   *
   * @internal
   */
  getShapeClusters(shapes, type, opts) {
    const ids = typeof shapes[0] === "string" ? shapes : shapes.map((s) => s.id);
    let freshShapes = compact(ids.map((id) => this.getShape(id)));
    if (opts?.filterAxisAligned) {
      freshShapes = freshShapes.filter(
        (s) => this.getShapePageTransform(s)?.rotation() % (PI / 2) === 0
      );
    }
    const clusters = [];
    const allBounds = [];
    const visited = /* @__PURE__ */ new Set();
    for (const shape of freshShapes) {
      if (visited.has(shape.id)) continue;
      visited.add(shape.id);
      const shapePageBounds = this.getShapePageBounds(shape);
      if (!shapePageBounds) continue;
      if (!this.getShapeUtil(shape).canBeLaidOut?.(shape, {
        type,
        shapes: freshShapes
      })) {
        continue;
      }
      const shapesMovingTogether = [shape];
      const boundsOfShapesMovingTogether = [shapePageBounds];
      this.collectShapesViaArrowBindings({
        bindings: this.getBindingsToShape(shape.id, "arrow"),
        initialShapes: freshShapes,
        resultShapes: shapesMovingTogether,
        resultBounds: boundsOfShapesMovingTogether,
        visited
      });
      const commonPageBounds = Box.Common(boundsOfShapesMovingTogether);
      if (!commonPageBounds) continue;
      clusters.push({
        shapes: shapesMovingTogether,
        pageBounds: commonPageBounds
      });
      allBounds.push(commonPageBounds);
    }
    return { clusters, allBounds };
  }
  /**
   * @internal
   */
  collectShapesViaArrowBindings(info) {
    const { initialShapes, resultShapes, resultBounds, bindings, visited } = info;
    for (const binding of bindings) {
      for (const id of [binding.fromId, binding.toId]) {
        if (!visited.has(id)) {
          const aligningShape = initialShapes.find((s) => s.id === id);
          if (aligningShape && !visited.has(aligningShape.id)) {
            visited.add(aligningShape.id);
            const shapePageBounds = this.getShapePageBounds(aligningShape);
            if (!shapePageBounds) continue;
            resultShapes.push(aligningShape);
            resultBounds.push(shapePageBounds);
            this.collectShapesViaArrowBindings({
              ...info,
              bindings: this.getBindingsInvolvingShape(aligningShape, "arrow")
            });
          }
        }
      }
    }
  }
  /**
   * Flip shape positions.
   *
   * @example
   * ```ts
   * editor.flipShapes([box1, box2], 'horizontal', 32)
   * editor.flipShapes(editor.getSelectedShapeIds(), 'horizontal', 32)
   * ```
   *
   * @param shapes - The ids of the shapes to flip.
   * @param operation - Whether to flip horizontally or vertically.
   *
   * @public
   */
  flipShapes(shapes, operation) {
    if (this.getIsReadonly()) return this;
    const ids = typeof shapes[0] === "string" ? shapes : shapes.map((s) => s.id);
    const shapesToFlipFirstPass = compact(ids.map((id) => this.getShape(id)));
    for (const shape of shapesToFlipFirstPass) {
      if (this.isShapeOfType(shape, "group")) {
        const childrenOfGroups = compact(
          this.getSortedChildIdsForParent(shape.id).map((id) => this.getShape(id))
        );
        shapesToFlipFirstPass.push(...childrenOfGroups);
      }
    }
    const shapesToFlip = [];
    const allBounds = [];
    for (const shape of shapesToFlipFirstPass) {
      const util = this.getShapeUtil(shape);
      if (!util.canBeLaidOut(shape, {
        type: "flip",
        shapes: shapesToFlipFirstPass
      })) {
        continue;
      }
      const pageBounds = this.getShapePageBounds(shape);
      const localBounds = this.getShapeGeometry(shape).bounds;
      const pageTransform = this.getShapePageTransform(shape.id);
      if (!(pageBounds && localBounds && pageTransform)) continue;
      shapesToFlip.push({
        shape,
        localBounds,
        pageTransform,
        isAspectRatioLocked: util.isAspectRatioLocked(shape)
      });
      allBounds.push(pageBounds);
    }
    if (!shapesToFlip.length) return this;
    const scaleOriginPage = Box.Common(allBounds).center;
    this.run(() => {
      for (const { shape, localBounds, pageTransform, isAspectRatioLocked } of shapesToFlip) {
        this.resizeShape(
          shape.id,
          { x: operation === "horizontal" ? -1 : 1, y: operation === "vertical" ? -1 : 1 },
          {
            initialBounds: localBounds,
            initialPageTransform: pageTransform,
            initialShape: shape,
            isAspectRatioLocked,
            mode: "scale_shape",
            scaleOrigin: scaleOriginPage,
            scaleAxisRotation: 0
          }
        );
      }
    });
    return this;
  }
  /**
   * Stack shape.
   *
   * @example
   * ```ts
   * editor.stackShapes([box1, box2], 'horizontal')
   * editor.stackShapes(editor.getSelectedShapeIds(), 'horizontal')
   * ```
   *
   * @param shapes - The shapes (or shape ids) to stack.
   * @param operation - Whether to stack horizontally or vertically.
   * @param gap - The gap to leave between shapes. By default, uses the editor's `adjacentShapeMargin` option.
   *
   * @public
   */
  stackShapes(shapes, operation, gap) {
    const _gap = gap ?? this.options.adjacentShapeMargin;
    if (this.getIsReadonly()) return this;
    const { clusters: shapeClustersToStack } = this.getShapeClusters(shapes, "stack");
    const len = shapeClustersToStack.length;
    if (_gap === 0 && len < 3 || len < 2) return this;
    let val;
    let min;
    let max;
    let dim;
    if (operation === "horizontal") {
      val = "x";
      min = "minX";
      max = "maxX";
      dim = "width";
    } else {
      val = "y";
      min = "minY";
      max = "maxY";
      dim = "height";
    }
    let shapeGap = 0;
    if (_gap === 0) {
      const gaps = {};
      shapeClustersToStack.sort((a, b) => a.pageBounds[min] - b.pageBounds[min]);
      for (let i = 0; i < len - 1; i++) {
        const currCluster = shapeClustersToStack[i];
        const nextCluster = shapeClustersToStack[i + 1];
        const gap2 = nextCluster.pageBounds[min] - currCluster.pageBounds[max];
        if (!gaps[gap2]) {
          gaps[gap2] = 0;
        }
        gaps[gap2]++;
      }
      let maxCount = 1;
      for (const [gap2, count] of Object.entries(gaps)) {
        if (count > maxCount) {
          maxCount = count;
          shapeGap = parseFloat(gap2);
        }
      }
      if (maxCount === 1) {
        let totalCount = 0;
        for (const [gap2, count] of Object.entries(gaps)) {
          shapeGap += parseFloat(gap2) * count;
          totalCount += count;
        }
        shapeGap /= totalCount;
      }
    } else {
      shapeGap = _gap;
    }
    const changes = [];
    let v = shapeClustersToStack[0].pageBounds[max];
    for (let i = 1; i < shapeClustersToStack.length; i++) {
      const { shapes: shapes2, pageBounds } = shapeClustersToStack[i];
      const delta = new Vec();
      delta[val] = v + shapeGap - pageBounds[val];
      for (const shape of shapes2) {
        const shapeDelta = delta.clone();
        const parent = this.getShapeParent(shape);
        if (parent) {
          const parentTransform = this.getShapePageTransform(parent);
          if (parentTransform) shapeDelta.rot(-parentTransform.rotation());
        }
        shapeDelta.add(shape);
        changes.push(this.getChangesToTranslateShape(shape, shapeDelta));
      }
      v += pageBounds[dim] + shapeGap;
    }
    this.updateShapes(changes);
    return this;
  }
  /**
   * Pack shapes into a grid centered on their current position. Based on potpack (https://github.com/mapbox/potpack).
   *
   * @example
   * ```ts
   * editor.packShapes([box1, box2])
   * editor.packShapes(editor.getSelectedShapeIds(), 32)
   * ```
   *
   *
   * @param shapes - The shapes (or shape ids) to pack.
   * @param gap - The padding to apply to the packed shapes. Defaults to the editor's `adjacentShapeMargin` option.
   */
  packShapes(shapes, _gap) {
    if (this.getIsReadonly()) return this;
    const gap = _gap ?? this.options.adjacentShapeMargin;
    const { clusters, allBounds } = this.getShapeClusters(shapes, "pack");
    const shapeClustersToPack = clusters.map((cluster) => ({
      ...cluster,
      nextPageBounds: cluster.pageBounds.clone()
    }));
    if (shapeClustersToPack.length < 2) return this;
    let area = 0;
    for (const { pageBounds } of shapeClustersToPack) {
      area += pageBounds.width * pageBounds.height;
    }
    const commonBounds = Box.Common(allBounds);
    const maxWidth = commonBounds.width;
    shapeClustersToPack.sort((a, b) => a.pageBounds.width - b.pageBounds.width).sort((a, b) => a.pageBounds.height - b.pageBounds.height);
    const startWidth = Math.max(Math.ceil(Math.sqrt(area / 0.95)), maxWidth);
    const spaces = [new Box(commonBounds.x, commonBounds.y, startWidth, Infinity)];
    let width = 0;
    let height = 0;
    let space;
    let last2;
    for (const { nextPageBounds } of shapeClustersToPack) {
      for (let i = spaces.length - 1; i >= 0; i--) {
        space = spaces[i];
        if (nextPageBounds.width > space.width || nextPageBounds.height > space.height) continue;
        nextPageBounds.x = space.x;
        nextPageBounds.y = space.y;
        height = Math.max(height, nextPageBounds.maxY);
        width = Math.max(width, nextPageBounds.maxX);
        if (nextPageBounds.width === space.width && nextPageBounds.height === space.height) {
          last2 = spaces.pop();
          if (i < spaces.length) spaces[i] = last2;
        } else if (nextPageBounds.height === space.height) {
          space.x += nextPageBounds.width + gap;
          space.width -= nextPageBounds.width + gap;
        } else if (nextPageBounds.width === space.width) {
          space.y += nextPageBounds.height + gap;
          space.height -= nextPageBounds.height + gap;
        } else {
          spaces.push(
            new Box(
              space.x + (nextPageBounds.width + gap),
              space.y,
              space.width - (nextPageBounds.width + gap),
              nextPageBounds.height
            )
          );
          space.y += nextPageBounds.height + gap;
          space.height -= nextPageBounds.height + gap;
        }
        break;
      }
    }
    const commonAfter = Box.Common(shapeClustersToPack.map((s) => s.nextPageBounds));
    const centerDelta = Vec.Sub(commonBounds.center, commonAfter.center);
    const changes = [];
    for (const { shapes: shapes2, pageBounds, nextPageBounds } of shapeClustersToPack) {
      const delta = Vec.Sub(nextPageBounds.point, pageBounds.point).add(centerDelta);
      for (const shape of shapes2) {
        const shapeDelta = delta.clone();
        const parent = this.getShapeParent(shape);
        if (parent) {
          const parentTransform = this.getShapeParentTransform(shape);
          if (parentTransform) shapeDelta.rot(-parentTransform.rotation());
        }
        shapeDelta.add(shape);
        changes.push(this.getChangesToTranslateShape(shape, shapeDelta));
      }
    }
    if (changes.length) {
      this.updateShapes(changes);
    }
    return this;
  }
  /**
   * Align shape positions.
   *
   * @example
   * ```ts
   * editor.alignShapes([box1, box2], 'left')
   * editor.alignShapes(editor.getSelectedShapeIds(), 'left')
   * ```
   *
   * @param shapes - The shapes (or shape ids) to align.
   * @param operation - The align operation to apply.
   *
   * @public
   */
  alignShapes(shapes, operation) {
    if (this.getIsReadonly()) return this;
    const { clusters: shapeClustersToAlign, allBounds } = this.getShapeClusters(shapes, "align");
    if (shapeClustersToAlign.length < 2) return this;
    const commonBounds = Box.Common(allBounds);
    const changes = [];
    shapeClustersToAlign.forEach(({ shapes: shapes2, pageBounds }) => {
      const delta = new Vec();
      switch (operation) {
        case "top": {
          delta.y = commonBounds.minY - pageBounds.minY;
          break;
        }
        case "center-vertical": {
          delta.y = commonBounds.midY - pageBounds.minY - pageBounds.height / 2;
          break;
        }
        case "bottom": {
          delta.y = commonBounds.maxY - pageBounds.minY - pageBounds.height;
          break;
        }
        case "left": {
          delta.x = commonBounds.minX - pageBounds.minX;
          break;
        }
        case "center-horizontal": {
          delta.x = commonBounds.midX - pageBounds.minX - pageBounds.width / 2;
          break;
        }
        case "right": {
          delta.x = commonBounds.maxX - pageBounds.minX - pageBounds.width;
          break;
        }
      }
      for (const shape of shapes2) {
        const shapeDelta = delta.clone();
        const parent = this.getShapeParent(shape);
        if (parent) {
          const parentTransform = this.getShapePageTransform(parent);
          if (parentTransform) shapeDelta.rot(-parentTransform.rotation());
        }
        shapeDelta.add(shape);
        changes.push(this.getChangesToTranslateShape(shape, shapeDelta));
      }
    });
    this.updateShapes(changes);
    return this;
  }
  /**
   * Distribute shape positions.
   *
   * @example
   * ```ts
   * editor.distributeShapes([box1, box2], 'horizontal')
   * editor.distributeShapes(editor.getSelectedShapeIds(), 'horizontal')
   * ```
   *
   * @param shapes - The shapes (or shape ids) to distribute.
   * @param operation - Whether to distribute shapes horizontally or vertically.
   *
   * @public
   */
  distributeShapes(shapes, operation) {
    if (this.getIsReadonly()) return this;
    const { clusters: shapeClustersToDistribute } = this.getShapeClusters(shapes, "distribute");
    if (shapeClustersToDistribute.length < 3) return this;
    let val;
    let min;
    let max;
    let dim;
    if (operation === "horizontal") {
      val = "x";
      min = "minX";
      max = "maxX";
      dim = "width";
    } else {
      val = "y";
      min = "minY";
      max = "maxY";
      dim = "height";
    }
    const changes = [];
    const first = shapeClustersToDistribute.sort((a, b) => a.pageBounds[min] - b.pageBounds[min])[0];
    const last2 = shapeClustersToDistribute.sort((a, b) => b.pageBounds[max] - a.pageBounds[max])[0];
    if (first === last2) {
      const excludedShapeIds = new Set(first.shapes.map((s) => s.id));
      const ids = typeof shapes[0] === "string" ? shapes : shapes.map((s) => s.id);
      return this.distributeShapes(
        ids.filter((id) => !excludedShapeIds.has(id)),
        operation
      );
    }
    const shapeClustersToMove = shapeClustersToDistribute.filter((shape) => shape !== first && shape !== last2).sort((a, b) => {
      if (a.pageBounds[min] === b.pageBounds[min]) {
        return a.shapes[0].id < b.shapes[0].id ? -1 : 1;
      }
      return a.pageBounds[min] - b.pageBounds[min];
    });
    const maxFirst = first.pageBounds[max];
    const range = last2.pageBounds[min] - maxFirst;
    const summedShapeDimensions = shapeClustersToMove.reduce((acc, s) => acc + s.pageBounds[dim], 0);
    const gap = (range - summedShapeDimensions) / (shapeClustersToMove.length + 1);
    for (let v = maxFirst + gap, i = 0; i < shapeClustersToMove.length; i++) {
      const { shapes: shapes2, pageBounds } = shapeClustersToMove[i];
      const delta = new Vec();
      delta[val] = v - pageBounds[val];
      if (v + pageBounds[dim] > last2.pageBounds[max] - 1) {
        delta[val] = last2.pageBounds[max] - pageBounds[max] - 1;
      }
      for (const shape of shapes2) {
        const shapeDelta = delta.clone();
        const parent = this.getShapeParent(shape);
        if (parent) {
          const parentTransform = this.getShapePageTransform(parent);
          if (parentTransform) shapeDelta.rot(-parentTransform.rotation());
        }
        shapeDelta.add(shape);
        changes.push(this.getChangesToTranslateShape(shape, shapeDelta));
      }
      v += pageBounds[dim] + gap;
    }
    this.updateShapes(changes);
    return this;
  }
  /**
   * Stretch shape sizes and positions to fill their common bounding box.
   *
   * @example
   * ```ts
   * editor.stretchShapes([box1, box2], 'horizontal')
   * editor.stretchShapes(editor.getSelectedShapeIds(), 'horizontal')
   * ```
   *
   * @param shapes - The shapes (or shape ids) to stretch.
   * @param operation - Whether to stretch shapes horizontally or vertically.
   *
   * @public
   */
  stretchShapes(shapes, operation) {
    if (this.getIsReadonly()) return this;
    const { clusters: shapeClustersToStretch, allBounds } = this.getShapeClusters(
      shapes,
      "stretch",
      { filterAxisAligned: true }
    );
    if (shapeClustersToStretch.length < 2) return this;
    const commonBounds = Box.Common(allBounds);
    let val;
    let min;
    let dim;
    if (operation === "horizontal") {
      val = "x";
      min = "minX";
      dim = "width";
    } else {
      val = "y";
      min = "minY";
      dim = "height";
    }
    this.run(() => {
      shapeClustersToStretch.forEach(({ shapes: shapes2, pageBounds }) => {
        const localOffset = new Vec();
        localOffset[val] = commonBounds[min] - pageBounds[min];
        const scaleOrigin = pageBounds.center.clone();
        scaleOrigin[val] = commonBounds[min];
        const scale = new Vec(1, 1);
        scale[val] = commonBounds[dim] / pageBounds[dim];
        for (const shape of shapes2) {
          const shapeLocalOffset = localOffset.clone();
          const parentTransform = this.getShapeParentTransform(shape);
          if (parentTransform) shapeLocalOffset.rot(-parentTransform.rotation());
          shapeLocalOffset.add(shape);
          const changes = this.getChangesToTranslateShape(shape, shapeLocalOffset);
          this.updateShape(changes);
          this.resizeShape(shape.id, scale, {
            initialBounds: this.getShapeGeometry(shape).bounds,
            scaleOrigin,
            isAspectRatioLocked: this.getShapeUtil(shape).isAspectRatioLocked(shape),
            scaleAxisRotation: 0
          });
        }
      });
    });
    return this;
  }
  /**
   * Resize and reposition a set of shapes so that their combined page bounds matches the given
   * target bounds.
   *
   * @example
   * ```ts
   * editor.resizeToBounds([box1, box2], { x: 0, y: 0, w: 500, h: 500 })
   * editor.resizeToBounds(editor.getSelectedShapeIds(), new Box(0, 0, 500, 500))
   * ```
   *
   * @param shapes - The shapes (or shape ids) to resize.
   * @param bounds - The target bounding box.
   *
   * @public
   */
  resizeToBounds(shapes, bounds) {
    if (this.getIsReadonly()) return this;
    const targetBounds = Box.From(bounds);
    const { clusters: shapeClusters, allBounds } = this.getShapeClusters(
      shapes,
      "resize_to_bounds",
      { filterAxisAligned: true }
    );
    if (shapeClusters.length === 0) return this;
    const commonBounds = Box.Common(allBounds);
    if (!commonBounds) return this;
    if (commonBounds.width === 0 || commonBounds.height === 0) return this;
    const scaleX = targetBounds.width / commonBounds.width;
    const scaleY = targetBounds.height / commonBounds.height;
    const scale = new Vec(scaleX, scaleY);
    shapeClusters.forEach(({ shapes: shapes2, pageBounds }) => {
      const localOffset = new Vec(
        targetBounds.minX - commonBounds.minX + (pageBounds.minX - commonBounds.minX) * (scaleX - 1),
        targetBounds.minY - commonBounds.minY + (pageBounds.minY - commonBounds.minY) * (scaleY - 1)
      );
      const scaleOrigin = new Vec(
        targetBounds.minX + (pageBounds.minX - commonBounds.minX) * scaleX,
        targetBounds.minY + (pageBounds.minY - commonBounds.minY) * scaleY
      );
      for (const shape of shapes2) {
        const shapeLocalOffset = localOffset.clone();
        const parentTransform = this.getShapeParentTransform(shape);
        if (parentTransform) shapeLocalOffset.rot(-parentTransform.rotation());
        shapeLocalOffset.add(shape);
        const changes = this.getChangesToTranslateShape(shape, shapeLocalOffset);
        this.updateShape(changes);
        this.resizeShape(shape.id, scale, {
          initialBounds: this.getShapeGeometry(shape).bounds,
          scaleOrigin,
          isAspectRatioLocked: this.getShapeUtil(shape).isAspectRatioLocked(shape),
          scaleAxisRotation: 0
        });
      }
    });
    return this;
  }
  /**
   * Resize a shape.
   *
   * @param shape - The shape (or the shape id of the shape) to resize.
   * @param scale - The scale factor to apply to the shape.
   * @param opts - Additional options.
   *
   * @public
   */
  resizeShape(shape, scale, opts = {}) {
    const partial = this.getResizeShapePartial(shape, scale, opts);
    if (partial) this.updateShapes([partial]);
    return this;
  }
  /**
   * Get the update for a resized shape without committing it to the store. Interactions that
   * resize many shapes at once use this to collect all of the updates and commit them in a
   * single batch. Returns null when there is nothing to update.
   *
   * Shapes that are rotated out of alignment with the scale axis cannot be resized with a
   * single update; those shapes are resized immediately (as `resizeShape` would do) and null
   * is returned.
   *
   * @internal
   */
  getResizeShapePartial(shape, scale, opts = {}) {
    const id = typeof shape === "string" ? shape : shape.id;
    if (this.getIsReadonly()) return null;
    if (!Number.isFinite(scale.x)) scale = new Vec(1, scale.y);
    if (!Number.isFinite(scale.y)) scale = new Vec(scale.x, 1);
    const initialShape = opts.initialShape ?? this.getShape(id);
    if (!initialShape) return null;
    const scaleOrigin = opts.scaleOrigin ?? this.getShapePageBounds(id)?.center;
    if (!scaleOrigin) return null;
    const pageTransform = opts.initialPageTransform ? Mat.Cast(opts.initialPageTransform) : this.getShapePageTransform(id);
    if (!pageTransform) return null;
    const pageRotation = pageTransform.rotation();
    if (pageRotation == null) return null;
    const scaleAxisRotation = opts.scaleAxisRotation ?? pageRotation;
    const initialBounds = opts.initialBounds ?? this.getShapeGeometry(id).bounds;
    if (!initialBounds) return null;
    const isAspectRatioLocked = opts.isAspectRatioLocked ?? this.getShapeUtil(initialShape).isAspectRatioLocked(initialShape);
    if (!areAnglesCompatible(pageRotation, scaleAxisRotation)) {
      this._resizeUnalignedShape(id, scale, {
        ...opts,
        initialBounds,
        scaleOrigin,
        scaleAxisRotation,
        initialPageTransform: pageTransform,
        isAspectRatioLocked,
        initialShape
      });
      return null;
    }
    const util = this.getShapeUtil(initialShape);
    if (isAspectRatioLocked) {
      if (Math.abs(scale.x) > Math.abs(scale.y)) {
        scale = new Vec(scale.x, Math.sign(scale.y) * Math.abs(scale.x));
      } else {
        scale = new Vec(Math.sign(scale.x) * Math.abs(scale.y), scale.y);
      }
    }
    let workingShape = null;
    if (util.onResize && util.canResize(initialShape)) {
      const newPagePoint = this._scalePagePoint(
        Mat.applyToPoint(pageTransform, new Vec(0, 0)),
        scaleOrigin,
        scale,
        scaleAxisRotation
      );
      const newLocalPoint = this.getPointInParentSpace(initialShape.id, newPagePoint);
      const myScale = new Vec(scale.x, scale.y);
      const areWidthAndHeightAlignedWithCorrectAxis = approximately(
        (pageRotation - scaleAxisRotation) % Math.PI,
        0
      );
      myScale.x = areWidthAndHeightAlignedWithCorrectAxis ? scale.x : scale.y;
      myScale.y = areWidthAndHeightAlignedWithCorrectAxis ? scale.y : scale.x;
      const initialPagePoint = Mat.applyToPoint(pageTransform, new Vec());
      const { x, y } = this.getPointInParentSpace(initialShape.id, initialPagePoint);
      workingShape = initialShape;
      if (!opts.skipStartAndEndCallbacks) {
        workingShape = applyPartialToRecordWithProps(
          initialShape,
          util.onResizeStart?.(initialShape) ?? void 0
        );
      }
      const resizedShape = util.onResize(
        { ...initialShape, x, y },
        {
          newPoint: newLocalPoint,
          handle: opts.dragHandle ?? "bottom_right",
          // don't set isSingle to true for children
          mode: opts.mode ?? "scale_shape",
          scaleX: myScale.x,
          scaleY: myScale.y,
          initialBounds,
          initialShape
        }
      );
      workingShape = applyPartialToRecordWithProps(workingShape, {
        id,
        type: initialShape.type,
        x: newLocalPoint.x,
        y: newLocalPoint.y,
        ...resizedShape
      });
      if (!opts.skipStartAndEndCallbacks) {
        workingShape = applyPartialToRecordWithProps(
          workingShape,
          util.onResizeEnd?.(initialShape, workingShape) ?? void 0
        );
      }
      if (resizedShape) {
        return workingShape;
      }
    }
    const initialPageCenter = Mat.applyToPoint(pageTransform, initialBounds.center);
    const newPageCenter = this._scalePagePoint(
      initialPageCenter,
      scaleOrigin,
      scale,
      scaleAxisRotation
    );
    const initialPageCenterInParentSpace = this.getPointInParentSpace(
      initialShape.id,
      initialPageCenter
    );
    const newPageCenterInParentSpace = this.getPointInParentSpace(initialShape.id, newPageCenter);
    const delta = Vec.Sub(newPageCenterInParentSpace, initialPageCenterInParentSpace);
    if (workingShape) {
      return {
        ...workingShape,
        x: initialShape.x + delta.x,
        y: initialShape.y + delta.y
      };
    }
    return {
      id,
      type: initialShape.type,
      x: initialShape.x + delta.x,
      y: initialShape.y + delta.y
    };
  }
  /** @internal */
  _scalePagePoint(point, scaleOrigin, scale, scaleAxisRotation) {
    const relativePoint = Vec.RotWith(point, scaleOrigin, -scaleAxisRotation).sub(scaleOrigin);
    const newRelativePagePoint = Vec.MulV(relativePoint, scale);
    const destination = Vec.Add(newRelativePagePoint, scaleOrigin).rotWith(
      scaleOrigin,
      scaleAxisRotation
    );
    return destination;
  }
  /** @internal */
  _resizeUnalignedShape(id, scale, options) {
    const { type } = options.initialShape;
    const shapeScale = new Vec(scale.x, scale.y);
    if (Math.abs(scale.x) > Math.abs(scale.y)) {
      shapeScale.x = Math.sign(scale.x) * Math.abs(scale.y);
    } else {
      shapeScale.y = Math.sign(scale.y) * Math.abs(scale.x);
    }
    this.resizeShape(id, shapeScale, {
      initialShape: options.initialShape,
      initialBounds: options.initialBounds,
      isAspectRatioLocked: options.isAspectRatioLocked,
      initialPageTransform: options.initialPageTransform
    });
    if (Math.sign(scale.x) * Math.sign(scale.y) < 0) {
      const parentRotation = this.getShapeParentTransform(id).rotation();
      const rotation = -options.initialShape.rotation - 2 * parentRotation;
      this.updateShapes([{ id, type, rotation }]);
    }
    const preScaleShapePageCenter = Mat.applyToPoint(
      options.initialPageTransform,
      options.initialBounds.center
    );
    const postScaleShapePageCenter = this._scalePagePoint(
      preScaleShapePageCenter,
      options.scaleOrigin,
      scale,
      options.scaleAxisRotation
    );
    const pageTransform = this.getShapePageTransform(id);
    const currentLocalBounds = this.getShapeGeometry(id).bounds;
    const currentPageCenter = Mat.applyToPoint(pageTransform, currentLocalBounds.center);
    const shapePageTransformOrigin = pageTransform.point();
    if (!currentPageCenter || !shapePageTransformOrigin) return this;
    const pageDelta = Vec.Sub(postScaleShapePageCenter, currentPageCenter);
    const postScaleShapePagePoint = Vec.Add(shapePageTransformOrigin, pageDelta);
    const { x, y } = this.getPointInParentSpace(id, postScaleShapePagePoint);
    this.updateShapes([{ id, type, x, y }]);
    return this;
  }
  /**
   * Get the initial meta value for a shape.
   *
   * @example
   * ```ts
   * editor.getInitialMetaForShape = (shape) => {
   *   if (shape.type === 'note') {
   *     return { createdBy: myCurrentUser.id }
   *   }
   * }
   * ```
   *
   * @param shape - The shape to get the initial meta for.
   *
   * @public
   */
  getInitialMetaForShape(_shape) {
    return {};
  }
  /**
   * Get whether the provided shape can be created.
   *
   * @param shape - The shape or shape IDs to check.
   *
   * @public
   */
  canCreateShape(shape) {
    return this.canCreateShapes([shape]);
  }
  /**
   * Get whether the provided shapes can be created.
   *
   * @param shapes - The shapes or shape IDs to create.
   *
   * @public
   */
  canCreateShapes(shapes) {
    return shapes.length + this.getCurrentPageShapeIds().size <= this.options.maxShapesPerPage;
  }
  /**
   * Create a single shape.
   *
   * @example
   * ```ts
   * editor.createShape(myShape)
   * editor.createShape({ id: 'box1', type: 'text', props: { richText: toRichText("ok") } })
   * ```
   *
   * @param shape - The shape (or shape partial) to create.
   *
   * @public
   */
  createShape(shape) {
    this.createShapes([shape]);
    return this;
  }
  /**
   * Create shapes.
   *
   * @example
   * ```ts
   * editor.createShapes([myShape])
   * editor.createShapes([{ id: 'box1', type: 'text', props: { richText: toRichText("ok") } }])
   * ```
   *
   * @param shapes - The shapes (or shape partials) to create.
   *
   * @public
   */
  createShapes(shapes) {
    if (!Array.isArray(shapes)) {
      throw Error("Editor.createShapes: must provide an array of shapes or shape partials");
    }
    if (this.getIsReadonly()) return this;
    if (shapes.length <= 0) return this;
    const currentPageShapeIds = this.getCurrentPageShapeIds();
    const maxShapesReached = shapes.length + currentPageShapeIds.size > this.options.maxShapesPerPage;
    if (maxShapesReached) {
      alertMaxShapes(this);
      return this;
    }
    const focusedGroupId = this.getFocusedGroupId();
    this.run(() => {
      const currentPageShapesSorted = this.getCurrentPageShapesSorted();
      const partials = shapes.map((partial) => {
        if (!partial.id) {
          partial = { id: createShapeId(), ...partial };
        }
        if (!partial.parentId || !(this.store.has(partial.parentId) || shapes.some((p) => p.id === partial.parentId))) {
          let parentId = this.getFocusedGroupId();
          const isPositioned = partial.x !== void 0 && partial.y !== void 0;
          if (isPositioned) {
            for (let i = currentPageShapesSorted.length - 1; i >= 0; i--) {
              const parent = currentPageShapesSorted[i];
              const util = this.getShapeUtil(parent);
              if (util.canReceiveNewChildrenOfType(parent, partial.type) && !this.isShapeHidden(parent) && this.isPointInShape(
                parent,
                // If no parent is provided, then we can treat the
                // shape's provided x/y as being in the page's space.
                { x: partial.x ?? 0, y: partial.y ?? 0 },
                {
                  margin: 0,
                  hitInside: true
                }
              )) {
                parentId = parent.id;
                break;
              }
            }
          }
          const prevParentId = partial.parentId;
          if (parentId === partial.id) {
            parentId = focusedGroupId;
          }
          if (parentId !== prevParentId) {
            partial = { ...partial };
            partial.parentId = parentId;
            if (isShapeId(parentId)) {
              const point = this.getPointInShapeSpace(this.getShape(parentId), {
                x: partial.x ?? 0,
                y: partial.y ?? 0
              });
              partial.x = point.x;
              partial.y = point.y;
              partial.rotation = -this.getShapePageTransform(parentId).rotation() + (partial.rotation ?? 0);
            }
          }
        }
        return partial;
      });
      const parentIndices = /* @__PURE__ */ new Map();
      const shapeRecordsToCreate = [];
      const { opacityForNextShape } = this.getInstanceState();
      for (const partial of partials) {
        const util = this.getShapeUtil(partial);
        let index = partial.index;
        if (!index) {
          const parentId = partial.parentId ?? focusedGroupId;
          if (!parentIndices.has(parentId)) {
            parentIndices.set(parentId, this.getHighestIndexForParent(parentId));
          }
          index = parentIndices.get(parentId);
          parentIndices.set(parentId, getIndexAbove(index));
        }
        const initialProps = util.getDefaultProps();
        for (const [style, propKey] of this.styleProps[partial.type]) {
          ;
          initialProps[propKey] = this.getStyleForNextShape(style);
        }
        let shapeRecordToCreate = this.store.schema.types.shape.create({
          ...partial,
          index,
          opacity: partial.opacity ?? opacityForNextShape,
          parentId: partial.parentId ?? focusedGroupId,
          props: "props" in partial ? { ...initialProps, ...partial.props } : initialProps
        });
        if (shapeRecordToCreate.index === void 0) {
          throw Error("no index!");
        }
        const next = this.getShapeUtil(shapeRecordToCreate).onBeforeCreate?.(shapeRecordToCreate);
        if (next) {
          shapeRecordToCreate = next;
        }
        shapeRecordsToCreate.push(shapeRecordToCreate);
      }
      shapeRecordsToCreate.forEach((shape) => {
        shape.meta = {
          ...this.getInitialMetaForShape(shape),
          ...shape.meta
        };
      });
      this.emit("created-shapes", shapeRecordsToCreate);
      this.emit("edit");
      this.store.put(shapeRecordsToCreate);
    });
    return this;
  }
  animatingShapes = /* @__PURE__ */ new Map();
  /**
   * Animate a shape.
   *
   * @example
   * ```ts
   * editor.animateShape({ id: 'box1', type: 'box', x: 100, y: 100 })
   * editor.animateShape({ id: 'box1', type: 'box', x: 100, y: 100 }, { animation: { duration: 100, ease: t => t*t } })
   * ```
   *
   * @param partial - The shape partial to update.
   * @param opts - The animation's options.
   *
   * @public
   */
  animateShape(partial, opts = { animation: DEFAULT_ANIMATION_OPTIONS }) {
    return this.animateShapes([partial], opts);
  }
  /**
   * Animate shapes.
   *
   * @example
   * ```ts
   * editor.animateShapes([{ id: 'box1', type: 'box', x: 100, y: 100 }])
   * editor.animateShapes([{ id: 'box1', type: 'box', x: 100, y: 100 }], { animation: { duration: 100, ease: t => t*t } })
   * ```
   *
   * @param partials - The shape partials to update.
   * @param opts - The animation's options.
   *
   * @public
   */
  animateShapes(partials, opts = { animation: DEFAULT_ANIMATION_OPTIONS }) {
    if (!opts.animation) return this;
    const { duration = 500, easing = EASINGS.linear } = opts.animation;
    const animationId = uniqueId();
    let remaining = duration;
    let t;
    const animations = [];
    let partial, result;
    for (let i = 0, n = partials.length; i < n; i++) {
      partial = partials[i];
      if (!partial) continue;
      const shape = this.getShape(partial.id);
      if (!shape) continue;
      result = {
        start: structuredClone(shape),
        end: applyPartialToRecordWithProps(structuredClone(shape), partial)
      };
      animations.push(result);
      this.animatingShapes.set(shape.id, animationId);
    }
    const handleTick = (elapsed) => {
      remaining -= elapsed;
      if (remaining < 0) {
        const { animatingShapes: animatingShapes2 } = this;
        const partialsToUpdate = partials.filter(
          (p) => p && animatingShapes2.get(p.id) === animationId
        );
        if (partialsToUpdate.length) {
          this.updateShapes(partialsToUpdate);
        }
        this.off("tick", handleTick);
        return;
      }
      t = easing(1 - remaining / duration);
      const { animatingShapes } = this;
      const updates = [];
      let animationIdForShape;
      for (let i = 0, n = animations.length; i < n; i++) {
        const { start, end } = animations[i];
        animationIdForShape = animatingShapes.get(start.id);
        if (animationIdForShape !== animationId) continue;
        updates.push({
          ...end,
          x: start.x + (end.x - start.x) * t,
          y: start.y + (end.y - start.y) * t,
          opacity: start.opacity + (end.opacity - start.opacity) * t,
          rotation: start.rotation + (end.rotation - start.rotation) * t,
          props: this.getShapeUtil(end).getInterpolatedProps?.(start, end, t) ?? end.props
        });
      }
      this._updateShapes(updates);
    };
    this.on("tick", handleTick);
    return this;
  }
  groupShapes(shapes, opts = {}) {
    const { groupId = createShapeId(), select = true } = opts;
    if (!Array.isArray(shapes)) {
      throw Error("Editor.groupShapes: must provide an array of shapes or shape ids");
    }
    if (this.getIsReadonly()) return this;
    const ids = typeof shapes[0] === "string" ? shapes : shapes.map((s) => s.id);
    if (ids.length <= 1) return this;
    const shapesToGroup = compact(
      (this._shouldIgnoreShapeLock ? ids : this._getUnlockedShapeIds(ids)).map(
        (id) => this.getShape(id)
      )
    );
    const sortedShapeIds = shapesToGroup.sort(sortByIndex).map((s) => s.id);
    const childBounds = compact(shapesToGroup.map((shape) => this.getShapePageBounds(shape)));
    const pageBounds = Box.Common(childBounds);
    if (!pageBounds.isValid()) {
      throw Error(`Editor.groupShapes: group bounds are invalid (NaN).`);
    }
    const { x, y } = pageBounds.point;
    const parentId = this.findCommonAncestor(shapesToGroup) ?? this.getCurrentPageId();
    if (this.getCurrentToolId() !== "select") return this;
    if (!this.isIn("select.idle")) {
      this.cancel();
    }
    const shapesWithRootParent = shapesToGroup.filter((shape) => shape.parentId === parentId).sort(sortByIndex);
    const highestIndex = shapesWithRootParent[shapesWithRootParent.length - 1]?.index;
    this.run(() => {
      this.createShapes([
        {
          id: groupId,
          type: "group",
          parentId,
          index: highestIndex,
          x,
          y,
          opacity: 1,
          props: {}
        }
      ]);
      this.reparentShapes(sortedShapeIds, groupId);
      if (select) {
        this.select(groupId);
      }
    });
    return this;
  }
  ungroupShapes(shapes, opts = {}) {
    if (this.getIsReadonly()) return this;
    const { select = true } = opts;
    const ids = typeof shapes[0] === "string" ? shapes : shapes.map((s) => s.id);
    const shapesToUngroup = compact(
      (this._shouldIgnoreShapeLock ? ids : this._getUnlockedShapeIds(ids)).map(
        (id) => this.getShape(id)
      )
    );
    if (shapesToUngroup.length === 0) return this;
    if (this.getCurrentToolId() !== "select") return this;
    if (!this.isIn("select.idle")) {
      this.cancel();
    }
    const idsToSelect = /* @__PURE__ */ new Set();
    const groups = [];
    shapesToUngroup.forEach((shape) => {
      if (this.isShapeOfType(shape, "group")) {
        groups.push(shape);
      } else {
        idsToSelect.add(shape.id);
      }
    });
    if (groups.length === 0) return this;
    this.run(() => {
      let group;
      for (let i = 0, n = groups.length; i < n; i++) {
        group = groups[i];
        const childIds = this.getSortedChildIdsForParent(group.id);
        for (let j = 0, n2 = childIds.length; j < n2; j++) {
          idsToSelect.add(childIds[j]);
        }
        this.reparentShapes(childIds, group.parentId, group.index);
      }
      this.deleteShapes(groups.map((group2) => group2.id));
      if (select) {
        this.select(...idsToSelect);
      }
    });
    return this;
  }
  /**
   * Update a shape using a partial of the shape.
   *
   * @example
   * ```ts
   * editor.updateShape({ id: 'box1', type: 'geo', props: { w: 100, h: 100 } })
   * ```
   *
   * @param partial - The shape partial to update.
   *
   * @public
   */
  updateShape(partial) {
    this.updateShapes([partial]);
    return this;
  }
  /**
   * Update shapes using partials of each shape.
   *
   * @example
   * ```ts
   * editor.updateShapes([{ id: 'box1', type: 'geo', props: { w: 100, h: 100 } }])
   * ```
   *
   * @param partials - The shape partials to update.
   *
   * @public
   */
  updateShapes(partials) {
    const compactedPartials = Array(partials.length);
    for (let i = 0, n = partials.length; i < n; i++) {
      const partial = partials[i];
      if (!partial) continue;
      const shape = this.getShape(partial.id);
      if (!shape) continue;
      if (!this._shouldIgnoreShapeLock) {
        if (shape.isLocked) {
          if (!(Object.hasOwn(partial, "isLocked") && !partial.isLocked)) {
            continue;
          }
        } else if (this.isShapeOrAncestorLocked(shape)) {
          continue;
        }
      }
      this.animatingShapes.delete(partial.id);
      compactedPartials.push(partial);
    }
    this._updateShapes(compactedPartials);
    return this;
  }
  /** @internal */
  _updateShapes(_partials) {
    if (this.getIsReadonly()) return;
    this.run(() => {
      const updates = [];
      let shape;
      let updated;
      for (let i = 0, n = _partials.length; i < n; i++) {
        const partial = _partials[i];
        if (!partial) continue;
        shape = this.getShape(partial.id);
        if (!shape) continue;
        updated = applyPartialToRecordWithProps(shape, partial);
        if (updated === shape) continue;
        updated = this.getShapeUtil(shape).onBeforeUpdate?.(shape, updated) ?? updated;
        updates.push(updated);
      }
      this.emit("edited-shapes", updates);
      this.emit("edit");
      this.store.put(updates);
    });
  }
  /** @internal */
  _getUnlockedShapeIds(ids) {
    return ids.filter((id) => !this.getShape(id)?.isLocked);
  }
  deleteShapes(_ids) {
    if (this.getIsReadonly()) return this;
    if (!Array.isArray(_ids)) {
      throw Error("Editor.deleteShapes: must provide an array of shapes or shapeIds");
    }
    const shapeIds = typeof _ids[0] === "string" ? _ids : _ids.map((s) => s.id);
    const shapeIdsToDelete = this._shouldIgnoreShapeLock ? shapeIds : this._getUnlockedShapeIds(shapeIds);
    if (shapeIdsToDelete.length === 0) return this;
    const allShapeIdsToDelete = new Set(shapeIdsToDelete);
    for (const id of shapeIdsToDelete) {
      this.visitDescendants(id, (childId) => {
        allShapeIdsToDelete.add(childId);
      });
    }
    this.emit("deleted-shapes", [...allShapeIdsToDelete]);
    this.emit("edit");
    return this.run(() => this.store.remove([...allShapeIdsToDelete]));
  }
  deleteShape(_id) {
    this.deleteShapes([typeof _id === "string" ? _id : _id.id]);
    return this;
  }
  /* --------------------- Styles --------------------- */
  /**
   * Get all the current styles among the users selected shapes
   *
   * @internal
   */
  _extractSharedStyles(shape, sharedStyleMap) {
    if (this.isShapeOfType(shape, "group")) {
      const childIds = this._parentIdsToChildIds.get()[shape.id];
      if (!childIds) return;
      for (let i = 0, n = childIds.length; i < n; i++) {
        this._extractSharedStyles(this.getShape(childIds[i]), sharedStyleMap);
      }
    } else {
      for (const [style, propKey] of this.styleProps[shape.type]) {
        sharedStyleMap.applyValue(style, getOwnProperty(shape.props, propKey));
      }
    }
  }
  _getSelectionSharedStyles() {
    const selectedShapes = this.getSelectedShapes();
    const sharedStyles = new SharedStyleMap();
    for (const selectedShape of selectedShapes) {
      this._extractSharedStyles(selectedShape, sharedStyles);
    }
    return sharedStyles;
  }
  /**
   * Get the style for the next shape.
   *
   * @example
   * ```ts
   * const color = editor.getStyleForNextShape(DefaultColorStyle)
   * ```
   *
   * @param style - The style to get.
   *
   * @public */
  getStyleForNextShape(style) {
    const value = this.getInstanceState().stylesForNextShape[style.id];
    return value === void 0 ? style.defaultValue : value;
  }
  getShapeStyleIfExists(shape, style) {
    const styleKey = this.styleProps[shape.type].get(style);
    if (styleKey === void 0) return void 0;
    return getOwnProperty(shape.props, styleKey);
  }
  getSharedStyles() {
    if (this.isIn("select") && this.getSelectedShapeIds().length > 0) {
      return this._getSelectionSharedStyles();
    }
    const currentTool = this.root.getCurrent();
    const styles = new SharedStyleMap();
    if (!currentTool) return styles;
    if (currentTool.shapeType) {
      if (currentTool.shapeType === "frame" && !this.getShapeUtil("frame").options.showColors) {
        for (const style of this.styleProps[currentTool.shapeType].keys()) {
          if (style.id === "tldraw:color") continue;
          styles.applyValue(style, this.getStyleForNextShape(style));
        }
      } else {
        for (const style of this.styleProps[currentTool.shapeType].keys()) {
          styles.applyValue(style, this.getStyleForNextShape(style));
        }
      }
    }
    return styles;
  }
  getSharedOpacity() {
    if (this.isIn("select") && this.getSelectedShapeIds().length > 0) {
      const shapesToCheck = [];
      const addShape = (shapeId) => {
        const shape = this.getShape(shapeId);
        if (!shape) return;
        if (this.isShapeOfType(shape, "group")) {
          for (const childId of this.getSortedChildIdsForParent(shape.id)) {
            addShape(childId);
          }
        } else {
          shapesToCheck.push(shape);
        }
      };
      for (const shapeId of this.getSelectedShapeIds()) {
        addShape(shapeId);
      }
      let opacity = null;
      for (const shape of shapesToCheck) {
        if (opacity === null) {
          opacity = shape.opacity;
        } else if (opacity !== shape.opacity) {
          return { type: "mixed" };
        }
      }
      if (opacity !== null) return { type: "shared", value: opacity };
    }
    return { type: "shared", value: this.getInstanceState().opacityForNextShape };
  }
  /**
   * Set the opacity for the next shapes. This will effect subsequently created shapes.
   *
   * @example
   * ```ts
   * editor.setOpacityForNextShapes(0.5)
   * ```
   *
   * @param opacity - The opacity to set. Must be a number between 0 and 1 inclusive.
   * @param historyOptions - The history options for the change.
   */
  setOpacityForNextShapes(opacity, historyOptions) {
    this.updateInstanceState({ opacityForNextShape: opacity }, historyOptions);
    return this;
  }
  /**
   * Set the current opacity. This will effect any selected shapes.
   *
   * @example
   * ```ts
   * editor.setOpacityForSelectedShapes(0.5)
   * ```
   *
   * @param opacity - The opacity to set. Must be a number between 0 and 1 inclusive.
   */
  setOpacityForSelectedShapes(opacity) {
    const selectedShapes = this.getSelectedShapes();
    if (selectedShapes.length > 0) {
      const shapesToUpdate = [];
      const addShapeById = (shape) => {
        if (this.isShapeOfType(shape, "group")) {
          const childIds = this.getSortedChildIdsForParent(shape);
          for (const childId of childIds) {
            addShapeById(this.getShape(childId));
          }
        } else {
          shapesToUpdate.push(shape);
        }
      };
      for (const id of selectedShapes) {
        addShapeById(id);
      }
      this.updateShapes(
        shapesToUpdate.map((shape) => {
          return {
            id: shape.id,
            type: shape.type,
            opacity
          };
        })
      );
    }
    return this;
  }
  /**
   * Set the value of a {@link @tldraw/tlschema#StyleProp} for the next shapes. This change will be applied to subsequently created shapes.
   *
   * @example
   * ```ts
   * editor.setStyleForNextShapes(DefaultColorStyle, 'red')
   * editor.setStyleForNextShapes(DefaultColorStyle, 'red', { ephemeral: true })
   * ```
   *
   * @param style - The style to set.
   * @param value - The value to set.
   * @param historyOptions - The history options for the change.
   *
   * @public
   */
  setStyleForNextShapes(style, value, historyOptions) {
    const stylesForNextShape = this.getInstanceState().stylesForNextShape;
    this.updateInstanceState(
      { stylesForNextShape: { ...stylesForNextShape, [style.id]: value } },
      historyOptions
    );
    return this;
  }
  /**
   * Set the value of a {@link @tldraw/tlschema#StyleProp}. This change will be applied to the currently selected shapes.
   *
   * @example
   * ```ts
   * editor.setStyleForSelectedShapes(DefaultColorStyle, 'red')
   * ```
   *
   * @param style - The style to set.
   * @param value - The value to set.
   *
   * @public
   */
  setStyleForSelectedShapes(style, value) {
    const selectedShapes = this.getSelectedShapes();
    if (selectedShapes.length > 0) {
      const updates = [];
      const addShapeById = (shape) => {
        if (this.isShapeOfType(shape, "group")) {
          const childIds = this.getSortedChildIdsForParent(shape.id);
          for (const childId of childIds) {
            addShapeById(this.getShape(childId));
          }
        } else {
          const util = this.getShapeUtil(shape);
          const stylePropKey = this.styleProps[shape.type].get(style);
          if (stylePropKey) {
            const shapePartial = {
              id: shape.id,
              type: shape.type,
              props: { [stylePropKey]: value }
            };
            updates.push({
              util,
              originalShape: shape,
              updatePartial: shapePartial
            });
          }
        }
      };
      for (const shape of selectedShapes) {
        addShapeById(shape);
      }
      this.updateShapes(updates.map(({ updatePartial }) => updatePartial));
    }
    return this;
  }
  /* --------------------- Content -------------------- */
  /** @internal */
  externalAssetContentHandlers = {
    file: null,
    url: null
  };
  /** @internal */
  temporaryAssetPreview = /* @__PURE__ */ new Map();
  /**
   * Register an external asset handler. This handler will be called when the editor needs to
   * create an asset for some external content, like an image/video file or a bookmark URL. For
   * example, the 'file' type handler will be called when a user drops an image onto the canvas.
   *
   * The handler should extract any relevant metadata for the asset, upload it to blob storage
   * using {@link Editor.uploadAsset} if needed, and return the asset with the metadata & uploaded
   * URL.
   *
   * @example
   * ```ts
   * editor.registerExternalAssetHandler('file', myHandler)
   * ```
   *
   * @param type - The type of external content.
   * @param handler - The handler to use for this content type.
   *
   * @public
   */
  registerExternalAssetHandler(type, handler) {
    this.externalAssetContentHandlers[type] = handler;
    return this;
  }
  /**
   * Register a temporary preview of an asset. This is useful for showing a ghost image of
   * something that is being uploaded. Retrieve the placeholder with
   * {@link Editor.getTemporaryAssetPreview}. Placeholders last for 3 minutes by default, but this
   * can be configured using
   *
   * @example
   * ```ts
   * editor.createTemporaryAssetPreview(assetId, file)
   * ```
   *
   * @param assetId - The asset's id.
   * @param file - The raw file.
   *
   * @public
   */
  createTemporaryAssetPreview(assetId, file) {
    if (this.temporaryAssetPreview.has(assetId)) {
      return this.temporaryAssetPreview.get(assetId);
    }
    const objectUrl = URL.createObjectURL(file);
    this.temporaryAssetPreview.set(assetId, objectUrl);
    setTimeout(() => {
      this.temporaryAssetPreview.delete(assetId);
      URL.revokeObjectURL(objectUrl);
    }, this.options.temporaryAssetPreviewLifetimeMs);
    return objectUrl;
  }
  /**
   * Get temporary preview of an asset. This is useful for showing a ghost
   * image of something that is being uploaded.
   *
   * @example
   * ```ts
   * editor.getTemporaryAssetPreview('someId')
   * ```
   *
   * @param assetId - The asset's id.
   *
   * @public
   */
  getTemporaryAssetPreview(assetId) {
    return this.temporaryAssetPreview.get(assetId);
  }
  /**
   * Get an asset for an external asset content type.
   *
   * @example
   * ```ts
   * const asset = await editor.getAssetForExternalContent({ type: 'file', file: myFile })
   * const asset = await editor.getAssetForExternalContent({ type: 'url', url: myUrl })
   * ```
   *
   * @param info - Info about the external content.
   * @returns The asset.
   */
  async getAssetForExternalContent(info) {
    return await this.externalAssetContentHandlers[info.type]?.(info);
  }
  hasExternalAssetHandler(type) {
    return !!this.externalAssetContentHandlers[type];
  }
  /** @internal */
  externalContentHandlers = {
    text: null,
    files: null,
    "file-replace": null,
    embed: null,
    "svg-text": null,
    url: null,
    tldraw: null,
    excalidraw: null
  };
  /**
   * Register an external content handler. This handler will be called when the editor receives
   * external content of the provided type. For example, the 'image' type handler will be called
   * when a user drops an image onto the canvas.
   *
   * @example
   * ```ts
   * editor.registerExternalContentHandler('text', myHandler)
   * ```
   * @example
   * ```ts
   * editor.registerExternalContentHandler<'embed', MyEmbedType>('embed', myHandler)
   * ```
   *
   * @param type - The type of external content.
   * @param handler - The handler to use for this content type.
   *
   * @public
   */
  registerExternalContentHandler(type, handler) {
    this.externalContentHandlers[type] = handler;
    return this;
  }
  /**
   * Handle external content, such as files, urls, embeds, or plain text which has been put into the app, for example by pasting external text or dropping external images onto canvas.
   *
   * @param info - Info about the external content.
   * @param opts - Options for handling external content, including force flag to bypass readonly checks.
   */
  async putExternalContent(info, opts = {}) {
    if (!opts.force && this.getIsReadonly()) return;
    return this.externalContentHandlers[info.type]?.(info);
  }
  /**
   * Handle replacing external content.
   *
   * @param info - Info about the external content.
   * @param opts - Options for handling external content, including force flag to bypass readonly checks.
   */
  async replaceExternalContent(info, opts = {}) {
    if (!opts.force && this.getIsReadonly()) return;
    return this.externalContentHandlers[info.type]?.(info);
  }
  /**
   * Get content that can be exported for the given shape ids.
   *
   * @param shapes - The shapes (or shape ids) to get content for.
   *
   * @returns The exported content.
   *
   * @public
   */
  getContentFromCurrentPage(shapes) {
    const ids = typeof shapes[0] === "string" ? shapes : shapes.map((s) => s.id);
    if (!ids) return;
    if (ids.length === 0) return;
    const shapeIds = this.getShapeAndDescendantIds(ids);
    return withIsolatedShapes(this, shapeIds, (bindingIdsToKeep) => {
      const bindings = [];
      for (const id of bindingIdsToKeep) {
        const binding = this.getBinding(id);
        if (!binding) continue;
        bindings.push(binding);
      }
      const rootShapeIds = [];
      const shapes2 = [];
      for (const shapeId of shapeIds) {
        const shape = this.getShape(shapeId);
        if (!shape) continue;
        const isRootShape = !shapeIds.has(shape.parentId);
        if (isRootShape) {
          const pageTransform = this.getShapePageTransform(shape.id);
          const pagePoint = pageTransform.point();
          shapes2.push({
            ...shape,
            x: pagePoint.x,
            y: pagePoint.y,
            rotation: pageTransform.rotation(),
            parentId: this.getCurrentPageId()
          });
          rootShapeIds.push(shape.id);
        } else {
          shapes2.push(shape);
        }
      }
      const assets = [];
      const seenAssetIds = /* @__PURE__ */ new Set();
      for (const shape of shapes2) {
        if (!("assetId" in shape.props)) continue;
        const assetId = shape.props.assetId;
        if (!assetId || seenAssetIds.has(assetId)) continue;
        seenAssetIds.add(assetId);
        const asset = this.getAsset(assetId);
        if (!asset) continue;
        assets.push(asset);
      }
      const users = [];
      const seenUserIds = /* @__PURE__ */ new Set();
      for (const userId of this._getReferencedUserIds(shapes2)) {
        const recordId = createUserId(userId);
        if (seenUserIds.has(recordId)) continue;
        seenUserIds.add(recordId);
        const user = this.store.get(recordId);
        if (user) users.push(user);
      }
      return {
        schema: this.store.schema.serialize(),
        shapes: shapes2,
        rootShapeIds,
        bindings,
        assets,
        users
      };
    });
  }
  async resolveAssetsInContent(content) {
    if (!content) return void 0;
    const assets = [];
    await Promise.allSettled(
      content.assets.map(async (asset) => {
        if ((asset.type === "image" || asset.type === "video") && !asset.props.src?.startsWith("data:image") && !asset.props.src?.startsWith("data:video") && !asset.props.src?.startsWith("http")) {
          const assetWithDataUrl = structuredClone(asset);
          const objectUrl = await this.store.props.assets.resolve(asset, {
            screenScale: 1,
            steppedScreenScale: 1,
            dpr: 1,
            networkEffectiveType: null,
            shouldResolveToOriginal: true
          });
          assetWithDataUrl.props.src = await FileHelpers.blobToDataUrl(
            await fetch(objectUrl).then((r) => r.blob())
          );
          assets.push(assetWithDataUrl);
        } else {
          assets.push(asset);
        }
      })
    );
    content.assets = assets;
    return content;
  }
  /**
   * Place content into the editor.
   *
   * @param content - The content.
   * @param opts - Options for placing the content.
   *
   * @public
   */
  putContentOntoCurrentPage(content, opts = {}) {
    if (this.getIsReadonly()) return this;
    if (!content.schema) {
      throw Error("Could not put content:\ncontent is missing a schema.");
    }
    const { select = false, preserveIds = false, preservePosition = false } = opts;
    let { point = void 0 } = opts;
    const currentPageId = this.getCurrentPageId();
    const { rootShapeIds } = content;
    const assets = [];
    const shapes = [];
    const bindings = [];
    const users = [];
    const store = {
      store: {
        ...Object.fromEntries(content.assets.map((asset) => [asset.id, asset])),
        ...Object.fromEntries(content.shapes.map((shape) => [shape.id, shape])),
        ...Object.fromEntries(
          content.bindings?.map((bindings2) => [bindings2.id, bindings2]) ?? []
        ),
        ...Object.fromEntries(content.users?.map((user) => [user.id, user]) ?? [])
      },
      schema: content.schema
    };
    const result = this.store.schema.migrateStoreSnapshot(store);
    if (result.type === "error") {
      throw Error("Could not put content: could not migrate content");
    }
    for (const record of Object.values(result.value)) {
      switch (record.typeName) {
        case "asset": {
          assets.push(record);
          break;
        }
        case "shape": {
          shapes.push(record);
          break;
        }
        case "binding": {
          bindings.push(record);
          break;
        }
        case "user": {
          users.push(record);
          break;
        }
      }
    }
    if (users.length > 0) {
      const existingUserIds = new Set(
        this.store.allRecords().filter((r) => r.typeName === "user").map((r) => r.id)
      );
      const usersToCreate = users.filter((u) => !existingUserIds.has(u.id));
      if (usersToCreate.length > 0) {
        this.store.put(usersToCreate);
      }
    }
    const shapeIdMap = new Map(
      preserveIds ? shapes.map((shape) => [shape.id, shape.id]) : shapes.map((shape) => [shape.id, createShapeId()])
    );
    const bindingIdMap = new Map(
      preserveIds ? bindings.map((binding) => [binding.id, binding.id]) : bindings.map((binding) => [binding.id, createBindingId()])
    );
    let pasteParentId = currentPageId;
    const shapesById = new Map(shapes.map((s) => [s.id, s]));
    const rootShapesFromContent = compact(rootShapeIds.map((id) => shapesById.get(id)));
    if (point) {
      if (rootShapesFromContent.length > 0) {
        const targetParent = this.getShapeAtPoint(point, {
          hitInside: true,
          hitFrameInside: true,
          hitLocked: true,
          filter: (shape) => {
            const util = this.getShapeUtil(shape);
            return rootShapesFromContent.every(
              (rootShape) => util.canReceiveNewChildrenOfType?.(shape, rootShape.type)
            );
          }
        });
        pasteParentId = targetParent?.id ?? currentPageId;
      }
    } else if (!preservePosition) {
      const selectedShapes = this.getSelectedShapes();
      let selectedParent = null;
      const canAcceptAll = (candidate) => {
        const util = this.getShapeUtil(candidate);
        return rootShapesFromContent.every(
          (rs) => util.canReceiveNewChildrenOfType?.(candidate, rs.type)
        );
      };
      for (const shape of selectedShapes) {
        const candidate = canAcceptAll(shape) ? shape : this.findShapeAncestor(shape, canAcceptAll) ?? (isShapeId(shape.parentId) ? this.getShape(shape.parentId) : null);
        if (!candidate) {
          selectedParent = null;
          break;
        }
        if (!selectedParent) {
          selectedParent = candidate;
        } else if (selectedParent.id !== candidate.id) {
          const spAncestors = this.getShapeAncestors(selectedParent);
          if (canAcceptAll(selectedParent)) spAncestors.push(selectedParent);
          const acceptingAncestors = spAncestors.filter(canAcceptAll);
          const candidateAncestorIds = /* @__PURE__ */ new Set([
            candidate.id,
            ...this.getShapeAncestors(candidate).map((a) => a.id)
          ]);
          let common = null;
          for (let i = acceptingAncestors.length - 1; i >= 0; i--) {
            if (candidateAncestorIds.has(acceptingAncestors[i].id)) {
              common = acceptingAncestors[i];
              break;
            }
          }
          selectedParent = common;
          if (!selectedParent) break;
        }
      }
      if (selectedParent && shapeIdMap.has(selectedParent.id)) {
        selectedParent = null;
      }
      if (selectedParent) {
        pasteParentId = selectedParent.id;
      }
    }
    let index = this.getHighestIndexForParent(pasteParentId);
    const rootShapes = [];
    const newShapes = shapes.map((oldShape) => {
      const newId = shapeIdMap.get(oldShape.id);
      let newShape = { ...oldShape, id: newId };
      if (!preserveIds) {
        newShape = this.getShapeUtil(newShape).onBeforeDuplicate?.(oldShape, newShape) ?? newShape;
      }
      if (rootShapeIds.includes(oldShape.id)) {
        newShape.parentId = currentPageId;
        rootShapes.push(newShape);
      }
      if (shapeIdMap.has(newShape.parentId)) {
        newShape.parentId = shapeIdMap.get(oldShape.parentId);
      } else {
        rootShapeIds.push(newShape.id);
        newShape.index = index;
        index = getIndexAbove(index);
      }
      return newShape;
    });
    if (newShapes.length + this.getCurrentPageShapeIds().size > this.options.maxShapesPerPage) {
      alertMaxShapes(this);
      return this;
    }
    const newBindings = bindings.map(
      (oldBinding) => ({
        ...oldBinding,
        id: assertExists(bindingIdMap.get(oldBinding.id)),
        fromId: assertExists(shapeIdMap.get(oldBinding.fromId)),
        toId: assertExists(shapeIdMap.get(oldBinding.toId))
      })
    );
    const assetsToCreate = [];
    const assetsToUpdate = [];
    for (const asset of assets) {
      if (this.store.has(asset.id)) {
        continue;
      }
      if (asset.type === "image" && asset.props.src?.startsWith("data:image") || asset.type === "video" && asset.props.src?.startsWith("data:video")) {
        assetsToUpdate.push(structuredClone(asset));
        asset.props.src = null;
      }
      assetsToCreate.push(asset);
    }
    Promise.allSettled(
      assetsToUpdate.map(async (asset) => {
        const file = await dataUrlToFile(
          asset.props.src,
          asset.props.name,
          asset.props.mimeType ?? "image/png"
        );
        const newAsset = await this.getAssetForExternalContent({
          type: "file",
          file,
          assetId: asset.id
        });
        if (!newAsset) {
          this.deleteAssets([asset.id]);
          return;
        }
        this.updateAssets([{ ...newAsset, id: asset.id }]);
      })
    );
    this.run(() => {
      if (assetsToCreate.length > 0) this.createAssets(assetsToCreate);
      this.createShapes(newShapes);
      this.createBindings(newBindings);
      if (select) this.select(...rootShapes.map((s) => s.id));
      if (pasteParentId !== currentPageId) {
        this.reparentShapes(
          rootShapes.map((s) => s.id),
          pasteParentId
        );
      }
      const rootBounds = Box.Common(compact(rootShapes.map((s) => this.getShapePageBounds(s.id))));
      if (point === void 0) {
        if (!isPageId(pasteParentId)) {
          const shape = this.getShape(pasteParentId);
          point = Mat.applyToPoint(
            this.getShapePageTransform(shape),
            this.getShapeGeometry(shape).bounds.center
          );
        } else if (preservePosition) {
          point = rootBounds.center;
        } else {
          const viewportPageBounds = this.getViewportPageBounds();
          const anyOverlap = rootShapes.some((s) => {
            const b = this.getShapePageBounds(s.id);
            return b && viewportPageBounds.collides(b);
          });
          point = anyOverlap ? rootBounds.center : viewportPageBounds.center;
        }
      }
      const pageCenter = Box.Common(
        compact(rootShapes.map(({ id }) => this.getShapePageBounds(id)))
      ).center;
      const offset = Vec.Sub(point, pageCenter);
      if (offset.x !== 0 || offset.y !== 0) {
        this.updateShapes(
          rootShapes.map(({ id }) => {
            const s = this.getShape(id);
            const localRotation = this.getShapeParentTransform(id).decompose().rotation;
            const localDelta = Vec.Rot(offset, -localRotation);
            return { id: s.id, type: s.type, x: s.x + localDelta.x, y: s.y + localDelta.y };
          })
        );
      }
      if (isPageId(pasteParentId)) {
        const currentRootShapes = compact(rootShapes.map((s) => this.getShape(s.id)));
        const { reparenting } = getDroppedShapesToNewParents(
          this,
          currentRootShapes,
          (shape, parent) => {
            if (shapeIdMap.has(parent.id)) return false;
            const shapeBounds = this.getShapePageBounds(shape);
            const parentBounds = this.getShapePageBounds(parent);
            if (!shapeBounds || !parentBounds) return false;
            return parentBounds.containsPoint(shapeBounds.center);
          }
        );
        reparenting.forEach((childrenToReparent, newParentId) => {
          if (childrenToReparent.length === 0) return;
          this.reparentShapes(
            childrenToReparent.map((s) => s.id),
            newParentId
          );
        });
      }
      const newShapeIdSet = new Set(newShapes.map((s) => s.id));
      const shapesToKickout = rootShapes.map((s) => s.id).filter((id) => {
        const shape = this.getShape(id);
        if (!shape) return false;
        if (isPageId(shape.parentId)) return false;
        const children = this.getSortedChildIdsForParent(id);
        return !children.some((childId) => newShapeIdSet.has(childId));
      });
      if (shapesToKickout.length > 0) {
        kickoutOccludedShapes(this, shapesToKickout);
      }
    });
    return this;
  }
  /**
   * Get an exported SVG element of the given shapes.
   *
   * @param shapes - The shapes (or shape ids) to export.
   * @param opts - Options for the export.
   *
   * @returns The SVG element.
   *
   * @public
   */
  async getSvgElement(shapes, opts = {}) {
    const ids = shapes.length === 0 ? this.getCurrentPageShapeIdsSorted() : typeof shapes[0] === "string" ? shapes : shapes.map((s) => s.id);
    if (ids.length === 0) return void 0;
    await this.fonts.loadRequiredFontsForCurrentPage(this.options.maxFontsToLoadBeforeRender);
    return exportToSvg(this, ids, opts);
  }
  /**
   * Get an exported SVG string of the given shapes.
   *
   * @param shapes - The shapes (or shape ids) to export.
   * @param opts - Options for the export.
   *
   * @returns The SVG element.
   *
   * @public
   */
  async getSvgString(shapes, opts = {}) {
    const result = await this.getSvgElement(shapes, opts);
    if (!result) return void 0;
    const serializer = new XMLSerializer();
    return {
      svg: serializer.serializeToString(result.svg),
      width: result.width,
      height: result.height,
      trimPadding: result.trimPadding
    };
  }
  /**
   * Get an exported image of the given shapes.
   *
   * @param shapes - The shapes (or shape ids) to export.
   * @param opts - Options for the export.
   *
   * @returns A blob of the image.
   * @public
   */
  async toImage(shapes, opts = {}) {
    const withDefaults = {
      format: "png",
      scale: 1,
      pixelRatio: opts.format === "svg" ? void 0 : 2,
      ...opts
    };
    const result = await this.getSvgString(shapes, withDefaults);
    if (!result) throw new Error("Could not create SVG");
    switch (withDefaults.format) {
      case "svg": {
        let svg = result.svg;
        let w = result.width;
        let h = result.height;
        if (result.trimPadding > 0) {
          const trimmed = await trimSvgToContent(svg, {
            width: w,
            height: h,
            trimPadding: result.trimPadding,
            scale: withDefaults.scale
          });
          if (trimmed) {
            svg = trimmed.svg;
            w = trimmed.width;
            h = trimmed.height;
          }
        }
        return {
          blob: new Blob([svg], { type: "image/svg+xml" }),
          width: w,
          height: h
        };
      }
      case "jpeg":
      case "png":
      case "webp": {
        const imageResult = await getSvgAsImageWithOptions(result.svg, {
          type: withDefaults.format,
          quality: withDefaults.quality,
          pixelRatio: withDefaults.pixelRatio,
          width: result.width,
          height: result.height,
          trimPadding: result.trimPadding,
          scale: withDefaults.scale
        });
        if (!imageResult) {
          throw new Error("Could not construct image.");
        }
        return imageResult;
      }
      default: {
        exhaustiveSwitchError(withDefaults.format);
      }
    }
  }
  /**
   * Get an exported image of the given shapes as a data URL.
   *
   * @param shapes - The shapes (or shape ids) to export.
   * @param opts - Options for the export.
   *
   * @returns A data URL of the image.
   * @public
   */
  async toImageDataUrl(shapes, opts = {}) {
    const { blob, width, height } = await this.toImage(shapes, opts);
    return {
      url: await FileHelpers.blobToDataUrl(blob),
      width,
      height
    };
  }
  /* --------------------- Events --------------------- */
  /**
   * Dispatch a cancel event.
   *
   * @example
   * ```ts
   * editor.cancel()
   * ```
   *
   * @public
   */
  cancel() {
    this.dispatch({ type: "misc", name: "cancel" });
    return this;
  }
  /**
   * Dispatch an interrupt event.
   *
   * @example
   * ```ts
   * editor.interrupt()
   * ```
   *
   * @public
   */
  interrupt() {
    this.dispatch({ type: "misc", name: "interrupt" });
    return this;
  }
  /**
   * Dispatch a complete event.
   *
   * @example
   * ```ts
   * editor.complete()
   * ```
   *
   * @public
   */
  complete() {
    this.dispatch({ type: "misc", name: "complete" });
    return this;
  }
  /**
   * Dispatch a pointer move event in the current position of the pointer. This is useful when
   * external circumstances have changed (e.g. the camera moved or a shape was moved) and you want
   * the current interaction to respond to that change.
   *
   * @example
   * ```ts
   * editor.updatePointer()
   * ```
   *
   * @param options - The options for updating the pointer.
   * @returns The editor instance.
   * @public
   */
  updatePointer(options) {
    const event = {
      type: "pointer",
      target: "canvas",
      name: "pointer_move",
      point: options?.point ?? // weird but true: what `inputs` calls screen-space is actually viewport space. so
      // we need to convert back into true screen space first. we should fix this...
      Vec.Add(
        this.inputs.getCurrentScreenPoint(),
        this.store.unsafeGetWithoutCapture(TLINSTANCE_ID).screenBounds
      ),
      pointerId: options?.pointerId ?? 0,
      button: options?.button ?? 0,
      isPen: options?.isPen ?? this.inputs.getIsPen(),
      shiftKey: options?.shiftKey ?? this.inputs.getShiftKey(),
      altKey: options?.altKey ?? this.inputs.getAltKey(),
      ctrlKey: options?.ctrlKey ?? this.inputs.getCtrlKey(),
      metaKey: options?.metaKey ?? this.inputs.getMetaKey(),
      accelKey: false
    };
    event.accelKey = options?.accelKey ?? this.inputs.getAccelKey();
    if (options?.immediate) {
      this._flushEventForTick(event);
    } else {
      this.dispatch(event);
    }
    return this;
  }
  /**
   * Puts the editor into focused mode.
   *
   * This makes the editor eligible to receive keyboard events and some pointer events (move, wheel).
   *
   * @example
   * ```ts
   * editor.focus()
   * ```
   *
   * By default this also dispatches a 'focus' event to the container element. To prevent this, pass `focusContainer: false`.
   *
   * @example
   * ```ts
   * editor.focus({ focusContainer: false })
   * ```
   *
   * @public
   */
  focus({ focusContainer = true } = {}) {
    if (this.getIsFocused()) return this;
    if (focusContainer) this.focusManager.focus();
    this.updateInstanceState({ isFocused: true });
    return this;
  }
  /**
   * Switches off the editor's focused mode.
   *
   * This makes the editor ignore keyboard events and some pointer events (move, wheel).
   *
   * @example
   * ```ts
   * editor.blur()
   * ```
   * By default this also dispatches a 'blur' event to the container element. To prevent this, pass `blurContainer: false`.
   *
   * @example
   * ```ts
   * editor.blur({ blurContainer: false })
   * ```
   *
   * @public
   */
  blur({ blurContainer = true } = {}) {
    if (!this.getIsFocused()) return this;
    this.focusManager.blur({ blurContainer });
    this.updateInstanceState({ isFocused: false });
    return this;
  }
  getIsFocused() {
    return this.getInstanceState().isFocused;
  }
  getIsReadonly() {
    return this.getInstanceState().isReadonly;
  }
  /**
   * @public
   * @returns a snapshot of the store's UI and document state
   */
  getSnapshot() {
    return getSnapshot(this.store);
  }
  /**
   * Loads a snapshot into the editor.
   * @param snapshot - The snapshot to load.
   * @param opts - The options for loading the snapshot.
   * @returns
   */
  loadSnapshot(snapshot, opts) {
    loadSnapshot(this.store, snapshot, opts);
    return this;
  }
  _zoomToFitPageContentAt100Percent() {
    const bounds = this.getCurrentPageBounds();
    if (bounds) {
      this.zoomToBounds(bounds, { immediate: true, targetZoom: this.getBaseZoom() });
    }
  }
  _navigateToDeepLink(deepLink) {
    this.run(() => {
      switch (deepLink.type) {
        case "page": {
          const page = this.getPage(deepLink.pageId);
          if (page) {
            this.setCurrentPage(page);
          }
          this._zoomToFitPageContentAt100Percent();
          return;
        }
        case "shapes": {
          const allShapes = compact(deepLink.shapeIds.map((id) => this.getShape(id)));
          const byPage = {};
          for (const shape of allShapes) {
            const pageId2 = this.getAncestorPageId(shape);
            if (!pageId2) continue;
            byPage[pageId2] ??= [];
            byPage[pageId2].push(shape);
          }
          const [pageId, shapes] = Object.entries(byPage).sort(
            ([_, a], [__, b]) => b.length - a.length
          )[0] ?? ["", []];
          if (!pageId || !shapes.length) {
            this._zoomToFitPageContentAt100Percent();
          } else {
            this.setCurrentPage(pageId);
            const bounds = Box.Common(shapes.map((s) => this.getShapePageBounds(s)));
            this.zoomToBounds(bounds, { immediate: true, targetZoom: this.getBaseZoom() });
          }
          return;
        }
        case "viewport": {
          if (deepLink.pageId) {
            if (!this.getPage(deepLink.pageId)) {
              this._zoomToFitPageContentAt100Percent();
              return;
            }
            this.setCurrentPage(deepLink.pageId);
          }
          this.zoomToBounds(deepLink.bounds, { immediate: true, inset: 0 });
          return;
        }
        default:
          exhaustiveSwitchError(deepLink);
      }
    });
  }
  /**
   * Handles navigating to the content specified by the query param in the given URL.
   *
   * Use {@link Editor.createDeepLink} to create a URL with a deep link query param.
   *
   * If no URL is provided, it will look for the param in the current `window.location.href`.
   *
   * @example
   * ```ts
   * editor.navigateToDeepLink()
   * ```
   *
   * The default parameter name is 'd'. You can override this by providing the `param` option.
   *
   * @example
   * ```ts
   * // disable page parameter and change viewport parameter to 'c'
   * editor.navigateToDeepLink({
   *   param: 'x',
   *   url: 'https://my-app.com/my-document?x=200.12.454.23.xyz123',
   * })
   * ```
   *
   * @param opts - Options for loading the state from the URL.
   */
  navigateToDeepLink(opts) {
    if (opts && "type" in opts) {
      this._navigateToDeepLink(opts);
      return this;
    }
    const url = new URL(opts?.url ?? window.location.href);
    const deepLinkString = url.searchParams.get(opts?.param ?? "d");
    if (!deepLinkString) {
      this._zoomToFitPageContentAt100Percent();
      return this;
    }
    try {
      this._navigateToDeepLink(parseDeepLinkString(deepLinkString));
    } catch (e) {
      console.warn(e);
      this._zoomToFitPageContentAt100Percent();
    }
    return this;
  }
  /**
   * Turns the given URL into a deep link by adding a query parameter.
   *
   * e.g. `https://my-app.com/my-document?d=100.100.200.200.xyz123`
   *
   * If no URL is provided, it will use the current `window.location.href`.
   *
   * @example
   * ```ts
   * // create a deep link to the current page + viewport
   * navigator.clipboard.writeText(editor.createDeepLink())
   * ```
   *
   * You can link to a particular set of shapes by providing a `to` parameter.
   *
   * @example
   * ```ts
   * // create a deep link to the set of currently selected shapes
   * navigator.clipboard.writeText(editor.createDeepLink({
   *   to: { type: 'selection', shapeIds: editor.getSelectedShapeIds() }
   * }))
   * ```
   *
   * The default query param is 'd'. You can override this by providing a `param` parameter.
   *
   * @example
   * ```ts
   * // Use `x` as the param name instead
   * editor.createDeepLink({ param: 'x' })
   * ```
   *
   * @param opts - Options for adding the state to the URL.
   * @returns the updated URL
   */
  createDeepLink(opts) {
    const url = new URL(opts?.url ?? window.location.href);
    url.searchParams.set(
      opts?.param ?? "d",
      createDeepLinkString(
        opts?.to ?? {
          type: "viewport",
          pageId: this.options.maxPages === 1 ? void 0 : this.getCurrentPageId(),
          bounds: this.getViewportPageBounds()
        }
      )
    );
    return url;
  }
  /**
   * Register a listener for changes to a deep link for the current document.
   *
   * You'll typically want to use this indirectly via the {@link TldrawEditorBaseProps.deepLinks} prop on the `<Tldraw />` component.
   *
   * By default this will update `window.location` in place, but you can provide a custom callback
   * to handle state changes on your own.
   *
   * @example
   * ```ts
   * editor.registerDeepLinkListener({
   *   onChange(url) {
   *     window.history.replaceState({}, document.title, url.toString())
   *   }
   * })
   * ```
   *
   * You can also provide a custom URL to update, in which case you must also provide `onChange`.
   *
   * @example
   * ```ts
   * editor.registerDeepLinkListener({
   *   getUrl: () => `https://my-app.com/my-document`,
   *   onChange(url) {
   *     setShareUrl(url.toString())
   *   }
   * })
   * ```
   *
   * By default this will update with a debounce interval of 500ms, but you can provide a custom interval.
   *
   * @example
   * ```ts
   * editor.registerDeepLinkListener({ debounceMs: 1000 })
   * ```
   * The default parameter name is `d`. You can override this by providing a `param` option.
   *
   * @example
   * ```ts
   * editor.registerDeepLinkListener({ param: 'x' })
   * ```
   * @param opts - Options for setting up the listener.
   * @returns a function that will stop the listener.
   */
  registerDeepLinkListener(opts) {
    if (opts?.getUrl && !opts?.onChange) {
      throw Error(
        "[tldraw:urlStateSync] If you specify getUrl, you must also specify the onChange callback."
      );
    }
    const url$ = computed("url with state", () => {
      const url = opts?.getUrl?.(this) ?? window.location.href;
      const urlWithState = this.createDeepLink({
        param: opts?.param,
        url,
        to: opts?.getTarget?.(this)
      });
      return urlWithState.toString();
    });
    const announceChange = opts?.onChange ?? (() => {
      const url = this.createDeepLink({
        param: opts?.param,
        to: opts?.getTarget?.(this)
      });
      window.history.replaceState({}, this.getContainerDocument().title, url.toString());
    });
    const scheduleEffect = debounce((execute) => execute(), opts?.debounceMs ?? 500);
    const unlisten = react(
      "update url on state change",
      () => announceChange(new URL(url$.get()), this),
      { scheduleEffect }
    );
    return () => {
      unlisten();
      scheduleEffect.cancel();
    };
  }
  /**
   * A manager for recording multiple click events.
   *
   * @internal
   */
  _clickManager = new ClickManager(this);
  /**
   * Prevent a double click event from firing the next time the user clicks
   *
   * @public
   */
  cancelDoubleClick() {
    this._clickManager.cancelDoubleClickTimeout();
  }
  /**
   * The previous cursor. Used for restoring the cursor after pan events.
   *
   * @internal
   */
  _prevCursor = "default";
  /** @internal */
  _shiftKeyTimeout = -1;
  _setShiftKeyTimeout() {
    this.inputs.setShiftKey(false);
    this.dispatch({
      type: "keyboard",
      name: "key_up",
      key: "Shift",
      shiftKey: this.inputs.getShiftKey(),
      ctrlKey: this.inputs.getCtrlKey(),
      altKey: this.inputs.getAltKey(),
      metaKey: this.inputs.getMetaKey(),
      accelKey: this.inputs.getAccelKey(),
      code: "ShiftLeft"
    });
  }
  /** @internal */
  _altKeyTimeout = -1;
  _setAltKeyTimeout() {
    this.inputs.setAltKey(false);
    this.dispatch({
      type: "keyboard",
      name: "key_up",
      key: "Alt",
      shiftKey: this.inputs.getShiftKey(),
      ctrlKey: this.inputs.getCtrlKey(),
      altKey: this.inputs.getAltKey(),
      metaKey: this.inputs.getMetaKey(),
      accelKey: this.inputs.getAccelKey(),
      code: "AltLeft"
    });
  }
  /** @internal */
  _ctrlKeyTimeout = -1;
  _setCtrlKeyTimeout() {
    this.inputs.setCtrlKey(false);
    this.dispatch({
      type: "keyboard",
      name: "key_up",
      key: "Ctrl",
      shiftKey: this.inputs.getShiftKey(),
      ctrlKey: this.inputs.getCtrlKey(),
      altKey: this.inputs.getAltKey(),
      metaKey: this.inputs.getMetaKey(),
      accelKey: this.inputs.getAccelKey(),
      code: "ControlLeft"
    });
  }
  /** @internal */
  _metaKeyTimeout = -1;
  _setMetaKeyTimeout() {
    this.inputs.setMetaKey(false);
    this.dispatch({
      type: "keyboard",
      name: "key_up",
      key: "Meta",
      shiftKey: this.inputs.getShiftKey(),
      ctrlKey: this.inputs.getCtrlKey(),
      altKey: this.inputs.getAltKey(),
      metaKey: this.inputs.getMetaKey(),
      accelKey: this.inputs.getAccelKey(),
      code: "MetaLeft"
    });
  }
  /** @internal */
  _restoreToolId = "select";
  /** @internal */
  _didPinch = false;
  /** @internal */
  _selectedShapeIdsAtPointerDown = [];
  /**
   * Whether `_selectedShapeIdsAtPointerDown` holds a pre-gesture selection
   * captured by a `pointer_down` (the touch path) that a following pinch
   * should restore. False when no pointer_down preceded the pinch (the
   * Safari trackpad path uses gesture events), in which case `pinch_start`
   * captures the live selection instead.
   * @internal
   */
  _didCaptureSelectionAtPointerDown = false;
  /** @internal */
  _longPressTimeout = -1;
  /** @internal */
  capturedPointerId = null;
  /** @internal */
  performanceTracker;
  /** @internal */
  performanceTrackerTimeout = -1;
  /** @internal */
  handledEvents = /* @__PURE__ */ new WeakSet();
  /**
   * In tldraw, events are sometimes handled by multiple components. For example, the shapes might
   * have events, but the canvas handles events too. The way that the canvas handles events can
   * interfere with the with the shapes event handlers - for example, it calls `.preventDefault()`
   * on `pointerDown`, which also prevents `click` events from firing on the shapes.
   *
   * You can use `.stopPropagation()` to prevent the event from propagating to the rest of the
   * DOM, but that can impact non-tldraw event handlers set up elsewhere. By using
   * `markEventAsHandled`, you'll stop other parts of tldraw from handling the event without
   * impacting other, non-tldraw event handlers. See also {@link Editor.wasEventAlreadyHandled}.
   *
   * @public
   */
  markEventAsHandled(e) {
    const nativeEvent = "nativeEvent" in e ? e.nativeEvent : e;
    this.handledEvents.add(nativeEvent);
  }
  /**
   * Checks if an event has already been handled. See {@link Editor.markEventAsHandled}.
   *
   * @public
   */
  wasEventAlreadyHandled(e) {
    const nativeEvent = "nativeEvent" in e ? e.nativeEvent : e;
    return this.handledEvents.has(nativeEvent);
  }
  /**
   * Dispatch an event to the editor.
   *
   * @example
   * ```ts
   * editor.dispatch(myPointerEvent)
   * ```
   *
   * @param info - The event info.
   *
   * @public
   */
  dispatch(info) {
    this._pendingEventsForNextTick.push(info);
    if (!(info.type === "pointer" && info.name === "pointer_move" || info.type === "wheel" || info.type === "pinch")) {
      this._flushEventsForTick(0);
    }
    return this;
  }
  _pendingEventsForNextTick = [];
  _flushEventsForTick(elapsed) {
    this.run(() => {
      if (this._pendingEventsForNextTick.length > 0) {
        const events = [...this._pendingEventsForNextTick];
        this._pendingEventsForNextTick.length = 0;
        for (const info of events) {
          this._flushEventForTick(info);
        }
      }
      if (elapsed > 0) {
        this.root.handleEvent({ type: "misc", name: "tick", elapsed });
      }
      this.scribbles.tick(elapsed);
    });
  }
  _flushEventForTick(info) {
    if (this.getCrashingError()) return this;
    this.emit("before-event", info);
    const { inputs } = this;
    const { type } = info;
    if (info.type === "misc") {
      if (info.name === "cancel" || info.name === "complete") {
        this.inputs.setIsDragging(false);
        if (this.inputs.getIsPanning()) {
          this.inputs.setIsPanning(false);
          this.inputs.setIsSpacebarPanning(false);
          this.setCursor({ type: this._prevCursor, rotation: 0 });
        }
      }
      this.root.handleEvent(info);
      this.emit("event", info);
      return;
    }
    if (info.shiftKey) {
      clearTimeout(this._shiftKeyTimeout);
      this._shiftKeyTimeout = -1;
      inputs.setShiftKey(true);
    } else if (!info.shiftKey && inputs.getShiftKey() && this._shiftKeyTimeout === -1) {
      this._shiftKeyTimeout = this.timers.setTimeout(this._setShiftKeyTimeout, 150);
    }
    if (info.altKey) {
      clearTimeout(this._altKeyTimeout);
      this._altKeyTimeout = -1;
      inputs.setAltKey(true);
    } else if (!info.altKey && inputs.getAltKey() && this._altKeyTimeout === -1) {
      this._altKeyTimeout = this.timers.setTimeout(this._setAltKeyTimeout, 150);
    }
    if (info.ctrlKey) {
      clearTimeout(this._ctrlKeyTimeout);
      this._ctrlKeyTimeout = -1;
      inputs.setCtrlKey(true);
    } else if (!info.ctrlKey && inputs.getCtrlKey() && this._ctrlKeyTimeout === -1) {
      this._ctrlKeyTimeout = this.timers.setTimeout(this._setCtrlKeyTimeout, 150);
    }
    if (info.metaKey && info.name !== "key_up") {
      clearTimeout(this._metaKeyTimeout);
      this._metaKeyTimeout = -1;
      inputs.setMetaKey(true);
    } else if (!info.metaKey && inputs.getMetaKey() && this._metaKeyTimeout === -1) {
      this._metaKeyTimeout = this.timers.setTimeout(this._setMetaKeyTimeout, 150);
    }
    if (!inputs.getIsPointing()) {
      inputs.setIsDragging(false);
    }
    const instanceState = this.store.unsafeGetWithoutCapture(TLINSTANCE_ID);
    const pageState = this.store.get(this._getCurrentPageStateId());
    const cameraOptions = this._cameraOptions.__unsafe__getWithoutCapture();
    switch (type) {
      case "pinch": {
        if (cameraOptions.isLocked) return;
        clearTimeout(this._longPressTimeout);
        this.inputs.updateFromEvent(info);
        switch (info.name) {
          case "pinch_start": {
            if (inputs.getIsPinching()) return;
            if (!inputs.getIsEditing()) {
              if (!this._didCaptureSelectionAtPointerDown) {
                this._selectedShapeIdsAtPointerDown = [...pageState.selectedShapeIds];
              }
              this._didPinch = true;
              inputs.setIsPinching(true);
              this.interrupt();
              if (this._didCaptureSelectionAtPointerDown) {
                this.setSelectedShapes(this._selectedShapeIdsAtPointerDown);
              }
            }
            this.emit("event", info);
            return;
          }
          case "pinch": {
            if (!inputs.getIsPinching()) return;
            const {
              point: { z = 1 },
              delta: { x: dx, y: dy }
            } = info;
            const { x, y } = Vec.SubXY(
              info.point,
              instanceState.screenBounds.x,
              instanceState.screenBounds.y
            );
            this.stopCameraAnimation();
            if (instanceState.followingUserId) {
              this.stopFollowingUser();
            }
            const { x: cx, y: cy, z: cz } = unsafe__withoutCapture(() => this.getCamera());
            const { panSpeed } = cameraOptions;
            this._setCamera(
              new Vec(
                cx + dx * panSpeed / cz - x / cz + x / z,
                cy + dy * panSpeed / cz - y / cz + y / z,
                z
              ),
              { immediate: true }
            );
            this.performance._notifyCameraOperation("zooming");
            this.emit("event", info);
            return;
          }
          case "pinch_end": {
            if (!inputs.getIsPinching()) return this;
            inputs.setIsPinching(false);
            const { _selectedShapeIdsAtPointerDown: shapesToReselect } = this;
            this.setSelectedShapes(this._selectedShapeIdsAtPointerDown);
            this._selectedShapeIdsAtPointerDown = [];
            this._didCaptureSelectionAtPointerDown = false;
            if (this._didPinch) {
              this._didPinch = false;
              if (shapesToReselect.length > 0) {
                this.once("tick", () => {
                  if (!this._didPinch) {
                    this.setSelectedShapes(shapesToReselect);
                  }
                });
              }
            }
            this.emit("event", info);
            return;
          }
        }
      }
      case "wheel": {
        if (cameraOptions.isLocked) return;
        this.inputs.updateFromEvent(info);
        const { panSpeed, zoomSpeed } = cameraOptions;
        let wheelBehavior = cameraOptions.wheelBehavior;
        const inputMode = this.user.getUserPreferences().inputMode;
        if (inputMode !== null) {
          wheelBehavior = inputMode === "trackpad" ? "pan" : "zoom";
        }
        if (wheelBehavior !== "none") {
          this.stopCameraAnimation();
          if (instanceState.followingUserId) {
            this.stopFollowingUser();
          }
          const { x: cx, y: cy, z: cz } = unsafe__withoutCapture(() => this.getCamera());
          const { x: dx, y: dy, z: dz = 0 } = info.delta;
          let behavior = wheelBehavior;
          if (info.ctrlKey) behavior = wheelBehavior === "pan" ? "zoom" : "pan";
          switch (behavior) {
            case "zoom": {
              const { x, y } = this.inputs.getCurrentScreenPoint();
              let delta = dz;
              if (wheelBehavior === "zoom") {
                if (Math.abs(dy) > 10) {
                  delta = 10 * Math.sign(dy) / 100;
                } else {
                  delta = dy / 100;
                }
              }
              const isZoomDirectionInverted = (this.user.getUserPreferences().isZoomDirectionInverted && inputMode === "mouse") ?? false;
              const deltaValue = delta ?? 0;
              const finalDelta = isZoomDirectionInverted ? -deltaValue : deltaValue;
              const zoom = cz + finalDelta * zoomSpeed * cz;
              this._setCamera(new Vec(cx + x / zoom - x / cz, cy + y / zoom - y / cz, zoom), {
                immediate: true
              });
              this.maybeTrackPerformance("Zooming");
              this.performance._notifyCameraOperation("zooming");
              this.root.handleEvent(info);
              this.emit("event", info);
              return;
            }
            case "pan": {
              this._setCamera(new Vec(cx + dx * panSpeed / cz, cy + dy * panSpeed / cz, cz), {
                immediate: true
              });
              this.maybeTrackPerformance("Panning");
              this.performance._notifyCameraOperation("panning");
              this.root.handleEvent(info);
              this.emit("event", info);
              return;
            }
          }
        }
        break;
      }
      case "pointer": {
        if (inputs.getIsPinching()) return;
        this.inputs.updateFromEvent(info);
        const { isPen } = info;
        const { isPenMode } = instanceState;
        switch (info.name) {
          case "pointer_down": {
            if (isPenMode && !isPen) return;
            if (!this.inputs.getIsPanning()) {
              this._longPressTimeout = this.timers.setTimeout(() => {
                const vsb = this.getViewportScreenBounds();
                this.dispatch({
                  ...info,
                  // important! non-obvious!! the screenpoint was adjusted using the
                  // viewport bounds, and will be again when this event is handled...
                  // so we need to counter-adjust from the stored value so that the
                  // new value is set correctly.
                  point: this.inputs.getOriginScreenPoint().clone().addXY(vsb.x, vsb.y),
                  name: "long_press"
                });
              }, this.options.longPressDurationMs);
            }
            if (!this._didCaptureSelectionAtPointerDown) {
              this._selectedShapeIdsAtPointerDown = this.getSelectedShapeIds();
              this._didCaptureSelectionAtPointerDown = true;
            }
            if (info.button === LEFT_MOUSE_BUTTON) this.capturedPointerId = info.pointerId;
            inputs.buttons.add(info.button);
            inputs.setIsPointing(true);
            inputs.setIsDragging(false);
            if (!isPenMode && info.isPenDirect) {
              this.updateInstanceState({ isPenMode: true });
              this.interrupt();
            }
            if (info.button === STYLUS_ERASER_BUTTON) {
              this._restoreToolId = this.getCurrentToolId();
              this.complete();
              this.setCurrentTool("eraser");
            } else if (info.button === MIDDLE_MOUSE_BUTTON) {
              if (!this.inputs.getIsPanning()) {
                this._prevCursor = this.getInstanceState().cursor.type;
              }
              this.inputs.setIsPanning(true);
              clearTimeout(this._longPressTimeout);
            } else if (info.button === RIGHT_MOUSE_BUTTON && this.options.rightClickPanning) {
              this.inputs.setIsRightPointing(true);
              clearTimeout(this._longPressTimeout);
              return this;
            }
            if (this.inputs.getIsPanning()) {
              this.stopCameraAnimation();
              this.setCursor({ type: "grabbing", rotation: 0 });
              return this;
            }
            break;
          }
          case "pointer_move": {
            if (!isPen && isPenMode) return;
            const { x: cx, y: cy, z: cz } = unsafe__withoutCapture(() => this.getCamera());
            if (this.inputs.getIsRightPointing() && !this.inputs.getIsPanning()) {
              const currentScreenPoint = this.inputs.getCurrentScreenPoint();
              const originScreenPoint = this.inputs.getOriginScreenPoint();
              if (Vec.Dist2(originScreenPoint, currentScreenPoint) > this.options.dragDistanceSquared) {
                this._prevCursor = this.getInstanceState().cursor.type;
                this.inputs.setIsPanning(true);
                this.setCursor({ type: "grabbing", rotation: 0 });
                this.stopCameraAnimation();
                const offset = Vec.Sub(currentScreenPoint, originScreenPoint);
                this.setCamera(new Vec(cx + offset.x / cz, cy + offset.y / cz, cz), {
                  immediate: true
                });
                this.maybeTrackPerformance("Panning");
              }
              return;
            }
            if (this.inputs.getIsPanning() && this.inputs.getIsPointing()) {
              const currentScreenPoint = this.inputs.getCurrentScreenPoint();
              const previousScreenPoint = this.inputs.getPreviousScreenPoint();
              const offset = Vec.Sub(currentScreenPoint, previousScreenPoint);
              this.setCamera(new Vec(cx + offset.x / cz, cy + offset.y / cz, cz), {
                immediate: true
              });
              this.maybeTrackPerformance("Panning");
              this.performance._notifyCameraOperation("panning");
              return;
            }
            if (inputs.getIsPointing() && !inputs.getIsDragging() && Vec.Dist2(inputs.getOriginPagePoint(), inputs.getCurrentPagePoint()) * this.getZoomLevel() > (instanceState.isCoarsePointer ? this.options.coarseDragDistanceSquared : this.options.dragDistanceSquared) / cz) {
              inputs.setIsDragging(true);
              clearTimeout(this._longPressTimeout);
            }
            break;
          }
          case "pointer_up": {
            inputs.setIsDragging(false);
            inputs.setIsPointing(false);
            clearTimeout(this._longPressTimeout);
            inputs.buttons.delete(info.button);
            if (instanceState.isPenMode && !isPen) return;
            if (this.inputs.getIsRightPointing() && !this.inputs.getIsPanning()) {
              this.inputs.setIsRightPointing(false);
              this._selectedShapeIdsAtPointerDown = [];
              this._didCaptureSelectionAtPointerDown = false;
              break;
            }
            this.inputs.setIsRightPointing(false);
            if (this.capturedPointerId === info.pointerId) {
              this.capturedPointerId = null;
              info.button = 0;
            }
            if (inputs.getIsPanning()) {
              if (!inputs.keys.has("Space")) {
                inputs.setIsPanning(false);
                inputs.setIsSpacebarPanning(false);
              }
              const slideDirection = this.inputs.getPointerVelocity();
              const slideSpeed = Math.min(2, slideDirection.len());
              switch (info.button) {
                case LEFT_MOUSE_BUTTON: {
                  this.setCursor({ type: "grab", rotation: 0 });
                  break;
                }
                case MIDDLE_MOUSE_BUTTON: {
                  if (this.inputs.keys.has("Space")) {
                    this.setCursor({ type: "grab", rotation: 0 });
                  } else {
                    this.setCursor({ type: this._prevCursor, rotation: 0 });
                  }
                  break;
                }
                case RIGHT_MOUSE_BUTTON: {
                  if (this.inputs.keys.has("Space")) {
                    this.setCursor({ type: "grab", rotation: 0 });
                  } else {
                    this.setCursor({ type: this._prevCursor, rotation: 0 });
                  }
                  if (slideSpeed > 0) {
                    this.slideCamera({
                      speed: slideSpeed,
                      direction: { x: slideDirection.x, y: slideDirection.y, z: 0 }
                    });
                  }
                  this._selectedShapeIdsAtPointerDown = [];
                  this._didCaptureSelectionAtPointerDown = false;
                  return this;
                }
              }
              if (slideSpeed > 0) {
                this.slideCamera({
                  speed: slideSpeed,
                  direction: { x: slideDirection.x, y: slideDirection.y, z: 0 }
                });
              }
            } else {
              if (info.button === STYLUS_ERASER_BUTTON) {
                this.complete();
                this.setCurrentTool(this._restoreToolId);
              }
            }
            this._selectedShapeIdsAtPointerDown = [];
            this._didCaptureSelectionAtPointerDown = false;
            break;
          }
        }
        break;
      }
      case "keyboard": {
        if (info.key === "ShiftRight") info.key = "ShiftLeft";
        if (info.key === "AltRight") info.key = "AltLeft";
        if (info.code === "ControlRight") info.code = "ControlLeft";
        if (info.code === "MetaRight") info.code = "MetaLeft";
        switch (info.name) {
          case "key_down": {
            inputs.keys.add(info.code);
            if (this.options.spacebarPanning) {
              if (info.code === "Space" && !info.ctrlKey) {
                if (!this.inputs.getIsPanning()) {
                  this._prevCursor = instanceState.cursor.type;
                }
                this.inputs.setIsPanning(true);
                this.inputs.setIsSpacebarPanning(true);
                clearTimeout(this._longPressTimeout);
                this.setCursor({
                  type: this.inputs.getIsPointing() ? "grabbing" : "grab",
                  rotation: 0
                });
              }
              if (this.inputs.getIsSpacebarPanning()) {
                let offset;
                switch (info.code) {
                  case "ArrowUp": {
                    offset = new Vec(0, -1);
                    break;
                  }
                  case "ArrowRight": {
                    offset = new Vec(1, 0);
                    break;
                  }
                  case "ArrowDown": {
                    offset = new Vec(0, 1);
                    break;
                  }
                  case "ArrowLeft": {
                    offset = new Vec(-1, 0);
                    break;
                  }
                }
                if (offset) {
                  const bounds = this.getViewportPageBounds();
                  const next = bounds.clone().translate(offset.mulV({ x: bounds.w, y: bounds.h }));
                  this._animateToViewport(next, { animation: { duration: 320 } });
                }
              }
            }
            break;
          }
          case "key_up": {
            inputs.keys.delete(info.code);
            if (this.options.spacebarPanning) {
              if (info.code === "Space") {
                if (this.inputs.buttons.has(MIDDLE_MOUSE_BUTTON)) {
                } else {
                  this.inputs.setIsPanning(false);
                  this.inputs.setIsSpacebarPanning(false);
                  this.setCursor({ type: this._prevCursor, rotation: 0 });
                }
              }
            }
            break;
          }
          case "key_repeat": {
            break;
          }
        }
        break;
      }
    }
    if (info.type === "pointer") {
      if (info.button === MIDDLE_MOUSE_BUTTON) {
        info.name = "middle_click";
      } else if (info.button === RIGHT_MOUSE_BUTTON) {
        info.name = "right_click";
      }
      if (info.name === "right_click" && this.getCurrentToolId() !== "select" && this.getStateDescendant("select")) {
        this.setCurrentTool("select");
      }
      const { isPenMode } = this.store.unsafeGetWithoutCapture(TLINSTANCE_ID);
      if (info.isPen === isPenMode) {
        const clickInfo = this._clickManager.handlePointerEvent(info);
        if (info.name !== clickInfo.name) {
          this.root.handleEvent(info);
          this.emit("event", info);
          this.root.handleEvent(clickInfo);
          this.emit("event", clickInfo);
          return;
        }
      }
    }
    this.root.handleEvent(info);
    this.emit("event", info);
    if (info.type === "pointer" && info.name === "pointer_down") {
      this.menus.clearOpenMenus();
    }
    return this;
  }
  /** @internal */
  maybeTrackPerformance(name) {
    if (debugFlags.measurePerformance.get()) {
      if (this.performanceTracker.isStarted()) {
        clearTimeout(this.performanceTrackerTimeout);
      } else {
        this.performanceTracker.start(name);
      }
      this.performanceTrackerTimeout = this.timers.setTimeout(() => {
        this.performanceTracker.stop();
      }, 50);
    }
  }
}
__decorateClass([
  computed
], Editor.prototype, "getIsShapeHiddenCache", 1);
__decorateClass([
  computed
], Editor.prototype, "getIsMounted", 1);
__decorateClass([
  computed
], Editor.prototype, "canUndo", 1);
__decorateClass([
  computed
], Editor.prototype, "canRedo", 1);
__decorateClass([
  computed
], Editor.prototype, "getPath", 1);
__decorateClass([
  computed
], Editor.prototype, "getCurrentTool", 1);
__decorateClass([
  computed
], Editor.prototype, "getCurrentToolId", 1);
__decorateClass([
  computed
], Editor.prototype, "getDocumentSettings", 1);
__decorateClass([
  computed
], Editor.prototype, "getInstanceState", 1);
__decorateClass([
  computed
], Editor.prototype, "getPageStates", 1);
__decorateClass([
  computed
], Editor.prototype, "_getPageStatesQuery", 1);
__decorateClass([
  computed
], Editor.prototype, "getCurrentPageState", 1);
__decorateClass([
  computed
], Editor.prototype, "_getCurrentPageStateId", 1);
__decorateClass([
  computed
], Editor.prototype, "getSelectedShapeIds", 1);
__decorateClass([
  computed
], Editor.prototype, "getSelectedShapes", 1);
__decorateClass([
  computed
], Editor.prototype, "getCurrentPageShapesInReadingOrder", 1);
__decorateClass([
  computed
], Editor.prototype, "getOnlySelectedShapeId", 1);
__decorateClass([
  computed
], Editor.prototype, "getOnlySelectedShape", 1);
__decorateClass([
  computed
], Editor.prototype, "getSelectionPageBounds", 1);
__decorateClass([
  computed
], Editor.prototype, "getSelectionRotation", 1);
__decorateClass([
  computed
], Editor.prototype, "getSelectionRotatedPageBounds", 1);
__decorateClass([
  computed
], Editor.prototype, "getSelectionRotatedScreenBounds", 1);
__decorateClass([
  computed
], Editor.prototype, "getFocusedGroupId", 1);
__decorateClass([
  computed
], Editor.prototype, "getFocusedGroup", 1);
__decorateClass([
  computed
], Editor.prototype, "getEditingShapeId", 1);
__decorateClass([
  computed
], Editor.prototype, "getEditingShape", 1);
__decorateClass([
  computed
], Editor.prototype, "getRichTextEditor", 1);
__decorateClass([
  computed
], Editor.prototype, "getHoveredShapeId", 1);
__decorateClass([
  computed
], Editor.prototype, "getHoveredShape", 1);
__decorateClass([
  computed
], Editor.prototype, "getHintingShapeIds", 1);
__decorateClass([
  computed
], Editor.prototype, "getHintingShape", 1);
__decorateClass([
  computed
], Editor.prototype, "getErasingShapeIds", 1);
__decorateClass([
  computed
], Editor.prototype, "getErasingShapes", 1);
__decorateClass([
  computed
], Editor.prototype, "_unsafe_getCameraId", 1);
__decorateClass([
  computed
], Editor.prototype, "getCamera", 1);
__decorateClass([
  computed
], Editor.prototype, "getViewportPageBoundsForFollowing", 1);
__decorateClass([
  computed
], Editor.prototype, "getCameraForFollowing", 1);
__decorateClass([
  computed
], Editor.prototype, "getZoomLevel", 1);
__decorateClass([
  computed
], Editor.prototype, "getResizeScaleFactor", 1);
__decorateClass([
  computed
], Editor.prototype, "getDebouncedZoomLevel", 1);
__decorateClass([
  computed
], Editor.prototype, "_getAboveDebouncedZoomThreshold", 1);
__decorateClass([
  computed
], Editor.prototype, "getEfficientZoomLevel", 1);
__decorateClass([
  computed
], Editor.prototype, "getViewportScreenBounds", 1);
__decorateClass([
  computed
], Editor.prototype, "getViewportScreenCenter", 1);
__decorateClass([
  computed
], Editor.prototype, "getViewportPageBounds", 1);
__decorateClass([
  computed
], Editor.prototype, "getRenderingShapes", 1);
__decorateClass([
  computed
], Editor.prototype, "_getAllPagesQuery", 1);
__decorateClass([
  computed
], Editor.prototype, "getPages", 1);
__decorateClass([
  computed
], Editor.prototype, "getCurrentPageId", 1);
__decorateClass([
  computed
], Editor.prototype, "getCurrentPageShapeIdsSorted", 1);
__decorateClass([
  computed
], Editor.prototype, "_getAllAssetsQuery", 1);
__decorateClass([
  computed
], Editor.prototype, "_getShapeHandlesCache", 1);
__decorateClass([
  computed
], Editor.prototype, "_getShapePageTransformCache", 1);
__decorateClass([
  computed
], Editor.prototype, "_getShapePageBoundsCache", 1);
__decorateClass([
  computed
], Editor.prototype, "_getShapeClipPathCache", 1);
__decorateClass([
  computed
], Editor.prototype, "_getShapeMaskCache", 1);
__decorateClass([
  computed
], Editor.prototype, "_getShapeMaskedPageBoundsCache", 1);
__decorateClass([
  computed
], Editor.prototype, "getNotVisibleShapes", 1);
__decorateClass([
  computed
], Editor.prototype, "getCulledShapes", 1);
__decorateClass([
  computed
], Editor.prototype, "getCurrentPageBounds", 1);
__decorateClass([
  computed
], Editor.prototype, "getHitTestMargin", 1);
__decorateClass([
  computed
], Editor.prototype, "getCurrentPageShapes", 1);
__decorateClass([
  computed
], Editor.prototype, "getCurrentPageShapesSorted", 1);
__decorateClass([
  computed
], Editor.prototype, "getCurrentPageRenderingShapesSorted", 1);
__decorateClass([
  computed
], Editor.prototype, "_getBindingsIndexCache", 1);
__decorateClass([
  computed
], Editor.prototype, "_getSelectionSharedStyles", 1);
__decorateClass([
  computed({ isEqual: (a, b) => a.equals(b) })
], Editor.prototype, "getSharedStyles", 1);
__decorateClass([
  computed
], Editor.prototype, "getSharedOpacity", 1);
__decorateClass([
  computed
], Editor.prototype, "getIsFocused", 1);
__decorateClass([
  computed
], Editor.prototype, "getIsReadonly", 1);
__decorateClass([
  bind
], Editor.prototype, "_setShiftKeyTimeout", 1);
__decorateClass([
  bind
], Editor.prototype, "_setAltKeyTimeout", 1);
__decorateClass([
  bind
], Editor.prototype, "_setCtrlKeyTimeout", 1);
__decorateClass([
  bind
], Editor.prototype, "_setMetaKeyTimeout", 1);
function alertMaxShapes(editor, pageId = editor.getCurrentPageId()) {
  const name = editor.getPage(pageId).name;
  editor.emit("max-shapes", { name, pageId, count: editor.options.maxShapesPerPage });
}
function applyPartialToRecordWithProps(prev, partial) {
  if (!partial) return prev;
  let next = null;
  const entries = Object.entries(partial);
  for (let i = 0, n = entries.length; i < n; i++) {
    const [k, v] = entries[i];
    if (v === void 0) continue;
    if (k === "id" || k === "type" || k === "typeName") continue;
    if (v === prev[k]) continue;
    if (!next) next = { ...prev };
    if (k === "props" || k === "meta") {
      next[k] = { ...prev[k] };
      for (const [nextKey, nextValue] of Object.entries(v)) {
        ;
        next[k][nextKey] = nextValue;
      }
      continue;
    }
    ;
    next[k] = v;
  }
  if (!next) return prev;
  return next;
}
function pushShapeWithDescendants(editor, id, result) {
  const shape = editor.getShape(id);
  if (!shape) return;
  result.push(shape);
  const childIds = editor.getSortedChildIdsForParent(id);
  for (let i = 0, n = childIds.length; i < n; i++) {
    pushShapeWithDescendants(editor, childIds[i], result);
  }
}
function withIsolatedShapes(editor, shapeIds, callback) {
  let result;
  editor.run(
    () => {
      const changes = editor.store.extractingChanges(() => {
        const bindingsWithBoth = /* @__PURE__ */ new Set();
        const bindingsToRemove = /* @__PURE__ */ new Set();
        for (const shapeId of shapeIds) {
          const shape = editor.getShape(shapeId);
          if (!shape) continue;
          for (const binding of editor.getBindingsInvolvingShape(shapeId)) {
            const hasFrom = shapeIds.has(binding.fromId);
            const hasTo = shapeIds.has(binding.toId);
            if (hasFrom && hasTo) {
              bindingsWithBoth.add(binding.id);
              continue;
            }
            if (!hasFrom || !hasTo) {
              bindingsToRemove.add(binding.id);
            }
          }
        }
        editor.deleteBindings([...bindingsToRemove], { isolateShapes: true });
        try {
          result = Result.ok(callback(bindingsWithBoth));
        } catch (error) {
          result = Result.err(error);
        }
      });
      editor.store.applyDiff(reverseRecordsDiff(changes), { runCallbacks: false });
    },
    { history: "ignore" }
  );
  if (result.ok) {
    return result.value;
  } else {
    throw result.error;
  }
}
function getCameraFitXFitY(editor, cameraOptions) {
  if (!cameraOptions.constraints) throw Error("Should have constraints here");
  const {
    padding: { x: px, y: py }
  } = cameraOptions.constraints;
  const vsb = editor.getViewportScreenBounds();
  const bounds = Box.From(cameraOptions.constraints.bounds);
  const zx = (vsb.w - px * 2) / bounds.w;
  const zy = (vsb.h - py * 2) / bounds.h;
  return { zx, zy };
}
export {
  Editor
};
//# sourceMappingURL=Editor.mjs.map
